import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as XLSX from "xlsx-js-style";
import { supabase } from "./supabase";
import Pagamenti from "./components/Pagamenti";
import Stipendi from "./components/Stipendi";
import { mc, cellInfo, oreFromStato, getAllievoCorsi, effectiveRetta } from "./utils/helpers";
import DatabaseAllievi from "./components/DatabaseAllievi";
import DatabaseCorsi from "./components/DatabaseCorsi";
import Calendario from "./components/Calendario";
import Solfeggio from "./components/Solfeggio";
import Segreteria from "./components/Segreteria";
import SalaProve from "./components/SalaProve";

// ── PALETTE ───────────────────────────────────────────────────────────────────
const P = {
  bg:"#080B14", card:"#0D1020", border:"#1D2238",
  gold:"#E9622A", goldGlow:"rgba(233,98,42,0.12)", goldBorder:"rgba(233,98,42,0.30)",
  text:"#EBE8F5", muted:"#8A8EAE", secondary:"#B4B7D0",
  green:"#5BB87A", yellow:"#E9622A", red:"#C46B6B", blue:"#5A82B8", purple:"#a78bfa",
  sidebar:"#060810", card2:"#111525", card3:"#161B2E",
  borderSubtle:"#141829", borderStrong:"#2A3055"
};

const FONT = ``;

// ── DATI MAESTRI ──────────────────────────────────────────────────────────────
const MC_INIT = {
  "ANTONELLO":"#E9622A","ELVIRA":"#60a5fa","ROSARIO":"#a78bfa",
  "ALESSIO":"#34d399","ANGELO B.":"#f87171","ALESSANDRO":"#fb923c",
  "DONATO":"#e879f9","M.TERESA":"#f472b6","PASQUALE":"#94a3b8",
  "ANGELO D.":"#22d3ee","ELLIS":"#facc15","FRANCESCO":"#86efac"
};

const PERC_MAESTRO = 0.60;

// ── DATI ALLIEVI ──────────────────────────────────────────────────────────────
const ALLIEVI_INIT = [];

const PAGAMENTI_INIT = [];

const PRESENZE_INIT = {};

const SOLFEGGIO = [];

const SALA_INIT = [];

const GDS = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
const PRIMO_MARZO = 0; // domenica — usato nei calendari fissi (Nicol, MDI)
const MESI_IDX = {gennaio:0,febbraio:1,marzo:2,aprile:3,maggio:4,giugno:5,luglio:6,agosto:7,settembre:8,ottobre:9,novembre:10,dicembre:11};
const NOMI_MESI_IT = ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"];
// Stati presenza nell'ordine del menù (stessi codici di cellInfo).
// "2r" (Recupero 1,5h) non si sceglie più, ma quelli già segnati si vedono e contano ancora.
const STATI_PRESENZA = [
  {v:"45p", desc:"Presente 45 min"}, {v:"1p", desc:"Presente 1h"},  {v:"2p", desc:"Presente 1,5h"},
  {v:"3p",  desc:"Presente 2h"},     {v:"1r", desc:"Recupero 1h"},  {v:"3r", desc:"Recupero 2h"},
  {v:"a",   desc:"Assente"},         {v:"v",  desc:"Prova"},        {v:"m",  desc:"Maestro assente"},
];
// Mese corrente reale — formato "luglio-2026". Nessun concetto di anno scolastico.
const getMeseCorrente = () => {
  const n = new Date();
  return NOMI_MESI_IT[n.getMonth()] + '-' + n.getFullYear();
};
// Alias storico: alcuni punti usano ancora questo nome. Identico a getMeseCorrente.
const getMeseCorrenteReale = getMeseCorrente;
// Chiave numerica ordinabile per un mese "luglio-2026" → 202606 (anno*12 + indiceMese)
const meseKeyNum = (meseKey) => {
  const [nome, anno] = meseKey.split('-');
  return parseInt(anno, 10) * 12 + NOMI_MESI_IT.indexOf(nome);
};
// Mese "settembre-2025" a partire da una data ISO "2025-09-22"
const meseDaISO = (iso) => {
  const d = new Date(iso);
  return NOMI_MESI_IT[d.getMonth()] + '-' + d.getFullYear();
};
// Un allievo con uno storico non viene mai cancellato: si archivia, e l'archiviazione è un periodo
// di pausa. Solo un archiviato senza storico (es. inserito per errore) si può eliminare davvero. a.pause = [{dal:'2026-09-22', al:'2026-10-05'|null}] — al===null significa
// pausa ancora aperta, cioè allievo attualmente archiviato.
// Fuori dalle pause l'allievo conta normalmente, quindi i mesi passati non cambiano mai.
const isArchiviato = (a) => (a.pause || []).some(p => !p.al);
// Data di inizio della pausa aperta (per mostrarla in elenco)
const dataArchiviazione = (a) => ((a.pause || []).find(p => !p.al) || {}).dal || null;
const attivoNelMese = (a, mese) => {
  const k = meseKeyNum(mese);
  return !(a.pause || []).some(p => {
    if (k < meseKeyNum(meseDaISO(p.dal))) return false;      // prima della pausa: attivo
    return !p.al || k < meseKeyNum(meseDaISO(p.al));          // in pausa fino al mese di rientro
  });
};
// Naviga un mese di ±delta senza limiti (timeline infinita) — es. meseAdd("giugno-2026", 1) → "luglio-2026"
const meseAdd = (meseKey, delta) => {
  const [nome, anno] = meseKey.split('-');
  const d = new Date(parseInt(anno, 10), NOMI_MESI_IT.indexOf(nome) + delta, 1);
  return NOMI_MESI_IT[d.getMonth()] + '-' + d.getFullYear();
};
// Timeline dinamica dei mesi: dal più vecchio mese con dati fino al mese corrente
// (o al più recente con dati, se futuro). Contigua, ordinata cronologicamente.
// mesiConDati = array di chiavi "mese-anno" che hanno dati registrati.
const buildTimeline = (mesiConDati) => {
  const oggi = getMeseCorrente();
  const chiavi = [...mesiConDati, oggi].filter(Boolean);
  const nums = chiavi.map(meseKeyNum);
  const min = Math.min(...nums), max = Math.max(...nums);
  const out = [];
  // ricostruisce da min a max, un mese per iterazione
  for (let k = min; k <= max; k++) {
    const anno = Math.floor(k / 12);
    const mese = NOMI_MESI_IT[k % 12];
    out.push(`${mese}-${anno}`);
  }
  return out;
};
const mesiInfo = (meseKey) => {
  const [nome, anno] = meseKey.split('-');
  const m = MESI_IDX[nome]; const y = parseInt(anno);
  return {
    primo: new Date(y, m, 1).getDay(),
    days:  new Date(y, m+1, 0).getDate(),
    label: nome.charAt(0).toUpperCase() + nome.slice(1) + ' ' + anno,
  };
};

// ── TITOLARE ──────────────────────────────────────────────────────────────────
// Il compenso del titolare è un'ENTRATA, non un'uscita. Cambia qui per aggiornarlo.
const NOME_TITOLARE = 'DONATO CIPRIANO';
const isTitolare = (nome) => {
  const n = (nome || '').trim().toUpperCase();
  const t = NOME_TITOLARE.toUpperCase();
  return n === t || n.startsWith(t + ' ') || t.startsWith(n + ' ');
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
// mc, cellInfo → src/utils/helpers.js
const lvlColor = l => l==="Propedeutico"?P.gold:l==="Fondamentale"?P.blue:P.purple;


const InfoTooltip = ({ lines, setTooltip }) => {
  const ref = useRef(null);
  const closeTimer = useRef(null);

  const open = (e) => {
    e.stopPropagation();
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const rect = ref.current.getBoundingClientRect();
    const estH = lines.length * 24 + 32;
    let x = rect.right + 8;
    let y = rect.top;
    if (x + 260 > window.innerWidth)  x = rect.left - 268;
    if (y + estH > window.innerHeight) y = window.innerHeight - estH - 8;
    setTooltip({ x, y, lines, onMouseEnter: cancelClose, onMouseLeave: scheduleClose });
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setTooltip(null), 150);
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  return (
    <span
      ref={ref}
      data-info-tip="1"
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
      onTouchStart={(e) => { e.preventDefault(); open(e); }}
      style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        padding:10, margin:"-10px -10px -10px -5px",
        cursor:"pointer", flexShrink:0, userSelect:"none",
        verticalAlign:"middle",
      }}
    >
      <span style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        width:16, height:16, borderRadius:"50%",
        background:"rgba(71,75,104,.25)", color:"#B4B7D0",
        fontSize:11, fontWeight:700,
        fontFamily:"'IBM Plex Mono',monospace", lineHeight:1,
        pointerEvents:"none",
      }}>ℹ</span>
    </span>
  );
};

const Badge = ({label,color}) => (
  <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,padding:"3px 9px",borderRadius:99,fontWeight:400,letterSpacing:0.3,
    color,background:`${color}15`,border:`1px solid ${color}30`}}>{label}</span>
);

const Avatar = ({nome,color,size=30}) => (
  <div style={{width:size,height:size,borderRadius:"50%",background:`${color}20`,
    border:`1px solid ${color}44`,display:"flex",alignItems:"center",
    justifyContent:"center",fontSize:size*0.4,color,fontWeight:700,flexShrink:0}}>
    {nome.charAt(0)}
  </div>
);


// Checkbox riutilizzabile - definito FUORI da App per evitare re-render
const Chk = ({checked, onChange, color, disabled}) => (
  <div onClick={e=>{e.stopPropagation();if(!disabled)onChange();}} style={{
    width:24, height:24, borderRadius:5,
    border:`2px solid ${disabled ? "#2a2e45" : checked ? color : "#3a3f58"}`,
    background: disabled ? "#0d1020" : checked ? `${color}25` : "#0d1020",
    cursor: disabled ? "not-allowed" : "pointer",
    display:"flex", alignItems:"center",
    justifyContent:"center", flexShrink:0, transition:"all .15s",
    opacity: disabled ? 0.35 : 1,
    userSelect:"none", WebkitUserSelect:"none"
  }}>
    {checked && !disabled && <span style={{color, fontSize:16, fontWeight:800, lineHeight:1, marginTop:-1}}>✓</span>}
  </div>
);


// Restituisce i corsi di un allievo — se non ha corsi espliciti, usa il campo maestro/retta legacy
// getAllievoCorsi, effectiveRetta → src/utils/helpers.js

// Campo testo editabile inline con stile custom (per nome, email, ecc.)
const EditableField = ({ value, onChange, color, placeholder="", inputStyle={} }) => {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  return (
    <input
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onChange(local); }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      placeholder={placeholder}
      style={{
        background:"transparent", border:"1px solid transparent",
        borderRadius:6, padding:"2px 6px", color:P.text,
        fontFamily:"'Syne',sans-serif", transition:"border-color .15s",
        outline:"none", ...inputStyle
      }}
      onFocus={e=>e.target.style.borderColor=`${color}66`}
      onBlurCapture={e=>e.target.style.borderColor="transparent"}
    />
  );
};

const NoteInput = ({ value, onChange, color }) => {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  return (
    <input
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onChange(local); }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      placeholder="Aggiungi nota..."
      style={{
        width:"100%", background:"transparent",
        border:"1px solid transparent", borderRadius:6,
        padding:"6px 8px", color:P.text, fontSize:14,
        fontFamily:"'Syne',sans-serif", transition:"border-color .15s"
      }}
      onFocus={e=>e.target.style.borderColor=`${color}66`}
      onBlurCapture={e=>e.target.style.borderColor="transparent"}
    />
  );
};

// ── NICOL SEGRETARIA DATA ──────────────────────────────────────────────────────
const NICOL_TARIFFA = 12.50; // €/ora

// nicolMesi: { "settembre-2025": { pagato: false }, ... }
const NICOL_MESI = {};

// nicolPresenze: { "settembre-2025": { 1: true, 3: true, ... }, ... }
const NICOL_PRESENZE_MARZO = {};


// ── MUSICA D'INSIEME DATA ─────────────────────────────────────────────────────
const MDI_ALLIEVI = [];
const MDI_PRESENZE_INIT = {};




// ── CALENDARIO DATA ───────────────────────────────────────────────────────────
const DAYS_IT_CAL = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
const MONTHS_IT_CAL = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

// Lezioni ricorrenti (dow: 0=Dom,1=Lun,...,6=Sab)
const CAL_RECURRING_INIT = [];

// ── LOGIN SCREEN ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) setError('Credenziali non valide');
    setLoading(false);
  };

  return (
    <div style={{ minHeight:'100vh', background:P.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:P.card, border:`1px solid ${P.border}`, borderRadius:20,
        padding:'40px 36px', width:380, maxWidth:'94vw', boxShadow:'0 24px 64px rgba(0,0,0,.55)' }}>
        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, letterSpacing:3,
          color:P.muted, marginBottom:6, textTransform:'uppercase' }}>Gestionale</div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:26, fontWeight:800,
          letterSpacing:-0.5, marginBottom:28, lineHeight:1.15 }}>
          <span style={{ color:'#fff' }}>Nuove</span><br/><span style={{ color:P.gold }}>Armonie</span>
        </div>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, color:P.muted, display:'block', marginBottom:4,
              letterSpacing:1, textTransform:'uppercase' }}>Email</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleLogin(e)}
              autoComplete="email" required
              style={{ width:'100%', background:P.bg, border:`1px solid ${P.border}`,
                borderRadius:8, padding:'10px 12px', color:P.text, fontSize:16,
                fontFamily:"'Figtree',sans-serif", outline:'none', boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:22 }}>
            <label style={{ fontSize:12, color:P.muted, display:'block', marginBottom:4,
              letterSpacing:1, textTransform:'uppercase' }}>Password</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleLogin(e)}
              autoComplete="current-password" required
              style={{ width:'100%', background:P.bg, border:`1px solid ${P.border}`,
                borderRadius:8, padding:'10px 12px', color:P.text, fontSize:16,
                fontFamily:"'Figtree',sans-serif", outline:'none', boxSizing:'border-box' }}/>
          </div>
          {error && <div style={{ color:P.red, fontSize:14, marginBottom:14 }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ width:'100%', padding:'12px', background:P.gold, border:'none',
              borderRadius:8, color:'#fff', fontWeight:700, fontSize:16,
              cursor:loading?'default':'pointer', fontFamily:"'Figtree',sans-serif",
              opacity:loading?0.7:1 }}>
            {loading ? 'Accesso...' : 'Accedi'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Wrapper stabile fuori da App: evita unmount/remount di Presenze ad ogni re-render di App
const PresenzeHost = ({ render }) => render();

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState("dashboard");
  const [tooltip, setTooltip] = useState(null);
  const InfoTip = ({ lines }) => <InfoTooltip lines={lines} setTooltip={setTooltip} />;
  const [corsiOpen, setCorsiOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const [authUser, setAuthUser]   = useState(null);  // Supabase auth user
  const [userRole, setUserRole]   = useState(null);  // 'admin' | 'segreteria' | 'maestro'
  const [userMaestro, setUserMaestro] = useState(null); // nome maestro collegato
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const close = (e) => {
      if (e.target.closest && e.target.closest('[data-info-tip]')) return;
      setTooltip(null);
    };
    document.addEventListener('touchstart', close);
    return () => document.removeEventListener('touchstart', close);
  }, []);

  useEffect(() => {
    // Stesso utente → stesso oggetto: il rinnovo automatico dell'accesso (circa ogni ora)
    // non deve rileggere il ruolo, altrimenti segreteria e maestri tornavano al Calendario
    const aggiornaUtente = (user) => setAuthUser(prev => (prev && user && prev.id === user.id) ? prev : (user ?? null));
    supabase.auth.getSession().then(({ data: { session } }) => {
      aggiornaUtente(session?.user);
      if (!session?.user) setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      aggiornaUtente(session?.user);
      if (!session?.user) { setUserRole(null); setUserMaestro(null); setAuthLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUser) return;
    supabase.from('utenti').select('email, ruolo, maestro_nome, nome').eq('email', authUser.email).maybeSingle()
      .then(async ({ data, error }) => {
        if (error) console.warn('utenti lookup error:', error.message);
        const ruolo = data?.ruolo;
        // Maestro archiviato: il DB gli nega già tutto (private.is_staff), qui glielo si dice.
        // Se la funzione non c'è ancora (SQL non lanciato) si va avanti come prima.
        if (ruolo === 'maestro') {
          const { data: attivo, error: errAccesso } = await supabase.rpc('accesso_attivo');
          if (!errAccesso && attivo === false) { setUserRole('sospeso'); setAuthLoading(false); return; }
        }
        if (ruolo && ['admin','segreteria','maestro'].includes(ruolo)) {
          setUserRole(ruolo);
          setUserMaestro(data?.maestro_nome || data?.nome || null);
          // Imposta il tab iniziale corretto per il ruolo
          if (ruolo === 'segreteria') setTab('calendario');
          if (ruolo === 'maestro') { setTab('calendario'); setCalView('day'); }
        } else {
          setUserRole('noaccess');
        }
        setAuthLoading(false);
      });
  }, [authUser]);

  const handleLogout = async () => { await supabase.auth.signOut(); };

  // ── NUOVA VERSIONE ─────────────────────────────────────────────────────────
  // Il PC della segreteria resta aperto per giorni: ogni 10 minuti e quando si torna
  // sulla finestra si guarda se il file main.*.js pubblicato è cambiato.
  const [nuovaVersione, setNuovaVersione] = useState(false);
  useEffect(() => {
    const attuale = document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src');
    if (!attuale) return; // in sviluppo il file non ha l'hash
    const controlla = async () => {
      try {
        const html = await (await fetch('/index.html', { cache: 'no-store' })).text();
        const pubblicato = html.match(/\/static\/js\/main\.[^"]+\.js/)?.[0];
        if (pubblicato && pubblicato !== attuale) setNuovaVersione(true);
      } catch {}
    };
    const alRitorno = () => { if (document.visibilityState === 'visible') controlla(); };
    const t = setInterval(controlla, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', alRitorno);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', alRitorno); };
  }, []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [dbReady, setDbReady] = useState(false);
  const [maestriColors, setMaestriColors] = useState(MC_INIT);
  // Lista completa, archiviati inclusi. Le viste usano `allievi` (derivato sotto).
  const [allieviAll, setAllievi] = useState(ALLIEVI_INIT);
  const [presenze, setPresenze] = useState(PRESENZE_INIT);
  const [pagamenti, setPagamenti] = useState(PAGAMENTI_INIT);
  const [stipendiPagati, setStipendi] = useState({});
  const [sala, setSala] = useState(SALA_INIT);
  const [search, setSearch] = useState("");
  const [filtroM, setFiltroM] = useState("TUTTI");
  const [filtroMP, setFiltroMP] = useState("TUTTI");
  const [presAllievo, setPresAllievo] = useState(null);
  const [presAllievoFrom, setPresAllievoFrom] = useState("presenze");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [notif, setNotif] = useState(null);
  const [nextId, setNextId] = useState(100);
  const [confirmDelete, setConfirmDelete] = useState(null); // allievo da eliminare
  const [deletedAllievo, setDeletedAllievo] = useState(null); // per undo
  const [nicolMesi, setNicolMesi] = useState(NICOL_MESI);
  const [nicolPresenze, setNicolPresenze] = useState(NICOL_PRESENZE_MARZO);
  const [solfeggioMesi, setSolfeggioMesi] = useState([]);
  const [solfeggioLezioni, setSolfeggioLezioni] = useState({}); // { "YYYY-MM-DD": { ore: 2.5 } }
  const [solfeggioPresenze, setSolfeggioPresenze] = useState({}); // { "YYYY-MM-DD": { allId: "P"|"A" } }
  const [calRecurring, setCalRecurring] = useState(CAL_RECURRING_INIT);
  const [calSingle, setCalSingle] = useState([]);
  const [calDate, setCalDate] = useState(new Date());
  const [calView, setCalView] = useState('day');
  const [calFiltroMaestro, setCalFiltroMaestro] = useState(null); // null = tutti
  const [calHidden, setCalHidden] = useState(false); // nascondi tutti gli eventi
  const [mdiPresenze, setMdiPresenze] = useState(MDI_PRESENZE_INIT);
  const [mdiAllievi, setMdiAllievi] = useState(()=>MDI_ALLIEVI.map((n,i)=>({id:i+1,nome:n})));
  const [nextMdiId, setNextMdiId] = useState(MDI_ALLIEVI.length+1);
  const [movimentiCassa, setMovimentiCassa] = useState([]);
  const [maestriDB, setMaestriDB] = useState([]);
  const [meseAllievi, setMeseAllievi] = useState(getMeseCorrenteReale);
  const [segreteriaNome, setSegreteriaNome] = useState('Nicol Napodano');

  // Allievi attivi in un mese specifico — usato dallo Storico, che calcola mese per mese.
  const allieviDelMese = useCallback(
    (mese) => allieviAll.filter(a => attivoNelMese(a, mese)),
    [allieviAll]
  );
  // Allievi del mese attualmente visualizzato: è questa la lista che alimenta
  // Dashboard, Presenze, Segreteria, Stipendi e MDI.
  const allievi = useMemo(() => allieviDelMese(meseAllievi), [allieviDelMese, meseAllievi]);
  const archiviati = useMemo(() => allieviAll.filter(isArchiviato), [allieviAll]);

  // ── CORSI — persistiti su Supabase ────────────────────────────────────────────
  const [corsi, setCorsi] = useState([]);

  // Converte una riga Supabase nel formato stato JS
  const dbCorsoToState = (d) => ({
    id:              d.id,
    nome:            d.nome,
    tipo:            d.tipo || 'singolo',
    maestro:         d.maestro || '',
    quotaOraria:     Number(d.quota_oraria)     || 0,
    durataLezioneOre:Number(d.durata_lezione_ore)|| 1,
    lezioniMensili:  d.lezioni_mensili           || 2,
    maestri:         d.maestri                  || [],
    label:           d.label                    || '',
  });

  // Callback per DatabaseCorsi: salva su Supabase + aggiorna stato
  const onSaveCorso = async (payload, editTargetId) => {
    const row = {
      nome:              payload.nome,
      tipo:              payload.tipo,
      maestro:           payload.maestro || null,
      quota_oraria:      payload.quotaOraria      || 0,
      durata_lezione_ore:payload.durataLezioneOre || 1,
      lezioni_mensili:   payload.lezioniMensili   || 2,
      maestri:           payload.maestri          || [],
      label:             payload.label            || '',
    };
    if (editTargetId) {
      const { data } = await supabase.from('corsi').update(row).eq('id', editTargetId).select().single();
      if (data) setCorsi(prev => prev.map(c => c.id === editTargetId ? dbCorsoToState(data) : c));
    } else {
      const { data } = await supabase.from('corsi').insert(row).select().single();
      if (data) setCorsi(prev => [...prev, dbCorsoToState(data)]);
    }
  };

  const onDeleteCorso = async (id) => {
    await supabase.from('corsi').delete().eq('id', id);
    setCorsi(prev => prev.filter(c => c.id !== id));
  };

  // ── PAGAMENTI CORSI MULTI (MDI e simili) — persistiti su Supabase rette_mensili ─
  // pagamentiMDI: { [mese]: { [allievoId]: true } }
  // oreMultiMese: { [mese]: { [allievoId]: n } }
  const [pagamentiMDI, setPagamentiMDI] = useState({});
  const [oreMultiMese, setOreMultiMese] = useState({});

  const getOreMDI = (allievoId, mese) => (oreMultiMese[mese] || {})[allievoId] || 0;
  const getMDIPagato = (allievoId, mese) => !!(pagamentiMDI[mese] || {})[allievoId];

  // Costo corso multi: ore scelte manualmente × Σ(quotaOraria maestri)
  // specPrezzoCustom è per le lezioni private, NON si applica al MDI
  const getCostoMusicaInsieme = (a, mese) => {
    const corso = getCorsoAllievo(a);
    if (!corso || corso.tipo !== 'multi') return 0;
    if (a.specNoRetta) return 0;
    const ore = getOreMDI(a.id, mese);
    const prezzoOra = (corso.maestri || []).reduce((s, m) => s + (m.quotaOraria || 0), 0);
    return ore * prezzoOra;
  };

  const setOreMDI = async (allievoId, mese, ore) => {
    setOreMultiMese(prev => ({
      ...prev,
      [mese]: { ...(prev[mese] || {}), [allievoId]: ore }
    }));
    if (dbReady) {
      const curMdi   = !!(pagamentiMDI[mese]?.[allievoId]);
      const curRetta = !!(pagamentiMensili[mese]?.[allievoId]?.retta);
      const curIscr  = !!(pagamentiMensili[mese]?.[allievoId]?.iscr);
      await supabase.from('rette_mensili').upsert(
        { allievo_id: allievoId, mese, retta_pagata: curRetta, iscr_pagata: curIscr, mdi_pagato: curMdi, mdi_ore: ore },
        { onConflict: 'allievo_id,mese' }
      );
    }
  };

  const toggleMDI = async (allievoId, mese) => {
    const newVal = !getMDIPagato(allievoId, mese);
    setPagamentiMDI(prev => ({
      ...prev,
      [mese]: { ...(prev[mese] || {}), [allievoId]: newVal }
    }));
    if (dbReady) {
      const curOre   = getOreMDI(allievoId, mese);
      const curRetta = !!(pagamentiMensili[mese]?.[allievoId]?.retta);
      const curIscr  = !!(pagamentiMensili[mese]?.[allievoId]?.iscr);
      await supabase.from('rette_mensili').upsert(
        { allievo_id: allievoId, mese, retta_pagata: curRetta, iscr_pagata: curIscr, mdi_pagato: newVal, mdi_ore: curOre },
        { onConflict: 'allievo_id,mese' }
      );
    }
    // Cassa
    const a = allieviAll.find(x => x.id === allievoId);
    if (!a) return;
    const importo = getCostoMusicaInsieme(a, mese);
    await _cassaToggle(newVal, importo, `MDI ${a.nome}`, mese);
  };

  // Totale incassato da corsi multi nel mese (per la dashboard)
  const totMDIIncassato = (mese) =>
    allievi.reduce((s, a) => {
      if (!getMDIPagato(a.id, mese)) return s;
      return s + getCostoMusicaInsieme(a, mese);
    }, 0);


  const [pagamentiMensili, setPagamentiMensili] = useState({});

  // Timeline dinamica dei mesi (sostituisce l'anno scolastico fisso):
  // dal primo mese con dati registrati fino al mese corrente. Contigua e infinita.
  const MESI_TIMELINE = useMemo(() => {
    const mesiConDati = [
      ...Object.keys(pagamentiMensili || {}),
      ...(movimentiCassa || [])
        .map(m => m.mese_competenza)
        .filter(Boolean),
    ];
    return buildTimeline(mesiConDati);
  }, [pagamentiMensili, movimentiCassa]);

  // Draft importo/sconto: valore in editing nell'input (App-level, persiste ai cambi tab)
  const [draftImporto, setDraftImporto] = useState({}); // { "id-mese": string }
  const [draftSconto, setDraftSconto] = useState({});   // { "id-mese": string }
  const [preMarkSnapshot, setPreMarkSnapshot] = useState(null); // { mese, ids: Set } — snapshot pre-bulk

  // ── UNDO (Ctrl+Z) ─────────────────────────────────────────────────────────────
  // Ogni azione annullabile mette in coda come tornare indietro (il valore di prima)
  // e l'annullamento lo riscrive anche nel DB. Prima si rimetteva tutto lo stato
  // dell'app solo sullo schermo: al ricaricamento la modifica "annullata" ricompariva.
  const [history, setHistory] = useState([]);
  const saveHistory = (voce) => setHistory(h => [...h.slice(-29), voce]);
  const undoRef = useRef(null);

  // Ctrl+Z / Cmd+Z (non mentre si scrive in un campo: lì vale l'annulla del testo)
  useEffect(()=>{
    const handler = (e) => {
      if((e.ctrlKey||e.metaKey) && e.key.toLowerCase()==="z" && !e.shiftKey) {
        if (e.target.closest && e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
        e.preventDefault(); undoRef.current?.();
      }
    };
    window.addEventListener("keydown", handler);
    return ()=>window.removeEventListener("keydown",handler);
  },[]);


  // ── SUPABASE SYNC ──────────────────────────────────────────────────────────
  // Carica dati dal database solo dopo il login, con un ruolo valido:
  // senza sessione le policy del DB non restituiscono nulla.
  useEffect(() => {
    if (['admin','segreteria','maestro'].includes(userRole)) loadFromDB(userRole);
  }, [userRole]);

  // Ogni lettura deve riuscire: se una fallisce ci si ferma (vedi catch di loadFromDB)
  const leggi = async (query) => {
    const { data, error } = await query;
    if (error) throw error;
    return data;
  };

  const caricaCalendario = async () => {
    const calDB = await leggi(supabase.from('calendario').select('*'));
    if (calDB) {
      const recurring = calDB.filter(e => e.recur === 'weekly' || e.recur === 'biweekly').map(e => ({
        id: e.id, title: e.title, maestro: e.maestro,
        startH: e.start_h, startM: e.start_m, endH: e.end_h, endM: e.end_m,
        dow: e.dow, note: e.note || '', recur: e.recur,
        startDate: e.start_date || null, endDate: e.end_date || null
      }));
      const single = calDB.filter(e => e.recur === 'none').map(e => ({
        id: e.id, title: e.title, maestro: e.maestro,
        startH: e.start_h, startM: e.start_m, endH: e.end_h, endM: e.end_m,
        date: e.data, note: e.note || ''
      }));
      setCalRecurring(recurring);
      setCalSingle(single);
    }
  };

  const applicaMaestri = (maestriData) => {
    setMaestriDB(maestriData);
    // Ricostruisce maestriColors dai maestri in DB
    // Indicizza sia per nome breve ("ANTONELLO") che per nome completo ("ANTONELLO BUONOCORE")
    // così i lookup funzionano sia dagli allievi (nome breve) che dal calendario (nome completo)
    const colorsFromDB = {};
    maestriData.forEach(m => {
      const color = m.color || '#E9622A';
      const keyNome = (m.nome || '').trim().toUpperCase();
      const keyFull = `${m.nome || ''} ${m.cognome || ''}`.trim().toUpperCase();
      if (keyNome) colorsFromDB[keyNome] = color;
      if (keyFull && keyFull !== keyNome) colorsFromDB[keyFull] = color;
    });
    setMaestriColors(colorsFromDB);
  };

  const [erroreCaricamento, setErroreCaricamento] = useState(false);

  const loadFromDB = async (ruolo) => {
    try {
      setErroreCaricamento(false);
      setLoading(true);

      // Il maestro vede solo il calendario: il DB gli nega tutte le altre tabelle,
      // e dei maestri riceve solo nome e colore (rpc maestri_colori), non quote e contatti.
      if (ruolo === 'maestro') {
        await caricaCalendario();
        const colori = await leggi(supabase.rpc('maestri_colori'));
        if (colori) applicaMaestri(colori);
        setDbReady(true);
        return;
      }

      // Carica corsi da Supabase (migra da localStorage se necessario)
      const corsiDB = await leggi(supabase.from('corsi').select('*').order('id'));
      if (corsiDB && corsiDB.length > 0) {
        setCorsi(corsiDB.map(dbCorsoToState));
      }

      // Carica allievi
      const allieviDB = await leggi(supabase.from('allievi').select('*').order('id'));
      if (allieviDB && allieviDB.length > 0) {
        setAllievi(allieviDB.map(a => ({
          id: a.id, maestro: a.maestro, nome: a.nome,
          retta: a.retta || 120, iscrizione: a.iscrizione || 20,
          note: a.note || '', rettaPagata: a.retta_pagata || false,
          iscrPagata: a.iscr_pagata || false,
          trattamento: a.trattamento || 'normale',
          metodoPagamento: a.metodo_pagamento || '',
          specNoRetta: a.spec_no_retta || false,
          specNoIscrizione: a.spec_no_iscrizione || false,
          specPrezzoCustom: a.spec_prezzo_custom ?? null,
          solfeggio: a.solfeggio || false,
          corsi: a.corsi || [],
          lezioniMaestro: a.lezioni_maestro || {},
          telefono: a.telefono || '',
          email: a.email || '',
          dataNascita: a.data_nascita || null,
          corsoId: a.corso_id ?? null,
          sconto: a.sconto || 0,
          scontoMDI: a.sconto_mdi || 0,
          escludiDashboard: a.escludi_dashboard || false,
          pause: a.pause || []
        })));
        // Aggiorna nextId al massimo ID esistente + 1 per evitare conflitti
        const maxId = Math.max(...allieviDB.map(a => a.id));
        setNextId(maxId + 1);
      }

      // Carica pagamenti
      const pagDB = await leggi(supabase.from('pagamenti').select('*').order('id'));
      if (pagDB && pagDB.length > 0) {
        setPagamenti(pagDB.map(p => ({
          id: p.id, nome: p.nome, maestro: p.maestro,
          retta: p.retta || 120, quota_m: p.quota_m,
          stato: p.stato || 'attesa', metodo: p.metodo || '', note: p.note || ''
        })));
      }

      // Carica presenze
      const presDB = await leggi(supabase.from('presenze').select('*'));
      if (presDB && presDB.length > 0) {
        const presMap = {};
        presDB.forEach(p => {
          const m = p.mese || 'marzo-2026';
          if (!presMap[m]) presMap[m] = {};
          if (!presMap[m][p.allievo_id]) presMap[m][p.allievo_id] = {};
          presMap[m][p.allievo_id][p.giorno] = p.stato;
        });
        setPresenze(presMap);
      }

      // Carica calendario
      await caricaCalendario();

      // Carica stipendi pagati (struttura mensile: {mese: {maestro: bool}})
      const stipDB = await leggi(supabase.from('stipendi_pagati').select('*'));
      if (stipDB) {
        const stipMap = {};
        stipDB.forEach(s => {
          if (!s.mese) return; // ignora righe legacy senza mese
          if (!stipMap[s.mese]) stipMap[s.mese] = {};
          stipMap[s.mese][s.maestro] = s.pagato;
        });
        setStipendi(stipMap);
      }

      // Carica movimenti cassa
      const cassaDB = await leggi(supabase.from('cassa_movimenti').select('*').order('created_at', {ascending: false}));
      if (cassaDB) setMovimentiCassa(cassaDB);

      // Carica ore solfeggio
      const solfDB = await leggi(supabase.from('solfeggio_ore').select('*'));
      if (solfDB && solfDB.length > 0) {
        const ORDER = ['Settembre','Ottobre','Novembre','Dicembre','Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno'];
        setSolfeggioMesi(solfDB.sort((a,b)=>ORDER.indexOf(a.mese)-ORDER.indexOf(b.mese))
          .map(r=>({mese:r.mese, ore:r.ore||0, pagato:r.pagato||false, note:r.note||''})));
      }

      // Carica lezioni solfeggio (solfeggio_lezioni)
      const solfLezDB = await leggi(supabase.from('solfeggio_lezioni').select('*'));
      if (solfLezDB) {
        const lez = {};
        solfLezDB.forEach(r => { lez[r.data] = { ore: Number(r.ore_lezione ?? r.ore) || 0, confermato: r.confermato || false }; });
        setSolfeggioLezioni(lez);
      }
      // Carica presenze solfeggio (solfeggio_presenze)
      const solfPresDB = await leggi(supabase.from('solfeggio_presenze').select('*'));
      if (solfPresDB) {
        const pres = {};
        solfPresDB.forEach(r => {
          if (!pres[r.data]) pres[r.data] = {};
          pres[r.data][r.allievo_id] = r.stato;
        });
        setSolfeggioPresenze(pres);
      }

      // Carica maestri DB
      const maestriData = await leggi(supabase.from('maestri').select('*').order('nome'));
      if (maestriData) applicaMaestri(maestriData);

      // Carica pagamenti mensili rette + MDI
      const retteDB = await leggi(supabase.from('rette_mensili').select('*'));
      if (retteDB && retteDB.length > 0) {
        const pm = {}, mdi = {}, ore = {};
        retteDB.forEach(r => {
          if (!pm[r.mese]) pm[r.mese] = {};
          pm[r.mese][r.allievo_id] = { retta: r.retta_pagata, iscr: r.iscr_pagata, importo: (r.retta_importo != null ? r.retta_importo : null), sconto: (r.sconto_mese != null ? r.sconto_mese : null), rettaAt: r.retta_pagata_at ?? null, iscrAt: r.iscr_pagata_at ?? null, bonifico: r.bonifico ?? false };
          if (r.mdi_pagato) {
            if (!mdi[r.mese]) mdi[r.mese] = {};
            mdi[r.mese][r.allievo_id] = true;
          }
          if (r.mdi_ore) {
            if (!ore[r.mese]) ore[r.mese] = {};
            ore[r.mese][r.allievo_id] = r.mdi_ore;
          }
        });
        setPagamentiMensili(pm);
        setPagamentiMDI(mdi);
        setOreMultiMese(ore);
      }

      // Carica config
      const configDB = await leggi(supabase.from('config').select('*'));
      if (configDB) {
        const nome = configDB.find(r => r.chiave === 'segreteria_nome');
        if (nome) setSegreteriaNome(nome.valore);
      }

      // Carica presenze segreteria
      const segPresDB = await leggi(supabase.from('segreteria_presenze').select('*'));
      if (segPresDB && segPresDB.length > 0) {
        const pres = {};
        segPresDB.forEach(r => {
          if (!pres[r.mese]) pres[r.mese] = {};
          pres[r.mese][r.giorno] = true;
        });
        setNicolPresenze(pres);
      }

      // Carica pagamenti segreteria
      const segPagDB = await leggi(supabase.from('segreteria_pagamenti').select('*'));
      if (segPagDB && segPagDB.length > 0) {
        const pag = {};
        segPagDB.forEach(r => { pag[r.mese] = { pagato: r.pagato }; });
        setNicolMesi(pag);
      }

      // Carica sala prove
      const salaDB = await leggi(supabase.from('sala_prove').select('*').order('created_at', { ascending: true }));
      if (salaDB && salaDB.length > 0) {
        setSala(salaDB.map(r => ({ id: r.id, nome: r.nome || '', data: r.data || '', pagato: Number(r.pagato) || 0, bibite: Number(r.bibite) || 0 })));
      }

      // Carica presenze Musica d'Insieme
      const mdiPresDB = await leggi(supabase.from('mdi_presenze').select('*'));
      if (mdiPresDB && mdiPresDB.length > 0) {
        const mp = {};
        mdiPresDB.forEach(r => {
          if (!mp[r.mese]) mp[r.mese] = {};
          if (!mp[r.mese][r.allievo_id]) mp[r.mese][r.allievo_id] = {};
          mp[r.mese][r.allievo_id][r.giorno] = true;
        });
        setMdiPresenze(mp);
      }

      setDbReady(true);
    } catch(err) {
      console.error('Errore caricamento DB:', err);
      // Sessione scaduta o non valida: si torna al login. Altrimenti (rete, server)
      // si blocca l'app con un messaggio, invece di mostrare liste vuote.
      const msg = `${err?.code || ''} ${err?.message || ''}`;
      if (/PGRST30[0-9]|JWT|42501/.test(msg)) await supabase.auth.signOut();
      else setErroreCaricamento(true);
    } finally {
      setLoading(false);
    }
  };

  // Salva allievo singolo su DB
  const saveAllievoToDB = async (a) => {
    return await supabase.from('allievi').upsert({
      id: a.id, nome: a.nome, maestro: a.maestro,
      retta: a.retta, iscrizione: a.iscrizione || 20,
      note: a.note || '', retta_pagata: a.rettaPagata || false,
      iscr_pagata: a.iscrPagata || false,
      trattamento: a.trattamento || 'normale',
      metodo_pagamento: a.metodoPagamento || '',
      spec_no_retta: a.specNoRetta || false,
      spec_no_iscrizione: a.specNoIscrizione || false,
      spec_prezzo_custom: a.specPrezzoCustom ?? null,
      solfeggio: a.solfeggio || false,
      corsi: a.corsi || [],
      lezioni_maestro: a.lezioniMaestro || {},
      telefono: a.telefono || '',
      email: a.email || '',
      data_nascita: a.dataNascita || null,
      corso_id: a.corsoId != null ? Number(a.corsoId) : null,
      sconto: a.sconto || 0,
      sconto_mdi: a.scontoMDI || 0,
      escludi_dashboard: a.escludiDashboard || false,
      pause: a.pause || []
    });
  };

  // Salva presenza su DB: una sola riga per allievo/mese/giorno (vincolo presenze_una_per_giorno).
  // Con "cancella e reinserisci" i tocchi veloci si accavallavano e lasciavano più righe.
  const savePresenzaToDB = async (allievoId, giorno, stato, mese) => {
    const { error } = stato
      ? await supabase.from('presenze')
          .upsert({ allievo_id: allievoId, giorno, stato, mese }, { onConflict: 'allievo_id,mese,giorno' })
      : await supabase.from('presenze')
          .delete().eq('allievo_id', allievoId).eq('giorno', giorno).eq('mese', mese);
    if (error) notify('Presenza non salvata: ' + error.message, 'err');
  };

  // Salva pagamento su DB
  const savePagamentoToDB = async (p) => {
    await supabase.from('pagamenti').upsert({
      id: p.id, nome: p.nome, maestro: p.maestro,
      retta: p.retta, quota_m: p.quota_m || null,
      stato: p.stato, metodo: p.metodo || '', note: p.note || ''
    });
  };

  // Salva stipendio maestro su DB
  const saveStipendioToDB = async (maestro, pagato, mese) => {
    await supabase.from('stipendi_pagati').upsert({ maestro, mese, pagato }, { onConflict: 'maestro,mese' });
  };

  // Salva evento calendario su DB
  const saveEventoToDB = async (ev, isRecurring) => {
    const row = {
      id: ev.id, title: ev.title, maestro: ev.maestro,
      start_h: ev.startH, start_m: ev.startM,
      end_h: ev.endH, end_m: ev.endM,
      recur: isRecurring ? (ev.recur || 'weekly') : 'none',
      dow: isRecurring ? ev.dow : null,
      data: isRecurring ? null : ev.date,
      note: ev.note || '',
      start_date: ev.startDate || null,
      end_date: ev.endDate || null
    };
    await supabase.from('calendario').upsert(row);
  };


    const notify = (msg,t="ok") => { setNotif({msg,t}); setTimeout(()=>setNotif(null),3000); };

  // Naviga al calendario di un allievo (per id o per nome)
  const goToAllievo = (nomeOrId) => {
    const a = typeof nomeOrId==="number"
      ? allieviAll.find(x=>x.id===nomeOrId)
      : allieviAll.find(x=>x.nome===nomeOrId || x.nome.includes(nomeOrId) || nomeOrId.includes(x.nome));
    if(!a) return;
    setPresAllievoFrom(tab);
    setPresAllievo(a.id);
    setTab("presenze");
  };

  // Naviga al filtro maestro in Presenze
  const goToMaestro = (m) => {
    setFiltroMP(m);
    setPresAllievo(null);
    setTab("presenze");
  };

  // Un nome per maestro (nome completo), derivato da maestriDB se disponibile
  const mkeys = maestriDB.length > 0
    ? maestriDB.map(m => `${m.nome||''} ${m.cognome||''}`.trim().toUpperCase())
    : Object.keys(maestriColors).filter(k => !k.includes(' ')); // fallback: solo nomi brevi

  // Maestri archiviati (maestri.archiviato) spariscono da filtri, tendine ed elenchi, ma restano
  // visibili nei mesi in cui hanno comunque allievi o uno stipendio: nessun dato o importo sparisce.
  // I totali (stipendi, dashboard, storico) usano sempre mkeys completo.
  const archiviatiM = useMemo(() => new Set(maestriDB.filter(m => m.archiviato)
    .map(m => `${m.nome||''} ${m.cognome||''}`.trim().toUpperCase())), [maestriDB]);
  const mkeysVisibili = (mese = meseAllievi, lista = allievi) => mkeys.filter(m =>
    !archiviatiM.has(m) ||
    lista.some(a => getAllievoCorsi(a).some(c => c.maestro === m)) ||
    stipendioMaestro(m, mese) > 0);
  // Per assegnare un maestro a un allievo o a un nuovo corso: solo i non archiviati
  const mkeysAssegnabili = mkeys.filter(m => !archiviatiM.has(m));

  // Imposta uno stato preciso (schermo + DB); stato null = cancella
  const scriviPresenza = (id, day, mese, stato) => {
    setPresenze(prev => {
      const mesePz = {...(prev[mese]||{})};
      const pz = {...(mesePz[id]||{})};
      if(stato) pz[day] = stato; else delete pz[day];
      mesePz[id] = pz;
      return {...prev, [mese]: mesePz};
    });
    if(dbReady) savePresenzaToDB(id, day, stato || null, mese);
  };
  // Come scriviPresenza, ma annullabile con Ctrl+Z
  const setPresenza = (id, day, mese, stato) => {
    saveHistory({ tipo:'presenza', id, day, mese, prima: presenze[mese]?.[id]?.[day] ?? null });
    scriviPresenza(id, day, mese, stato);
  };

  // Tocco su una casella presenze. Vuota → Presente 1h (l'81% delle presenze registrate);
  // doppio tocco su una vuota, o tocco su una già segnata, → menù per scegliere o cancellare.
  // Il tocco singolo su una vuota aspetta un attimo per capire se arriva il secondo.
  const [menuPresenza, setMenuPresenza] = useState(null); // {id, nome, day, mese, rect}
  const toccoInSospeso = useRef(null); // {chiave, timer, segna}
  // Da computer il menù si chiude anche con Esc
  useEffect(() => {
    if(!menuPresenza) return;
    const esc = (e) => { if(e.key === 'Escape') setMenuPresenza(null); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [menuPresenza]);
  const apriMenuPresenza = (el, a, day, mese) =>
    setMenuPresenza({id:a.id, nome:a.nome, day, mese, rect:el.getBoundingClientRect(), t:Date.now()});
  const gestiCasella = (a, day, mese) => ({
    className: "casella-pz",
    onClick: (e) => {
      const el = e.currentTarget;
      const chiave = `${a.id}|${mese}|${day}`;
      const sospeso = toccoInSospeso.current;
      if(sospeso) {
        clearTimeout(sospeso.timer);
        toccoInSospeso.current = null;
        if(sospeso.chiave === chiave) { apriMenuPresenza(el, a, day, mese); return; } // doppio tocco
        sospeso.segna(); // tocco rapido su un'altra casella: la precedente si segna subito
      }
      if(presenze[mese]?.[a.id]?.[day]) { apriMenuPresenza(el, a, day, mese); return; }
      const segna = () => setPresenza(a.id, day, mese, "1p");
      toccoInSospeso.current = {chiave, segna, timer: setTimeout(() => { toccoInSospeso.current = null; segna(); }, 280)};
    },
  });

  const togglePagamento = (id) => {
    saveHistory({ tipo:'pagamento', prima: pagamenti.find(p => p.id === id) });
    setPagamenti(prev => {
      const updated = prev.map(p => {
        if(p.id!==id) return p;
        const next = p.stato==="attesa"?"pagato":p.stato==="pagato"?"ritardo":"attesa";
        return {...p,stato:next};
      });
      const p = updated.find(x=>x.id===id);
      if(p && dbReady) savePagamentoToDB(p);
      return updated;
    });
  };

  const oggiISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  // Scrive le pause prima sul DB e solo dopo sullo stato: se il salvataggio fallisce
  // la schermata non deve mostrare un'archiviazione che in realtà non è avvenuta.
  const salvaPause = async (id, pause, msg, tipo) => {
    if(dbReady) {
      const { error } = await supabase.from('allievi').update({ pause }).eq('id', id);
      if(error) {
        notify(error.code === '42703'
          ? "Manca la colonna 'pause' sul database: esegui l'ALTER TABLE su Supabase."
          : "Errore: " + error.message, "err");
        return false;
      }
    }
    setAllievi(prev => prev.map(a => a.id === id ? {...a, pause} : a));
    notify(msg, tipo);
    return true;
  };

  // Archivia l'allievo invece di cancellarlo: apre un periodo di pausa. Presenze,
  // rette e pagamenti restano intatti sul DB e i mesi passati non cambiano mai.
  const archiviaAllievo = async (id) => {
    const allievo = allieviAll.find(a => a.id === id);
    if(!allievo || isArchiviato(allievo)) { setConfirmDelete(null); return; }
    const pause = [...(allievo.pause || []), { dal: oggiISO(), al: null }];
    setConfirmDelete(null);
    const ok = await salvaPause(id, pause, "Allievo archiviato. Lo storico è conservato.", "warn");
    if(!ok) return;                       // salvataggio fallito: niente undo, niente uscita dal profilo
    setDeletedAllievo({ allievo });
    if(presAllievo === id) setPresAllievo(null);
  };

  // modo 'riprende' → chiude la pausa da oggi: i mesi di pausa restano fuori dai conti.
  // modo 'annulla'  → elimina la pausa: l'allievo torna attivo anche nei mesi di pausa.
  const ripristinaAllievo = async (id, modo = 'riprende') => {
    const allievo = allieviAll.find(a => a.id === id);
    if(!allievo) return;
    const aperte = (allievo.pause || []).filter(p => !p.al);
    if(aperte.length === 0) return;
    const pause = modo === 'annulla'
      ? (allievo.pause || []).filter(p => p.al)
      : (allievo.pause || []).map(p => p.al ? p : {...p, al: oggiISO()});
    setDeletedAllievo(null);
    await salvaPause(id, pause,
      modo === 'annulla' ? "Archiviazione annullata." : "Allievo ripristinato da questo mese.", "ok");
  };

  // Undo dal toast: annulla del tutto, perché è un'archiviazione appena fatta per errore
  const undoElimina = async () => {
    if(!deletedAllievo) return;
    await ripristinaAllievo(deletedAllievo.allievo.id, 'annulla');
  };

  // Storico = almeno una presenza (lezioni, MDI, solfeggio) o una riga in rette_mensili.
  // Eliminare un allievo con storico cambierebbe i conti dei mesi passati.
  const haStorico = useCallback((id) =>
    Object.values(presenze).some(m => Object.values(m?.[id] || {}).some(Boolean)) ||
    Object.values(mdiPresenze).some(m => Object.values(m?.[id] || {}).some(Boolean)) ||
    Object.values(solfeggioPresenze).some(d => d?.[id]) ||
    Object.values(pagamentiMensili).some(m => m?.[id])
  , [presenze, mdiPresenze, solfeggioPresenze, pagamentiMensili]);

  // Eliminazione definitiva, solo per archiviati senza storico. Prima di cancellare
  // ricontrolla sul DB: un altro dispositivo potrebbe aver registrato qualcosa nel frattempo.
  const eliminaAllievoDefinitivo = async (id) => {
    const allievo = allieviAll.find(a => a.id === id);
    if(!allievo || !isArchiviato(allievo) || haStorico(id)) return;
    if(dbReady) {
      const tabelle = ['presenze', 'rette_mensili', 'mdi_presenze', 'solfeggio_presenze'];
      const conteggi = await Promise.all(tabelle.map(t =>
        supabase.from(t).select('allievo_id', { count: 'exact', head: true }).eq('allievo_id', id)));
      const fallito = conteggi.find(r => r.error);
      if(fallito) { notify("Errore: " + fallito.error.message, "err"); return; }
      if(conteggi.some(r => r.count > 0)) {
        notify("L'allievo ha presenze o rette registrate: resta archiviato.", "err");
        return;
      }
      const { data, error } = await supabase.from('allievi').delete().eq('id', id).select('id');
      if(error || !data?.length) { notify("Errore: " + (error?.message || "allievo non eliminato"), "err"); return; }
    }
    setAllievi(prev => prev.filter(a => a.id !== id));
    if(deletedAllievo?.allievo.id === id) setDeletedAllievo(null);
    if(presAllievo === id) setPresAllievo(null);
    notify(`${allievo.nome} eliminato definitivamente.`, "ok");
  };

  const addAllievo = async () => {
    if(!form.nome||!form.maestro) return;
    const id = nextId; setNextId(id+1);
    const newA = {id, maestro:form.maestro, nome:form.nome,
      retta:Number(form.retta||0), iscrizione:form.iscrizione?Number(form.iscrizione):null,
      note:form.note||"", rettaPagata:false, iscrPagata:false,
      trattamento:'normale', metodoPagamento:'contanti',
      specNoRetta:false, specNoIscrizione:false, specPrezzoCustom:null, sconto:0, scontoMDI:0, escludiDashboard:false,
      corsi:[{maestro:form.maestro, strumento:form.strumento||'', retta:Number(form.retta||0), quota_m:null}]};
    if(dbReady) {
      const { error } = await saveAllievoToDB(newA);
      if(error) { notify("Errore salvataggio: " + error.message, "err"); return; }
    }
    setAllievi(prev=>[...prev, newA]);
    setModal(null); setForm({}); notify("Allievo aggiunto!");
  };

  const addMaestro = () => {
    if(!form.nome) return;
    const nome = form.nome.toUpperCase();
    setMaestriColors(prev=>({...prev,[nome]:form.color||"#E9622A"}));
    setModal(null); setForm({}); notify("Maestro aggiunto!");
  };

  const addSala = async (dataOverride) => {
    const today = new Date();
    const data = dataOverride || `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    // Restituisce l'id della nuova riga, così la schermata può metterci subito il cursore
    if (dbReady) {
      const { data: ins, error } = await supabase.from('sala_prove').insert({ nome:'', data, pagato:0, bibite:0 }).select().single();
      if (error || !ins) { notify('Errore: ' + (error?.message || 'prenotazione non creata'), 'err'); return null; }
      setSala(prev => [...prev, { id: ins.id, nome:'', data, pagato:0, bibite:0 }]);
      return ins.id;
    }
    const id = Date.now();
    setSala(prev => [...prev, { id, nome:'', data, pagato:0, bibite:0 }]);
    return id;
  };

  // Se il DB rifiuta l'operazione, la riga torna com'era e compare un avviso
  const deleteSala = async (id) => {
    const riga = sala.find(r => r.id === id);
    setSala(prev => prev.filter(r => r.id !== id));
    if (!dbReady) return;
    const { error } = await supabase.from('sala_prove').delete().eq('id', id);
    if (error) {
      notify('Errore eliminazione: ' + error.message, 'err');
      if (riga) setSala(prev => [...prev, riga]);
    }
  };

  const updateSalaCell = async (id, field, val) => {
    const prima = sala.find(r => r.id === id);
    setSala(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));
    if (!dbReady) return;
    const { error } = await supabase.from('sala_prove').update({ [field]: val }).eq('id', id);
    if (error) {
      notify('Errore salvataggio: ' + error.message, 'err');
      if (prima) setSala(prev => prev.map(r => r.id === id ? prima : r));
    }
  };

  const s = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));

  const ALL_NAV_ITEMS = [
    {id:"dashboard",  label:"Dashboard",   icon:"𝄞", roles:["admin"]},
    {id:"calendario", label:"Calendario",  icon:"♬", roles:["admin","segreteria","maestro"]},
    {id:"presenze",   label:"Presenze",    icon:"♫", roles:["admin","segreteria"]},
    {id:"segreteria", label:"Segreteria",  icon:"♩", roles:["admin","segreteria"]},
    {id:"mdi",        label:"Mus. Insieme",icon:"♫", roles:["admin","segreteria"]},
    {id:"solfeggio",  label:"Solfeggio",   icon:"♪", roles:["admin","segreteria"]},
    {id:"sala",       label:"Sala Prove",  icon:"♩", roles:["admin","segreteria"]},
    {id:"db-allievi", label:"DB Allievi",  icon:"🗃", roles:["admin","segreteria"]},
    {id:"db-maestri", label:"DB Maestri",  icon:"🎓", roles:["admin","segreteria"]},
    {id:"db-corsi",   label:"DB Corsi",    icon:"🎵", roles:["admin","segreteria"]},
    {id:"utenti",     label:"Utenti",      icon:"👤", roles:["admin"]},
  ];
  const NAV_ITEMS = ALL_NAV_ITEMS.filter(i => !userRole || i.roles.includes(userRole));
  const goTab = (id) => { setTab(id); setPresAllievo(null); setSearch(""); setFiltroM("TUTTI"); setFiltroMP("TUTTI"); setCorsiOpen(false); };

  const inp = (id,label,type="text",ph="") => (
    <div style={{marginBottom:14}}>
      <label style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:P.muted,letterSpacing:2,display:"block",marginBottom:6,textTransform:"uppercase"}}>{label}</label>
      <input id={id} type={type} placeholder={ph} value={form[id]||""} onChange={s(id)}
        style={{width:"100%",background:P.card,border:`1px solid ${P.border}`,borderRadius:8,
          padding:"10px 13px",color:P.text,fontSize:15,fontFamily:"'Figtree',sans-serif"}}
        onFocus={e=>{e.target.style.borderColor=P.goldBorder;e.target.style.boxShadow=`0 0 0 3px ${P.goldGlow}`;}}
        onBlur={e=>{e.target.style.borderColor=P.border;e.target.style.boxShadow="none";}}/>
    </div>
  );

  // Restituisce il corso (da state) associato a un allievo tramite corsoId, o null
  const getCorsoAllievo = (a) => {
    if (!a.corsoId) return null;
    return corsi.find(c => String(c.id) === String(a.corsoId)) || null;
  };

  // Importo reale di un allievo per un mese dato:
  // — corso multi → getCostoMusicaInsieme (basato su presenze × ore × €/h)
  // — corso normale → rettaNetta (retta effettiva meno eventuale sconto)
  const getImportoAllievoMese = (a, mese) => {
    const corso = getCorsoAllievo(a);
    if (corso && corso.tipo === 'multi') return getCostoMusicaInsieme(a, mese);
    return rettaNetta(a, mese);
  };

  // Calcola stipendio maestro
  // — corsi tipo "multi": quotaOraria × durataLezioneOre × lezioniMensili × n.allievi
  //   (tutti gli allievi contano, anche i speciali: pagano o no, il maestro ha lavorato)
  // — corsi normali: presenze svolte × €18  (logica invariata)
  // — in entrambi i casi aggiunge quota_custom dal maestriDB
  // Retta base per il calcolo stipendio: usa sempre la retta dal profilo corsi,
  // ignorando specNoRetta, specPrezzoCustom e sconto (il maestro viene sempre pagato sulla retta piena)

  // Retta netta per entrate: retta effettiva meno lo sconto applicato
  const rettaNetta = (a, mese = meseAllievi) =>
    Math.max(0, getRettaImportoMese(a.id, mese) - getScontoMese(a.id, mese));

  const stipendioMaestro = (nomeMaestro, mese) => {
    const key = nomeMaestro.trim().toUpperCase();
    // Tollerante: "ANTONELLO" matcha "ANTONELLO BUONOCORE" e viceversa
    const matchM = (name) => {
      const n = (name || '').trim().toUpperCase();
      return n === key || n.startsWith(key + ' ') || key.startsWith(n + ' ');
    };
    let totale = 0;

    // Un allievo conta per lo stipendio in un mese solo se ha attività registrata
    const isAttivoInMese = (a) =>
      !!(pagamentiMensili[mese]?.[a.id]) ||
      !!(presenze[mese]?.[a.id] && Object.keys(presenze[mese][a.id]).length > 0) ||
      !!(pagamentiMDI[mese]?.[a.id]);

    // ── 1. Corsi multi-maestro ─────────────────────────────────────────────────
    const corsiMulti = corsi.filter(c =>
      c.tipo === 'multi' &&
      Array.isArray(c.maestri) &&
      c.maestri.some(m => matchM(m.nome))
    );

    for (const corso of corsiMulti) {
      const infoMaestro = corso.maestri.find(m => matchM(m.nome));
      if (!infoMaestro) continue;
      const allieviCorso = allievi.filter(a =>
        String(a.corsoId) === String(corso.id)
      );
      // Stipendio MDI: solo se il pagamento MDI è stato confermato per l'allievo
      totale += allieviCorso.reduce((s, a) => {
        if (!getMDIPagato(a.id, mese)) return s;
        const ore = getOreMDI(a.id, mese);
        return s + infoMaestro.quotaOraria * ore;
      }, 0);
    }

    // ── 2. Corsi singolo: importo mensile × ratio maestro × % ───────────────
    const percStipendio = isTitolare(nomeMaestro) ? 1.0 : 0.60;
    const suoiSingoli = allievi.filter(a =>
      getAllievoCorsi(a).some(cc => matchM(cc.maestro))
    );
    totale += suoiSingoli.reduce((s, a) => {
      const tuttiCorsi = getAllievoCorsi(a);
      const rettaTotCorsi = tuttiCorsi.reduce((sr, cc) => sr + (cc.retta || 0), 0);
      const rettaMaestro = tuttiCorsi
        .filter(cc => matchM(cc.maestro))
        .reduce((sr, cc) => sr + (cc.retta || 0), 0);
      const ratio = rettaTotCorsi > 0 ? rettaMaestro / rettaTotCorsi : 1;
      const importoMese = getRettaImportoMese(a.id, mese);
      return s + importoMese * ratio * percStipendio;
    }, 0);

    return totale;
  };

  // ── DASHBOARD ───────────────────────────────────────────────────────────────
  const Dashboard = ({allievi}) => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
      const t = setInterval(() => setNow(new Date()), 1000);
      return () => clearInterval(t);
    }, []);
    const timeStr = now.toLocaleTimeString('it-IT', {hour:'2-digit', minute:'2-digit', second:'2-digit'});
    const dateStr = now.toLocaleDateString('it-IT', {weekday:'long', day:'numeric', month:'long', year:'numeric'});

    const totRette = allievi.reduce((s,a)=>s+rettaNetta(a, meseAllievi),0);
    const totIncassatoRette = allievi.filter(a=>getRettaPagata(a.id)).reduce((s,a)=>s+rettaNetta(a, meseAllievi),0);
    const totIncassato = totIncassatoRette + totMDIIncassato(meseAllievi);
    const totIscrizioni = allievi.filter(a=>getIscrPagata(a.id)&&a.iscrizione).reduce((s,a)=>s+(a.iscrizione||0),0);

    // Stipendi maestri: somma stipendioMaestro() solo per non-titolari (il titolare è entrata, non costo)
    const totStipendi = mkeys.filter(m => !isTitolare(m)).reduce((s, m) => s + stipendioMaestro(m, meseAllievi), 0);
    const stipendiSoloMaestri = totStipendi;
    const allieviConRetta = allievi.filter(a => rettaNetta(a, meseAllievi) > 0);
    const allieviPaganti  = allievi.filter(a => getRettaPagata(a.id) && rettaNetta(a, meseAllievi) > 0).length;
    const retteNonAncoraIncassate = allievi.filter(a => !getRettaPagata(a.id) && rettaNetta(a, meseAllievi) > 0 && !getBonificoMese(a.id)).reduce((s,a) => s + rettaNetta(a, meseAllievi), 0);
    const rettaMedia = allieviConRetta.length > 0 ? totRette / allieviConRetta.length : 0;
    const allieviNecessari = rettaMedia > 0 ? Math.ceil(stipendiSoloMaestri / rettaMedia) : 0;
    const allieviMancanti = Math.max(0, allieviNecessari - allieviPaganti);
    const breakEvenRaggiunto = allieviPaganti >= allieviNecessari;
    const compensoTitolare = mkeys.filter(m =>  isTitolare(m)).reduce((tot, m) => tot + stipendioMaestro(m, meseAllievi), 0);
    const totScontiMese = allievi.reduce((s,a) => s + (a.sconto||0) + (a.scontoMDI||0), 0);

    // Netto atteso: per ogni allievo → retta − costo del suo corso
    // costo corso = quotaOraria × durataLezioneOre × lezioniMensili (singolo o multi)
    // Calcola il costo totale dei corsi per un allievo (MDI + tutti i corsi singoli)
    // Funziona anche per allievi con più corsi singoli + MDI contemporaneamente
    const costoCorsoPerAllievo = (a) => {
      let totale = 0;
      const matchCorsino = (mk, cs) => {
        if (cs.tipo !== 'singolo' || !cs.quotaOraria) return false;
        const cn = (cs.maestro || '').trim().toUpperCase();
        return cn === mk || cn.startsWith(mk + ' ') || mk.startsWith(cn + ' ');
      };
      // Corsi singoli: solo se il maestro NON è il titolare (MDI gestito separatamente)
      for (const ca of getAllievoCorsi(a)) {
        const mk = (ca.maestro || '').trim().toUpperCase();
        if (isTitolare(mk)) continue;
        const cs = corsi.find(c => matchCorsino(mk, c));
        if (cs) totale += cs.quotaOraria * (cs.durataLezioneOre||1) * (cs.lezioniMensili||1);
      }
      return totale;
    };
    const [nomeMeseNAraw] = meseAllievi.split('-');
    const annoMeseNA = parseInt(meseAllievi.split('-')[1], 10);
    const mIdxNA = NOMI_MESI_IT.indexOf(nomeMeseNAraw);
    const oreSolfConfermato = Object.entries(solfeggioLezioni).reduce((s,[ds,v]) => {
      const d = new Date(ds);
      if (d.getFullYear()===annoMeseNA && d.getMonth()===mIdxNA && v.confermato)
        return s + Number(v.ore||0);
      return s;
    }, 0);
    const costoSolfeggioMese = Math.round(oreSolfConfermato * NICOL_TARIFFA * 100) / 100;
    const meseTagNA = `${meseAllievi.split('-')[1]}-${String(MESI_IDX[nomeMeseNAraw]+1).padStart(2,'0')}`;
    const salaTotaleNA = sala.filter(r => r.data && r.data.startsWith(meseTagNA))
      .reduce((s,r) => s + (Number(r.pagato)||0) + (Number(r.bibite)||0), 0);
    const totMDILordo = totMDIIncassato(meseAllievi);
    const totMDIQuotaNonTitolari = allievi.reduce((s, a) => {
      if (!getMDIPagato(a.id, meseAllievi)) return s;
      const corso = getCorsoAllievo(a);
      if (!corso || corso.tipo !== 'multi') return s;
      const ore = getOreMDI(a.id, meseAllievi);
      return s + (corso.maestri||[])
        .filter(m => !isTitolare(m.nome))
        .reduce((sum, m) => sum + (m.quotaOraria||0) * ore, 0);
    }, 0);
    const totMDINetto = Math.round((totMDILordo - totMDIQuotaNonTitolari) * 100) / 100;
    const oreSegreteriaMese = Object.values(nicolPresenze[meseAllievi] || {}).filter(Boolean).length;
    const stipSegreteriaMese = Math.round(oreSegreteriaMese * NICOL_TARIFFA * 100) / 100;
    const nettoAtteso = allievi.reduce((s,a) => s + rettaNetta(a, meseAllievi) - costoCorsoPerAllievo(a), 0) + totIscrizioni + compensoTitolare - costoSolfeggioMese - stipSegreteriaMese + salaTotaleNA + totMDINetto;
    const totUscite = movimentiCassa.filter(m => Number(m.importo) < 0 && movAppartieneMese(m, meseAllievi)).reduce((s,m) => s + Math.abs(Number(m.importo)), 0);
    const movPosMese = movimentiCassa.filter(m => Number(m.importo) > 0 && movAppartieneMese(m, meseAllievi)).reduce((s,m) => s + Number(m.importo), 0);
    // Tutti gli stipendi non-titolari già pagati questo mese (da movimenti cassa)
    const tuttiStipGiaPagati = movimentiCassa.filter(m => Number(m.importo) < 0 && movAppartieneMese(m, meseAllievi) && (m.descrizione||'').startsWith('Stipendio ')).reduce((s,m) => s + Math.abs(Number(m.importo)), 0);
    // Stipendi maestri non-titolari teorici del mese
    const tuttiStipTeorici = mkeys.filter(m => !isTitolare(m)).reduce((s,m) => s + stipendioMaestro(m, meseAllievi), 0);
    // Solfeggio non ancora pagato
    const solfDaPagare = stipendiPagati[meseAllievi]?.['SOLFEGGIO'] ? 0 : costoSolfeggioMese;
    // Segreteria non ancora pagata (da nicolPresenze + nicolMesi)
    const segrDaPagare = nicolMesi[meseAllievi]?.pagato ? 0 : stipSegreteriaMese;
    // Totale ancora da pagare
    const tuttoDaPagare = Math.max(0, tuttiStipTeorici - tuttiStipGiaPagati) + solfDaPagare + segrDaPagare;
    const prelevabile = (totIncassato + totIscrizioni + movPosMese + retteNonAncoraIncassate) - (totUscite + tuttoDaPagare);
    const [showUsciteForm, setShowUsciteForm] = useState(false);
    const [usciteInput, setUsciteInput] = useState('');

    return (
      <div style={{animation:"fi .35s ease"}}>
        <div style={{marginBottom:isMobile?16:32}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:isMobile?"baseline":"flex-start",flexWrap:"wrap",gap:8,marginBottom:7}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.secondary,fontWeight:400,textTransform:"uppercase"}}>PANORAMICA</div>
            <div style={{textAlign:"right"}}>
              {/* Sul telefono l'ora c'è già in alto: solo la data */}
              {!isMobile && <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:22,fontWeight:600,color:P.text,letterSpacing:1}}>{timeStr}</div>}
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:P.secondary,letterSpacing:1,textTransform:"capitalize"}}>{dateStr}</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            {(() => {
              const btn = (dir, onClick) => (
                <button onClick={onClick}
                  style={{background:"transparent",border:`1px solid ${P.gold}`,
                    color:P.gold,borderRadius:8,width:36,height:36,
                    cursor:"pointer",fontSize:20,display:"flex",
                    alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {dir}
                </button>
              );
              return (
                <>
                  {btn('‹', () => setMeseAllievi(meseAdd(meseAllievi,-1)))}
                  <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5,margin:0}}>{mesiInfo(meseAllievi).label}</h1>
                  {btn('›', () => setMeseAllievi(meseAdd(meseAllievi,1)))}
                </>
              );
            })()}
          </div>
          {(() => {
            const isAnnulla = preMarkSnapshot?.mese === meseAllievi;
            return (
              <button onClick={toggleTutteRette}
                style={{marginTop:10,padding:"6px 16px",borderRadius:8,cursor:"pointer",
                  fontSize:13,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:1,textTransform:"uppercase",fontWeight:600,
                  background: isAnnulla ? `${P.red}18` : `${P.gold}15`,
                  border: `1px solid ${isAnnulla ? P.red : P.gold}55`,
                  color: isAnnulla ? P.red : P.gold}}>
                {isAnnulla ? '↩ Annulla segna tutte' : '✓ Segna tutte pagate'}
              </button>
            );
          })()}
        </div>

        {/* Riquadro Incassato in evidenza */}
        <div style={{background:P.card,border:`1px solid ${P.goldBorder}`,
          borderRadius:16,padding:isMobile?"16px":"24px 30px",marginBottom:isMobile?14:24,
          display:"flex",flexDirection:isMobile?"column":"row",alignItems:isMobile?"stretch":"center",
          justifyContent:"space-between",flexWrap:"wrap",gap:isMobile?14:16}}>
          <div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.secondary,marginBottom:6,textTransform:"uppercase"}}>ENTRATE · {mesiInfo(meseAllievi).label.toUpperCase()}</div>
            {/* Sul telefono Entrate e Uscite stanno affiancate */}
            <div style={{display:"flex",alignItems:"flex-end",gap:isMobile?20:32,flexWrap:"wrap",marginBottom:4}}>
              <div>
                <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?32:48,fontWeight:500,color:P.gold,lineHeight:1}}>€{totIncassato}</div>
                <div style={{fontSize:11,color:P.secondary,marginTop:3,letterSpacing:1.5,textTransform:"uppercase"}}>Entrate</div>
              </div>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?32:48,fontWeight:500,color:P.red,lineHeight:1}}>€{totUscite}</div>
                  <button onClick={()=>setShowUsciteForm(p=>!p)} title="Registra prelievo"
                    style={{width:26,height:26,borderRadius:"50%",border:`1px solid ${P.red}55`,background:`${P.red}18`,color:P.red,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,padding:0,flexShrink:0}}>+</button>
                </div>
                {showUsciteForm && (
                  <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center"}}>
                    <input type="number" min="0" placeholder="€ importo" value={usciteInput}
                      onChange={e=>setUsciteInput(e.target.value)}
                      onKeyDown={e=>{if(e.key==="Enter"&&usciteInput){addMovimentoCassa(-Math.abs(parseFloat(usciteInput)||0),"Prelievo Donato");setUsciteInput('');setShowUsciteForm(false);}}}
                      style={{background:P.bg,border:`1px solid ${P.border}`,borderRadius:6,padding:"5px 8px",color:P.text,width:100,fontSize:14,fontFamily:"'IBM Plex Mono',monospace"}} autoFocus/>
                    <button onClick={()=>{if(usciteInput){addMovimentoCassa(-Math.abs(parseFloat(usciteInput)||0),"Prelievo Donato");setUsciteInput('');setShowUsciteForm(false);}}}
                      style={{padding:"5px 10px",borderRadius:6,border:"none",background:P.red,color:"#fff",cursor:"pointer",fontSize:13,fontWeight:600}}>✓</button>
                  </div>
                )}
                <div style={{fontSize:11,color:P.secondary,marginTop:3,letterSpacing:1.5,textTransform:"uppercase"}}>Uscite</div>
              </div>
            </div>
            <div style={{fontSize:14,color:P.secondary,marginTop:4}}>
              Rette €{totIncassato} + Iscrizioni €{totIscrizioni}
            </div>
          </div>
          <div style={isMobile
            ? {display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"12px 6px"}
            : {display:"flex",gap:28,flexWrap:"wrap"}}>
            {[
              {
                l:"Rette",
                v:`€${totRette + totMDIIncassato(meseAllievi)}`,
                c:P.yellow,
                tip:[
                  {label:"Rette nette (tutti gli allievi)", value:`€${totRette}`},
                  {label:"MDI incassato", value:`€${totMDIIncassato(meseAllievi)}`},
                  {label:"Totale", value:`€${totRette + totMDIIncassato(meseAllievi)}`, highlight:true, sep:true},
                ]
              },
              {l:"Iscrizioni", v:`€${totIscrizioni}`, c:P.blue, tip:null},
              {
                l:"Stipendi",
                v:`€${totStipendi}`,
                c:P.red,
                tip:[
                  ...mkeysVisibili().filter(m=>!isTitolare(m)).map(m=>({
                    label: m.split(' ')[0],
                    value:`€${Math.round(stipendioMaestro(m,meseAllievi)*100)/100}`
                  })),
                  {label:"Totale", value:`€${Math.round(totStipendi*100)/100}`, highlight:true, sep:true},
                ]
              },
              ...(totScontiMese>0?[{l:"Sconti",v:`-€${totScontiMese}`,c:P.red,tip:null}]:[]),
              {
                l:"Netto atteso",
                v:`€${nettoAtteso}`,
                c:nettoAtteso>=0?P.green:P.red,
                tip:[
                  {label:"Rette nette", value:`€${Math.round(allievi.reduce((s,a)=>s+rettaNetta(a, meseAllievi),0)*100)/100}`},
                  {label:"+ MDI lordo", value:`€${totMDILordo}`},
                  {label:"− Quota maestro MDI", value:`-€${totMDIQuotaNonTitolari}`},
                  {label:"− Costi corsi", value:`-€${Math.round(allievi.reduce((s,a)=>s+costoCorsoPerAllievo(a),0)*100)/100}`},
                  {label:"+ Iscrizioni", value:`€${totIscrizioni}`},
                  {label:"+ Compenso titolare", value:`€${Math.round(compensoTitolare*100)/100}`},
                  {label:"− Solfeggio", value:`-€${costoSolfeggioMese}`},
                  {label:"− Segreteria", value:`-€${stipSegreteriaMese}`},
                  {label:"+ Sala prove", value:`€${salaTotaleNA}`},
                  {label:"Netto atteso", value:`€${nettoAtteso}`, highlight:true, sep:true},
                ]
              },
            ].map(s=>(
              <div key={s.l} style={{textAlign:"center",minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:2}}>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?19:28,fontWeight:400,color:s.c,whiteSpace:"nowrap"}}>{s.v}</div>
                  {s.tip && <InfoTip lines={s.tip}/>}
                </div>
                <div style={{fontSize:isMobile?11:13,color:P.secondary,marginTop:isMobile?2:4,letterSpacing:1}}>{s.l.toUpperCase()}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:isMobile?0:14,paddingTop:isMobile?12:14,borderTop:`1px solid ${P.borderSubtle}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <div>
              {breakEvenRaggiunto
                ? <span style={{fontSize:14,color:P.green,fontWeight:600}}>✓ Break even raggiunto — gli stipendi sono coperti dalle rette</span>
                : <span style={{fontSize:14,color:P.secondary}}>
                    Mancano <span style={{fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:P.red,fontSize:16}}>{allieviMancanti}</span> {allieviMancanti===1?'allievo':'allievi'} al break even <span style={{color:P.muted,fontSize:12}}>(retta media €{Math.round(rettaMedia)})</span>
                  </span>
              }
            </div>
            <span style={{fontSize:14,color:P.secondary,display:"inline-flex",alignItems:"center",gap:4}}>
              Prelevabile: <span style={{fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:prelevabile>=0?P.green:P.red,fontSize:16}}>{prelevabile>=0?'+':''}{prelevabile.toFixed(1)}€</span>
              <InfoTip lines={[
                {label:"Incassato (rette+MDI)", value:`€${totIncassato}`},
                {label:"+ Iscrizioni", value:`€${totIscrizioni}`},
                {label:"+ Entrate extra", value:`€${movPosMese}`},
                {label:"+ Rette non ancora incassate", value:`€${retteNonAncoraIncassate}`},
                {label:"− Uscite", value:`-€${totUscite}`},
                {label:"− Ancora da pagare", value:`-€${tuttoDaPagare}`},
                {label:"Prelevabile", value:`${prelevabile>=0?'+':''}${prelevabile.toFixed(1)}€`, highlight:true, sep:true},
              ]}/>
            </span>
          </div>
        </div>

        <GestioneCassa/>

        <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobile?10:16,marginBottom:isMobile?14:30}}>
          {[
            {icon:"♪",label:"Allievi",value:allievi.length,color:P.blue},
            {icon:"♬",label:"Maestri",value:maestriDB.filter(m=>!m.archiviato).length,color:P.gold},
            {icon:"♩",label:"Rette Totali",value:`€${totRette + totMDIIncassato(meseAllievi)}`,color:P.purple},
            {icon:"♫",label:"Stipendi",value:`€${totStipendi}`,color:P.red},
          ].map(s=>(
            <div key={s.label} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,
              padding:isMobile?"12px 14px":"22px 24px",borderTop:`2px solid ${s.color}33`,position:"relative"}}>
              {/* Sul telefono la nota musicale va nell'angolo invece che su una riga sua */}
              <div style={isMobile?{position:"absolute",top:10,right:12,fontSize:16,opacity:.7}:{fontSize:24,marginBottom:10}}>{s.icon}</div>
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?22:28,fontWeight:500,color:s.color}}>{s.value}</div>
              <div style={{fontSize:12,color:P.secondary,marginTop:4,letterSpacing:1.5}}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:isMobile?14:20,alignItems:"stretch"}}>

          {/* ── STIPENDI MAESTRI: da pagare ── */}
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,padding:isMobile?16:24,display:"flex",flexDirection:"column",maxHeight:520,overflowY:"auto"}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:4}}>Stipendi Maestri</div>
            <div style={{fontSize:13,color:P.secondary,marginBottom:16}}>Stato pagamento mensile</div>
            {mkeysVisibili().map(m=>{
              const c = mc(m,maestriColors);
              const stipendio = stipendioMaestro(m, meseAllievi);
              if(stipendio===0) return null;
              const titolare = isTitolare(m);
              const pagato = !!(stipendiPagati[meseAllievi]?.[m]);
              const rowBg    = titolare ? "rgba(74,222,128,0.06)"  : pagato ? "rgba(74,222,128,0.06)"  : "rgba(248,113,113,0.06)";
              const rowBdr   = titolare ? P.green+"22"             : pagato ? P.green+"22"             : P.red+"22";
              const valColor = titolare ? P.green                  : pagato ? P.green                  : P.red;
              const badge    = titolare ? "↑ Entrata"              : pagato ? "✓ Pagato"               : "Da pagare";
              const badgeBg  = titolare ? "rgba(74,222,128,0.15)"  : pagato ? "rgba(74,222,128,0.15)"  : "rgba(248,113,113,0.15)";
              return (
                <div key={m} style={{display:"flex",alignItems:"center",gap:10,
                  padding:"9px 12px",borderRadius:8,marginBottom:6,
                  background:rowBg, border:`1px solid ${rowBdr}`}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:c,flexShrink:0}}/>
                  <span style={{fontSize:15,fontWeight:500,flex:1,color:c}}>{m}</span>
                  <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:16,fontWeight:500,color:valColor}}>
                    {titolare ? '+' : ''}€{stipendio}
                  </span>
                  <span
                    onClick={titolare ? undefined : () => toggleStipendioConCassa(m, stipendio, meseAllievi)}
                    style={{fontSize:12,fontWeight:600,padding:"2px 8px",borderRadius:10,
                      background:badgeBg, color:valColor,
                      cursor: titolare ? 'default' : 'pointer',
                      userSelect: 'none'}}>
                    {badge}
                  </span>
                </div>
              );
            })}
            {/* ── Riga Solfeggio ── */}
            {(()=>{
              const [nomeMeseRaw] = meseAllievi.split('-');
              const annoMese = parseInt(meseAllievi.split('-')[1], 10);
              const mIdxDash = NOMI_MESI_IT.indexOf(nomeMeseRaw);
              const stipSolf = Object.entries(solfeggioLezioni).reduce((s,[ds,v])=>{
                const d = new Date(ds);
                if (d.getFullYear()===annoMese && d.getMonth()===mIdxDash && v.confermato)
                  return s + Number(v.ore||0) * NICOL_TARIFFA;
                return s;
              }, 0);
              const costoSolf = Math.round(stipSolf * 100) / 100;
              if (costoSolf === 0) return null;
              const pagatoSolf = !!(stipendiPagati[meseAllievi]?.['SOLFEGGIO']);
              const rowBgS  = pagatoSolf ? "rgba(74,222,128,0.06)"  : "rgba(248,113,113,0.06)";
              const rowBdrS = pagatoSolf ? P.green+"22"             : P.red+"22";
              const valCS   = pagatoSolf ? P.green                  : P.red;
              const badgeS  = pagatoSolf ? "✓ Pagato"               : "Da pagare";
              const badgeBgS= pagatoSolf ? "rgba(74,222,128,0.15)"  : "rgba(248,113,113,0.15)";
              return (
                <div style={{display:"flex",alignItems:"center",gap:10,
                  padding:"9px 12px",borderRadius:8,marginBottom:6,
                  background:rowBgS, border:`1px solid ${rowBdrS}`}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:"#8b5cf6",flexShrink:0}}/>
                  <span style={{fontSize:15,fontWeight:500,flex:1,color:"#8b5cf6"}}>SOLFEGGIO</span>
                  <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:16,fontWeight:500,color:valCS}}>
                    €{costoSolf}
                  </span>
                  <span
                    onClick={()=>toggleStipendioConCassa('SOLFEGGIO', costoSolf, meseAllievi)}
                    style={{fontSize:12,fontWeight:600,padding:"2px 8px",borderRadius:10,
                      background:badgeBgS, color:valCS, cursor:'pointer', userSelect:'none'}}>
                    {badgeS}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* ── ALLIEVI CHE NON HANNO ANCORA PAGATO ── */}
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,padding:isMobile?16:24,maxHeight:520,overflowY:"auto"}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:4}}>Rette in Sospeso</div>
            <div style={{fontSize:13,color:P.secondary,marginBottom:16}}>
              Allievi che non hanno ancora pagato la retta mensile
            </div>
            <div>
              {allieviConRetta.filter(a=>!getRettaPagata(a.id)).length === 0 ? (
                <div style={{textAlign:"center",padding:"20px 0",color:P.green,fontSize:15,fontWeight:600}}>
                  ✓ Tutti gli allievi hanno pagato!
                </div>
              ) : (
                allieviConRetta.filter(a=>!getRettaPagata(a.id))
                  .sort((a,b)=>(a.nome||'').localeCompare(b.nome||'','it'))
                  .map((a,i)=>{
                  const c=mc(a.maestro,maestriColors);
                  return (
                    <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,
                      padding:"8px 12px",borderRadius:8,marginBottom:5,
                      background:"rgba(248,113,113,0.05)",border:`1px solid rgba(248,113,113,0.15)`}}>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:P.muted,
                        minWidth:18,textAlign:"right",flexShrink:0}}>{i+1}</span>
                      <div style={{width:28,height:28,borderRadius:"50%",background:`${c}20`,
                        border:`1px solid ${c}44`,display:"flex",alignItems:"center",
                        justifyContent:"center",fontSize:13,color:c,fontWeight:700,flexShrink:0}}>
                        {a.nome.charAt(0)}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:500,overflow:"hidden",
                          textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.nome}</div>
                        <div style={{fontSize:12,color:P.secondary,marginTop:1}}>{a.maestro}</div>
                      </div>
                      <span style={{fontFamily:"'Syne',sans-serif",fontSize:16,
                        fontWeight:700,color:P.red,flexShrink:0}}>€{rettaNetta(a, meseAllievi)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── STORICO ANNO SCOLASTICO ── */}
        {(() => {
          const meseCorrente = getMeseCorrente();
          const idxCorrente = MESI_TIMELINE.indexOf(meseCorrente);
          const primoLabel = MESI_TIMELINE.length ? mesiInfo(MESI_TIMELINE[0]).label : '';
          const ultimoLabel = MESI_TIMELINE.length ? mesiInfo(MESI_TIMELINE[MESI_TIMELINE.length-1]).label : '';
          return (
            <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,padding:isMobile?16:24,marginTop:isMobile?14:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,marginBottom:3}}>Storico</div>
                  <div style={{fontSize:13,color:P.secondary}}>{primoLabel} — {ultimoLabel}</div>
                </div>
                {/* Totale complessivo */}
                {(() => {
                  const totals = MESI_TIMELINE.reduce((acc,mese) => {
                    const pm = pagamentiMensili[mese] || {};
                    const allieviMese = allieviDelMese(mese);
                    const entrateCash = allieviMese
                      .filter(a => !!(pm[a.id]?.retta) && !getBonificoMese(a.id, mese))
                      .reduce((s,a) => s + rettaNetta(a, mese), 0);
                    const iscrCash = allieviMese
                      .filter(a => !!(pm[a.id]?.iscr) && (a.iscrizione||0) > 0 && !getBonificoMese(a.id, mese))
                      .reduce((s,a) => s + (a.iscrizione||0), 0);
                    const meseTagSt = (() => { const [n,a] = mese.split('-'); return `${a}-${String(MESI_IDX[n]+1).padStart(2,'0')}`; })();
                    const salaProv = sala.filter(r => r.data && r.data.startsWith(meseTagSt)).reduce((s,r) => s + (Number(r.pagato)||0) + (Number(r.bibite)||0), 0);
                    const movMeseSt = movimentiCassa.filter(m => movAppartieneMese(m, mese));
                    const movPos = movMeseSt.filter(m => Number(m.importo) > 0)
                      .reduce((s,m) => s + Number(m.importo), 0);
                    const movNeg = movMeseSt.filter(m => Number(m.importo) < 0)
                      .reduce((s,m) => s + Math.abs(Number(m.importo)), 0);
                    const entrate = entrateCash + iscrCash + salaProv + movPos;
                    return { entrate: acc.entrate + entrate, uscite: acc.uscite + movNeg };
                  }, { entrate: 0, uscite: 0 });
                  const netto = totals.entrate - totals.uscite;
                  return (
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:20,fontWeight:600,color:netto>=0?P.green:P.red}}>
                        {netto >= 0 ? '+' : ''}€{netto}
                      </div>
                      <div style={{fontSize:12,color:P.secondary,letterSpacing:1}}>TOTALE NETTO</div>
                      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:15,color:P.gold,marginTop:2}}>
                        Entrate +€{totals.entrate}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)",gap:8}}>
                {MESI_TIMELINE.map((mese,idx) => {
                  const pm = pagamentiMensili[mese] || {};
                  const allieviMese = allieviDelMese(mese);
                  const entrate = allieviMese.reduce((s,a) => {
                    const p = pm[a.id] || {};
                    const imp = getRettaImportoMese(a.id, mese);
                    return s + (p.retta ? imp : 0) + (p.iscr ? (a.iscrizione||0) : 0);
                  }, 0);
                  // Rette cash nette: no bonifico, sconto applicato
                  const entrateCash = allieviMese
                    .filter(a => !!(pm[a.id]?.retta) && !getBonificoMese(a.id, mese))
                    .reduce((s,a) => s + rettaNetta(a, mese), 0);
                  const iscrCash = allieviMese
                    .filter(a => !!(pm[a.id]?.iscr) && (a.iscrizione||0) > 0 && !getBonificoMese(a.id, mese))
                    .reduce((s,a) => s + (a.iscrizione||0), 0);
                  const meseTagSt = (() => { const [n,a] = mese.split('-'); return `${a}-${String(MESI_IDX[n]+1).padStart(2,'0')}`; })();
                  const movMeseSt = movimentiCassa.filter(m => movAppartieneMese(m, mese));
                  const movPos = movMeseSt.filter(m => Number(m.importo) > 0).reduce((s,m) => s + Number(m.importo), 0);
                  const movNeg = movMeseSt.filter(m => Number(m.importo) < 0).reduce((s,m) => s + Math.abs(Number(m.importo)), 0);
                  const salaProv = sala.filter(r => r.data && r.data.startsWith(meseTagSt)).reduce((s,r) => s + (Number(r.pagato)||0) + (Number(r.bibite)||0), 0);
                  const hasData = Object.keys(pm).length > 0 || movMeseSt.length > 0 || salaProv > 0;
                  const nettoReale = !hasData ? 0 : (entrateCash + iscrCash + movPos + salaProv) - movNeg;
                  const mkeysMaestri = mkeys.filter(m => !isTitolare(m) && !m.toUpperCase().includes('SOLFEGGIO') && !m.toUpperCase().includes('SEGRETERIA'));
                  const stipendiTeoriciMese = !hasData ? 0 : mkeysMaestri.reduce((s,m) => s + stipendioMaestro(m, mese), 0);
                  const stipendiPagatiMese = movMeseSt.filter(m => m.importo < 0 && (m.descrizione||'').startsWith('Stipendio ')).reduce((s,m) => s + Math.abs(Number(m.importo)), 0);
                  const daPagare = !hasData ? 0 : Math.max(0, stipendiTeoriciMese - stipendiPagatiMese);
                  const allieviPaganti = allieviMese.filter(a => (pm[a.id]||{}).retta && rettaNetta(a, mese)>0).length;
                  const totTeorico = allieviMese.reduce((s,a) => s + rettaNetta(a, mese), 0);
                  const pct = totTeorico > 0 ? Math.min(100, Math.round((entrate / totTeorico) * 100)) : 0;
                  const isAttuale = mese === meseAllievi;
                  const isFuturo = idx > idxCorrente;
                  return (
                    <div key={mese} onClick={() => setMeseAllievi(mese)}
                      style={{
                        padding:"12px 14px",borderRadius:10,cursor:"pointer",
                        background: isAttuale ? P.goldGlow : P.card2,
                        border:`1px solid ${isAttuale ? P.goldBorder : P.borderSubtle}`,
                        opacity: isFuturo && !hasData ? 0.45 : 1,
                        transition:"background .15s,border-color .15s"
                      }}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <span style={{fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:700,
                          color: isAttuale ? P.gold : isFuturo ? P.muted : P.text}}>
                          {mesiInfo(mese).label}
                          {isAttuale && <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:P.gold,marginLeft:6,letterSpacing:1}}>ATTUALE</span>}
                        </span>
                        <span style={{display:"flex",alignItems:"center",gap:4,fontFamily:"'IBM Plex Mono',monospace",fontSize:16,fontWeight:600,
                          color: nettoReale > 0 ? P.green : nettoReale < 0 ? P.red : P.muted}}>
                          {!hasData ? "—" : `${nettoReale>=0?'+':''}€${nettoReale}`}
                          {!isFuturo && hasData && <InfoTip lines={[
                            {label:"Rette cash (nette)", value:`+€${entrateCash}`},
                            ...(iscrCash > 0 ? [{label:"Iscrizioni cash", value:`+€${iscrCash}`}] : []),
                            ...(salaProv > 0 ? [{label:"Sala prove", value:`+€${salaProv}`}] : []),
                            ...(movPos > 0 ? [{label:"Entrate extra (MDI, altri)", value:`+€${movPos}`}] : []),
                            ...(movNeg > 0 ? [{label:"Uscite", value:`-€${movNeg}`}] : []),
                            {label:"Netto reale", value:`${nettoReale>=0?'+':''}€${nettoReale}`, highlight:true, sep:true},
                          ]}/>}
                        </span>
                      </div>
                      {/* Barra progresso */}
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                        <div style={{flex:1,height:4,background:P.border,borderRadius:2}}>
                          <div style={{height:"100%",width:`${pct}%`,borderRadius:2,
                            background: isAttuale ? P.gold : pct>=80 ? P.green : pct>=50 ? P.yellow : P.red,
                            transition:"width .3s"}}/>
                        </div>
                        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:12,color:P.secondary,flexShrink:0,minWidth:28,textAlign:"right"}}>
                          {isFuturo && !hasData ? "" : `${pct}%`}
                        </span>
                      </div>
                      {/* Dettagli */}
                      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                        {hasData && (
                          <span style={{fontSize:12,color:P.secondary}}>
                            Da pagare <span style={{color:daPagare>0?P.red:P.green,fontFamily:"'IBM Plex Mono',monospace"}}>
                              €{daPagare}
                            </span>
                          </span>
                        )}
                        {!isFuturo && (
                          <span style={{fontSize:12,color:P.secondary}}>
                            <span style={{fontFamily:"'IBM Plex Mono',monospace",color:P.secondary}}>{allieviPaganti}</span> paganti
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

      </div>
    );
  };

  // ── ALLIEVI ─────────────────────────────────────────────────────────────────
  // Aggiorna i corsi di un allievo e allinea il campo maestro primario
  const updateAllievoCorsi = (id, newCorsi) => {
    if (!newCorsi || newCorsi.length === 0) return;
    saveHistory({ tipo:'allievo', prima: allieviAll.find(a => a.id === id) });
    setAllievi(prev => {
      const updated = prev.map(a => a.id===id
        ? {...a, corsi: newCorsi, maestro: newCorsi[0].maestro}
        : a);
      const a = updated.find(x=>x.id===id);
      if(a && dbReady) saveAllievoToDB(a);
      return updated;
    });
  };

  const updateAllievo = (id, field, value) => {
    saveHistory({ tipo:'allievo', prima: allieviAll.find(a => a.id === id) });
    setAllievi(prev => {
      const updated = prev.map(a => a.id===id ? {...a,[field]:value} : a);
      const a = updated.find(x=>x.id===id);
      if(a && dbReady) saveAllievoToDB(a);
      return updated;
    });
  };

  // Modifica la retta base (permanente) di un allievo dal DB Allievi.
  // Aggiorna sia `corsi[0].retta` sia il campo legacy `retta` (che ha precedenza
  // in getRettaImportoMese), così la nuova retta vale da subito ovunque.
  const updateAllievoRettaBase = (id, nuovaRetta) => {
    const val = Math.max(0, Number(nuovaRetta) || 0);
    saveHistory({ tipo:'allievo', prima: allieviAll.find(a => a.id === id) });
    setAllievi(prev => {
      const updated = prev.map(a => {
        if (a.id !== id) return a;
        const corsi = getAllievoCorsi(a);
        const newCorsi = corsi.map((c, i) => i===0 ? {...c, retta: val} : c);
        return {...a, corsi: newCorsi, retta: val};
      });
      const a = updated.find(x=>x.id===id);
      if(a && dbReady) saveAllievoToDB(a);
      return updated;
    });
  };

  // Annulla l'ultima azione (vedi saveHistory): rimette il valore di prima sullo schermo e nel DB
  const undo = () => {
    const voce = history[history.length - 1];
    if (!voce) { setNotif({msg:"Niente da annullare",t:"undo"}); setTimeout(()=>setNotif(null),2000); return; }
    setHistory(h => h.slice(0, -1));
    const seErrore = ({ error } = {}) => { if (error) notify('Annullamento non salvato: ' + error.message, 'err'); };
    if (voce.tipo === 'presenza') scriviPresenza(voce.id, voce.day, voce.mese, voce.prima);
    if (voce.tipo === 'allievo' && voce.prima) {
      setAllievi(prev => prev.map(a => a.id === voce.prima.id ? voce.prima : a));
      if (dbReady) saveAllievoToDB(voce.prima).then(seErrore);
    }
    if (voce.tipo === 'pagamento' && voce.prima) {
      setPagamenti(prev => prev.map(p => p.id === voce.prima.id ? voce.prima : p));
      if (dbReady) savePagamentoToDB(voce.prima);
    }
    if (voce.tipo === 'stipendio') {
      setStipendi(prev => ({ ...prev, [voce.mese]: { ...(prev[voce.mese]||{}), [voce.m]: voce.prima } }));
      if (dbReady) saveStipendioToDB(voce.m, voce.prima, voce.mese);
    }
    // Retta del mese: pagata, iscrizione, bonifico, importo e sconto tornano tutti com'erano
    if (voce.tipo === 'retta') {
      const p = voce.prima;
      setPagamentiMensili(prev => ({ ...prev, [voce.mese]: { ...prev[voce.mese], [voce.id]: p } }));
      if (dbReady) supabase.from('rette_mensili').upsert(
        { allievo_id: voce.id, mese: voce.mese, retta_pagata: !!p.retta, iscr_pagata: !!p.iscr,
          mdi_pagato: !!(pagamentiMDI[voce.mese]?.[voce.id]), mdi_ore: getOreMDI(voce.id, voce.mese),
          retta_importo: p.importo ?? null, sconto_mese: p.sconto ?? null,
          retta_pagata_at: p.rettaAt ?? null, iscr_pagata_at: p.iscrAt ?? null, bonifico: !!p.bonifico },
        { onConflict: 'allievo_id,mese' }
      ).then(seErrore);
    }
    setNotif({msg:"Azione annullata",t:"undo"});
    setTimeout(()=>setNotif(null),2000);
  };
  undoRef.current = undo;

  // Aggiorna il maestro associato a una singola lezione nel calendario allievo
  const updateLezioneMAestro = (allId, day, mese, maestro) => {
    setAllievi(prev => {
      const updated = prev.map(a => {
        if (a.id !== allId) return a;
        const lm = JSON.parse(JSON.stringify(a.lezioniMaestro || {}));
        if (!lm[mese]) lm[mese] = {};
        if (maestro) lm[mese][String(day)] = maestro;
        else delete lm[mese][String(day)];
        return { ...a, lezioniMaestro: lm };
      });
      const al = updated.find(x => x.id === allId);
      if (al && dbReady) saveAllievoToDB(al);
      return updated;
    });
  };

  // Restituisce true se un movimento cassa appartiene a un dato mese ("aprile-2026").
  // Usa mese_competenza se presente (stipendi), altrimenti cade su data reale (movimenti manuali).
  const movAppartieneMese = (mov, mese) => {
    if (mov.mese_competenza) return mov.mese_competenza === mese;
    const [n, a] = (mese || '').split('-');
    if (!n || !a || MESI_IDX[n] === undefined) return false;
    const tag = `${a}-${String(MESI_IDX[n]+1).padStart(2,'0')}`;
    return !!(mov.data && mov.data.startsWith(tag));
  };

  // ── PAGAMENTI MENSILI ────────────────────────────────────────────────────────
  const getRettaPagata  = (id, mese = meseAllievi) => !!(pagamentiMensili[mese]?.[id]?.retta);
  const getIscrPagata   = (id, mese = meseAllievi) => !!(pagamentiMensili[mese]?.[id]?.iscr);
  const getBonificoMese = (id, mese = meseAllievi) => !!(pagamentiMensili[mese]?.[id]?.bonifico);

  // Importo mensile: override per quel mese se impostato, altrimenti retta base del profilo
  const getRettaImportoMese = (id, mese = meseAllievi) => {
    const imp = pagamentiMensili[mese]?.[id]?.importo;
    const a = allieviAll.find(x => x.id === id);
    if (!a) return 0;
    const base = a.retta || effectiveRetta(a);
    return (imp != null) ? imp : base;
  };

  // Salva l'importo mensile su rette_mensili per quel mese specifico
  const updateRettaImporto = async (id, importo, mese) => {
    const cur = pagamentiMensili[mese]?.[id] || {};
    saveHistory({ tipo:'retta', id, mese, prima: cur });
    setPagamentiMensili(prev => ({
      ...prev,
      [mese]: { ...prev[mese], [id]: { ...cur, importo } },
    }));
    if (dbReady) {
      const curMdi = !!(pagamentiMDI[mese]?.[id]);
      const curOre = getOreMDI(id, mese);
      await supabase.from('rette_mensili').upsert(
        { allievo_id: id, mese, retta_pagata: !!cur.retta, iscr_pagata: !!cur.iscr,
          mdi_pagato: curMdi, mdi_ore: curOre, retta_importo: importo,
          retta_pagata_at: cur.rettaAt ?? null, iscr_pagata_at: cur.iscrAt ?? null },
        { onConflict: 'allievo_id,mese' }
      );
    }
  };

  // Sconto mensile: override per quel mese se impostato, altrimenti sconto base del profilo
  const getScontoMese = (id, mese = meseAllievi) => {
    const s = pagamentiMensili[mese]?.[id]?.sconto;
    if (s != null) return s;
    const a = allieviAll.find(x => x.id === id);
    return a?.sconto || 0;
  };

  const updateScontoMese = async (id, sconto, mese) => {
    const cur = pagamentiMensili[mese]?.[id] || {};
    saveHistory({ tipo:'retta', id, mese, prima: cur });
    setPagamentiMensili(prev => ({
      ...prev,
      [mese]: { ...prev[mese], [id]: { ...cur, sconto } },
    }));
    if (dbReady) {
      const curMdi = !!(pagamentiMDI[mese]?.[id]);
      const curOre = getOreMDI(id, mese);
      await supabase.from('rette_mensili').upsert(
        { allievo_id: id, mese, retta_pagata: !!cur.retta, iscr_pagata: !!cur.iscr,
          mdi_pagato: curMdi, mdi_ore: curOre, retta_importo: cur.importo ?? null,
          sconto_mese: sconto, retta_pagata_at: cur.rettaAt ?? null, iscr_pagata_at: cur.iscrAt ?? null },
        { onConflict: 'allievo_id,mese' }
      );
    }
  };

  // Helper interno: aggiunge o rimuove un movimento cassa
  const _cassaToggle = async (on, importo, desc, meseKey) => {
    const [nomeMese, anno] = meseKey.split('-');
    const meseTag = `${anno}-${String(MESI_IDX[nomeMese]+1).padStart(2,'0')}`;
    if (on && importo > 0) {
      const _d = new Date();
      const oggi = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
      const { data } = await supabase.from('cassa_movimenti').insert({importo, descrizione:desc, data:oggi}).select().single();
      if (data) setMovimentiCassa(prev => [data, ...prev]);
    } else if (!on) {
      const mov = movimentiCassa.find(m => m.descrizione === desc && m.data?.startsWith(meseTag));
      if (mov) {
        await supabase.from('cassa_movimenti').delete().eq('id', mov.id);
        setMovimentiCassa(prev => prev.filter(m => m.id !== mov.id));
      }
    }
  };

  const toggleRettaMese = async (id, tipo, mese = meseAllievi) => {
    const cur = pagamentiMensili[mese]?.[id] || {};
    saveHistory({ tipo:'retta', id, mese, prima: cur });
    const newRetta = tipo === 'retta' ? !cur.retta : !!cur.retta;
    const newIscr  = tipo === 'iscr'  ? !cur.iscr  : !!cur.iscr;
    const now = new Date().toISOString();
    const newRettaAt = tipo === 'retta' ? (newRetta ? now : null) : (cur.rettaAt ?? null);
    const newIscrAt  = tipo === 'iscr'  ? (newIscr  ? now : null) : (cur.iscrAt  ?? null);
    setPagamentiMensili(prev => ({
      ...prev,
      [mese]: { ...prev[mese], [id]: { ...cur, retta: newRetta, iscr: newIscr, rettaAt: newRettaAt, iscrAt: newIscrAt } }
    }));
    if (dbReady) {
      const curMdi = !!(pagamentiMDI[mese]?.[id]);
      const curOre = getOreMDI(id, mese);
      const curBonif = getBonificoMese(id, mese);
      await supabase.from('rette_mensili').upsert(
        { allievo_id: id, mese, retta_pagata: newRetta, iscr_pagata: newIscr, mdi_pagato: curMdi, mdi_ore: curOre, retta_pagata_at: newRettaAt, iscr_pagata_at: newIscrAt, bonifico: curBonif },
        { onConflict: 'allievo_id,mese' }
      );
    }
  };

  const toggleBonifico = async (id, mese = meseAllievi) => {
    const cur = pagamentiMensili[mese]?.[id] || {};
    saveHistory({ tipo:'retta', id, mese, prima: cur });
    const newVal = !cur.bonifico;
    setPagamentiMensili(prev => ({
      ...prev,
      [mese]: { ...prev[mese], [id]: { ...cur, bonifico: newVal } }
    }));
    if (dbReady) {
      const curMdi = !!(pagamentiMDI[mese]?.[id]);
      const curOre = getOreMDI(id, mese);
      await supabase.from('rette_mensili').upsert(
        { allievo_id: id, mese, retta_pagata: !!cur.retta, iscr_pagata: !!cur.iscr, mdi_pagato: curMdi, mdi_ore: curOre, retta_pagata_at: cur.rettaAt ?? null, iscr_pagata_at: cur.iscrAt ?? null, bonifico: newVal },
        { onConflict: 'allievo_id,mese' }
      );
    }
  };

  const toggleTutteRette = async () => {
    const snapshotValid = preMarkSnapshot?.mese === meseAllievi;
    const now = new Date().toISOString();

    if (snapshotValid) {
      // ANNULLA: ripristina lo stato pre-bulk (solo gli allievi che erano già pagati restano pagati)
      const preIds = preMarkSnapshot.ids;
      setPagamentiMensili(prev => {
        const mesePrev = prev[meseAllievi] || {};
        const newMese = { ...mesePrev };
        allievi.forEach(a => {
          const cur = mesePrev[a.id] || {};
          const eraPagato = preIds.has(a.id);
          newMese[a.id] = { ...cur, retta: eraPagato, rettaAt: eraPagato ? (cur.rettaAt ?? null) : null };
        });
        return { ...prev, [meseAllievi]: newMese };
      });
      if (dbReady) {
        const rows = allievi.map(a => {
          const cur = pagamentiMensili[meseAllievi]?.[a.id] || {};
          const eraPagato = preIds.has(a.id);
          return {
            allievo_id: a.id, mese: meseAllievi,
            retta_pagata: eraPagato, iscr_pagata: !!cur.iscr,
            mdi_pagato: !!(pagamentiMDI[meseAllievi]?.[a.id]),
            mdi_ore: getOreMDI(a.id, meseAllievi),
            retta_pagata_at: eraPagato ? (cur.rettaAt ?? null) : null,
            iscr_pagata_at: cur.iscrAt ?? null,
          };
        });
        await supabase.from('rette_mensili').upsert(rows, { onConflict: 'allievo_id,mese' });
      }
      setPreMarkSnapshot(null);
    } else {
      // SEGNA TUTTE: salva snapshot di chi era già pagato, poi marca tutti
      const preIds = new Set(
        allievi.filter(a => !!(pagamentiMensili[meseAllievi]?.[a.id]?.retta)).map(a => a.id)
      );
      setPreMarkSnapshot({ mese: meseAllievi, ids: preIds });
      setPagamentiMensili(prev => {
        const mesePrev = prev[meseAllievi] || {};
        const newMese = { ...mesePrev };
        allievi.forEach(a => {
          const cur = mesePrev[a.id] || {};
          if (!cur.retta) newMese[a.id] = { ...cur, retta: true, rettaAt: now };
        });
        return { ...prev, [meseAllievi]: newMese };
      });
      if (dbReady) {
        const daPagare = allievi.filter(a => !preIds.has(a.id));
        if (daPagare.length > 0) {
          const rows = daPagare.map(a => {
            const cur = pagamentiMensili[meseAllievi]?.[a.id] || {};
            return {
              allievo_id: a.id, mese: meseAllievi,
              retta_pagata: true, iscr_pagata: !!cur.iscr,
              mdi_pagato: !!(pagamentiMDI[meseAllievi]?.[a.id]),
              mdi_ore: getOreMDI(a.id, meseAllievi),
              retta_pagata_at: now, iscr_pagata_at: cur.iscrAt ?? null,
            };
          });
          await supabase.from('rette_mensili').upsert(rows, { onConflict: 'allievo_id,mese' });
        }
      }
    }
  };

// ── GESTIONE CASSA ───────────────────────────────────────────────────────────
  const addMovimentoCassa = async (importo, descrizione) => {
    const _d = new Date();
    const oggi = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
    const row = { importo: Number(importo), descrizione: descrizione || '', data: oggi };
    const { data } = await supabase.from('cassa_movimenti').insert(row).select().single();
    if (data) setMovimentiCassa(prev => [data, ...prev]);
  };

  const deleteMovimentoCassa = async (id) => {
    await supabase.from('cassa_movimenti').delete().eq('id', id);
    setMovimentiCassa(prev => prev.filter(m => m.id !== id));
  };

  const updateMovimentoCassa = async (id, field, value) => {
    const patch = { [field]: field === 'importo' ? Number(value) : value };
    await supabase.from('cassa_movimenti').update(patch).eq('id', id);
    setMovimentiCassa(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
  };

  // Toggle stipendio pagato + aggiunge/rimuove movimento in cassa_movimenti
  // Non annullabile con Ctrl+Z: scrive anche un movimento di cassa; si annulla ricliccando
  const toggleStipendioConCassa = async (nomeMaestro, stipendio, mese) => {
    const nuovoPagato = !(stipendiPagati[mese]?.[nomeMaestro]);
    setStipendi(prev => ({ ...prev, [mese]: { ...(prev[mese]||{}), [nomeMaestro]: nuovoPagato } }));
    if (dbReady) saveStipendioToDB(nomeMaestro, nuovoPagato, mese);

    const [nomeMese, anno] = mese.split('-');
    const meseCap = nomeMese.charAt(0).toUpperCase() + nomeMese.slice(1);
    const desc = `Stipendio ${nomeMaestro} ${meseCap}`;
    const meseTagLocale = `${anno}-${String(MESI_IDX[nomeMese] + 1).padStart(2, '0')}`;

    if (nuovoPagato) {
      // Data reale del pagamento + mese_competenza per attribuirlo al mese selezionato
      const _t = new Date();
      const dataMovimento = `${_t.getFullYear()}-${String(_t.getMonth()+1).padStart(2,'0')}-${String(_t.getDate()).padStart(2,'0')}`;
      const row = { importo: -stipendio, descrizione: desc, data: dataMovimento, mese_competenza: mese };
      const { data } = await supabase.from('cassa_movimenti').insert(row).select().single();
      if (data) setMovimentiCassa(prev => [data, ...prev]);
    } else {
      // Rimuove il movimento corrispondente (controlla mese_competenza o data)
      const mov = movimentiCassa.find(m =>
        m.descrizione === desc && movAppartieneMese(m, mese)
      );
      if (mov) {
        await supabase.from('cassa_movimenti').delete().eq('id', mov.id);
        setMovimentiCassa(prev => prev.filter(m => m.id !== mov.id));
      }
    }
  };

  const GestioneCassa = () => {
    const [importoInput, setImportoInput] = useState('');
    const [descInput,    setDescInput]    = useState('');
    const [showAll,      setShowAll]      = useState(false);
    const [editingCell,  setEditingCell]  = useState(null); // {id, field}
    const [editVal,      setEditVal]      = useState('');
    const [searchMov,    setSearchMov]    = useState('');

    const startEdit = (m, field) => {
      setEditingCell({ id: m.id, field });
      setEditVal(field === 'importo' ? String(m.importo) : (m.descrizione || ''));
    };
    const commitEdit = (id, field) => {
      if (editVal.trim() !== '') updateMovimentoCassa(id, field, editVal.trim());
      setEditingCell(null);
    };

    // Saldo automatico da lezioni cash — usa rettaNetta (già al netto degli sconti, che non entrano in cassa)
    const cassaLezioni =
      allievi.filter(a=>getRettaPagata(a.id) && !getBonificoMese(a.id))
        .reduce((s,a)=>s+rettaNetta(a, meseAllievi), 0)
      + allievi.filter(a=>getIscrPagata(a.id) && a.iscrizione && !getBonificoMese(a.id))
        .reduce((s,a)=>s+(a.iscrizione||0), 0);
    // Movimenti filtrati per il mese selezionato (usa mese_competenza se presente)
    const meseTag = (() => { const [n,a] = meseAllievi.split('-'); return `${a}-${String(MESI_IDX[n]+1).padStart(2,'0')}`; })();
    const movMese = movimentiCassa.filter(m => movAppartieneMese(m, meseAllievi));
    const cassaExtra   = movMese.reduce((s,m)=>s+Number(m.importo), 0);
    const salaMese     = sala.filter(r => r.data && r.data.startsWith(meseTag))
      .reduce((s,r) => s + (Number(r.pagato)||0) + (Number(r.bibite)||0), 0);
    const saldo        = cassaLezioni + cassaExtra + salaMese;

    // Stats mese (entrate/uscite)
    const totEntrate = movMese.filter(m=>m.importo>0).reduce((s,m)=>s+Number(m.importo),0);
    const totUscite  = movMese.filter(m=>m.importo<0).reduce((s,m)=>s+Number(m.importo),0);

    const handleSubmit = () => {
      const n = parseFloat(importoInput.replace(',','.'));
      if (isNaN(n) || n === 0) return;
      addMovimentoCassa(n, descInput.trim());
      setImportoInput('');
      setDescInput('');
    };



    const fmtData = (d) => {
      if (!d) return '';
      const [y,m,g] = d.split('-');
      return `${g}/${m}/${y}`;
    };
    // Nelle righe sul telefono l'anno va corto (27/10/26) per lasciare spazio alla descrizione; l'Excel resta con fmtData
    const fmtDataRiga = (d) => isMobile && d ? fmtData(d).replace(/\/\d{2}(\d{2})$/, '/$1') : fmtData(d);

    const fmtOra = (isoStr) => {
      if (!isoStr) return '';
      const d = new Date(isoStr);
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };

    // Righe virtuali readonly: rette e iscrizioni pagate nel mese (già conteggiate nel saldo via cassaLezioni)
    const retteVirtuali = allievi
      .filter(a => getRettaPagata(a.id))
      .map(a => {
        const pm = pagamentiMensili[meseAllievi]?.[a.id] || {};
        return { _v: true, _tipo: 'v', importo: rettaNetta(a, meseAllievi), descrizione: `Retta — ${a.nome}`, at: pm.rettaAt ?? null };
      });
    const iscrVirtuali = allievi
      .filter(a => getIscrPagata(a.id) && (a.iscrizione||0) > 0)
      .map(a => {
        const pm = pagamentiMensili[meseAllievi]?.[a.id] || {};
        return { _v: true, _tipo: 'v', importo: a.iscrizione, descrizione: `Iscrizione — ${a.nome}`, at: pm.iscrAt ?? null };
      });
    const salaVirtuali = sala
      .filter(r => r.data && r.data.startsWith(meseTag) && (Number(r.pagato)||0) + (Number(r.bibite)||0) > 0)
      .map(r => ({
        _v: true, _tipo: 's',
        importo: (Number(r.pagato)||0) + (Number(r.bibite)||0),
        descrizione: `Sala prove — ${r.nome || '—'}`,
        parziali: { pagato: Number(r.pagato)||0, bibite: Number(r.bibite)||0 },
        _ts: r.data,
      }));
    const virtuali = [...retteVirtuali, ...iscrVirtuali];

    return (
      <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,
        padding:isMobile?"16px":"22px 24px",marginBottom:isMobile?14:24}}>

        {/* Header + Saldo */}
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",
          flexWrap:"wrap",gap:12,marginBottom:isMobile?14:20}}>
          <div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,
              color:P.secondary,marginBottom:4,textTransform:"uppercase"}}>SALDO CASSA</div>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?30:38,fontWeight:500,
                color:saldo>=0?P.green:P.red,lineHeight:1}}>
                {saldo>=0?'+':''}{saldo}€
              </div>
              <InfoTip lines={[
                {label:"Rette pagate (cash)", value:`€${cassaLezioni}`},
                {label:"Movimenti manuali", value:`${cassaExtra>=0?'+':''}€${cassaExtra}`},
                {label:"Sala prove", value:`€${salaMese}`},
                {label:"Saldo", value:`${saldo>=0?'+':''}€${saldo}`, highlight:true, sep:true},
              ]}/>
            </div>
            <div style={{fontSize:13,color:P.secondary,marginTop:6}}>
              {cassaLezioni}€ da lezioni cash &nbsp;·&nbsp;
              <span style={{color:cassaExtra>=0?P.green:P.red}}>
                {cassaExtra>=0?'+':''}{cassaExtra}€
              </span> movimenti manuali
            </div>
          </div>

          {/* Stats mese */}
          <div style={{display:"flex",gap:16,alignItems:"center"}}>
            {[
              {l:`${movMese.length} mov.`, c:P.secondary},
              {l:`+${totEntrate}€`, c:P.green},
              {l:`${totUscite}€`, c:P.red},
            ].map(s=>(
              <div key={s.l} style={{textAlign:"center"}}>
                <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:16,fontWeight:500,color:s.c}}>{s.l}</div>
                <div style={{fontSize:11,color:P.secondary,marginTop:2,letterSpacing:1}}>
                  {s===0?'MOVIMENTI':s.l.includes('mov')?'QUESTO MESE':s.l.includes('+')?'ENTRATE EXTRA':'USCITE'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form aggiunta movimento */}
        <div style={{display:"flex",gap:8,marginBottom:isMobile?14:20,flexWrap:"wrap"}}>
          <input
            type="number" placeholder="es. -50 o +100"
            value={importoInput}
            onChange={e=>setImportoInput(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handleSubmit()}
            style={{width:130,padding:"8px 12px",borderRadius:8,
              border:`1px solid ${P.border}`,background:P.bg,color:P.text,
              fontFamily:"'IBM Plex Mono',monospace",fontSize:16,outline:"none"}}
          />
          <input
            type="text" placeholder="Descrizione (es. prelievo)"
            value={descInput}
            onChange={e=>setDescInput(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handleSubmit()}
            style={{flex:1,minWidth:160,padding:"8px 12px",borderRadius:8,
              border:`1px solid ${P.border}`,background:P.bg,color:P.text,
              fontFamily:"'Figtree',sans-serif",fontSize:15,outline:"none"}}
          />
          <button onClick={handleSubmit}
            style={{padding:"8px 20px",borderRadius:8,border:"none",
              background:P.gold,color:"#fff",fontWeight:700,fontSize:15,
              fontFamily:"'Figtree',sans-serif",cursor:"pointer"}}>
            Registra
          </button>
          <button onClick={() => {
            // Righe: prima rette/iscrizioni, poi sala prove, poi movimenti reali
            const rows = [
              ...virtuali.map(m => ({
                Data:        m.at ? m.at.slice(0,10).split('-').reverse().join('/') : '',
                Ora:         m.at ? fmtOra(m.at) : '',
                Descrizione: m.descrizione,
                Importo:     m.importo,
                Tipo:        'Retta/Iscrizione',
              })),
              ...salaVirtuali.map(m => ({
                Data:        fmtData(m._ts),
                Ora:         '',
                Descrizione: m.descrizione,
                Importo:     m.importo,
                Tipo:        'Sala Prove',
              })),
              ...movMese.map(m => ({
                Data:        fmtData(m.data),
                Ora:         fmtOra(m.created_at),
                Descrizione: m.descrizione || '',
                Importo:     Number(m.importo),
                Tipo:        Number(m.importo) >= 0 ? 'Entrata' : 'Uscita',
              })),
            ];
            // Riga totale
            const totale = virtuali.reduce((s,m)=>s+m.importo,0) + salaVirtuali.reduce((s,m)=>s+m.importo,0) + movMese.reduce((s,m)=>s+Number(m.importo),0);
            rows.push({ Data:'', Ora:'', Descrizione:'TOTALE', Importo: totale, Tipo:'' });

            const ws = XLSX.utils.json_to_sheet(rows, { header:['Data','Ora','Descrizione','Importo','Tipo'] });
            ws['!cols'] = [{wch:12},{wch:8},{wch:36},{wch:12},{wch:16}];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, meseAllievi);

            // Foglio 2: allievi che devono ancora pagare
            const daPagareRows = allievi
              .filter(a => !getRettaPagata(a.id) && rettaNetta(a, meseAllievi) > 0)
              .sort((a,b) => (a.nome||'').localeCompare(b.nome||'','it'))
              .map(a => ({
                Nominativo: a.nome,
                'Da pagare (€)': getRettaImportoMese(a.id, meseAllievi),
                'Metodo pagamento': getBonificoMese(a.id, meseAllievi) ? 'bonifico' : 'cash',
              }));
            const totDaPagare = daPagareRows.reduce((s,r) => s + r['Da pagare (€)'], 0);
            daPagareRows.push({ Nominativo: 'TOTALE', 'Da pagare (€)': totDaPagare, 'Metodo pagamento': '' });
            const ws2 = XLSX.utils.json_to_sheet(daPagareRows, { header:['Nominativo','Da pagare (€)','Metodo pagamento'] });
            ws2['!cols'] = [{wch:28},{wch:16},{wch:20}];
            XLSX.utils.book_append_sheet(wb, ws2, 'Da incassare');

            XLSX.writeFile(wb, `Cassa_${meseAllievi}.xlsx`);
          }}
            style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${P.border}`,
              background:"transparent",color:P.secondary,fontSize:15,
              fontFamily:"'Figtree',sans-serif",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            ↓ Excel
          </button>
        </div>

        {/* Lista movimenti */}
        {(movimentiCassa.length > 0 || movMese.length > 0 || virtuali.length > 0 || salaVirtuali.length > 0) && (() => {
          const q = searchMov.trim().toLowerCase();
          let tuttiRighe;
          if (q) {
            // Movimenti reali (tutti i mesi)
            const realMovs = movimentiCassa
              .filter(m => (m.descrizione||'').toLowerCase().includes(q))
              .map(m => ({ ...m, _tipo: 'r', _ts: m.created_at || m.data || '' }));
            // Rette e iscrizioni virtuali (tutti i mesi)
            const retteAll = [];
            MESI_TIMELINE.forEach(mese => {
              const [n] = mese.split('-');
              const meseCap = n.charAt(0).toUpperCase() + n.slice(1);
              allievi.forEach(a => {
                const pm = pagamentiMensili[mese]?.[a.id] || {};
                if (pm.retta) {
                  const desc = `Retta — ${a.nome}`;
                  if (desc.toLowerCase().includes(q))
                    retteAll.push({ _v:true, _tipo:'v', importo: rettaNetta(a, mese), descrizione: `${desc} (${meseCap})`, at: pm.rettaAt ?? null, _ts: pm.rettaAt || '' });
                }
                if (pm.iscr && (a.iscrizione||0) > 0) {
                  const desc = `Iscrizione — ${a.nome}`;
                  if (desc.toLowerCase().includes(q))
                    retteAll.push({ _v:true, _tipo:'v', importo: a.iscrizione, descrizione: `${desc} (${meseCap})`, at: pm.iscrAt ?? null, _ts: pm.iscrAt || '' });
                }
              });
            });
            // Sala prove (tutte le date)
            const salaAll = sala
              .filter(r => (Number(r.pagato)||0)+(Number(r.bibite)||0) > 0 &&
                `Sala prove — ${r.nome||'—'}`.toLowerCase().includes(q))
              .map(r => ({ _v:true, _tipo:'s',
                importo: (Number(r.pagato)||0)+(Number(r.bibite)||0),
                descrizione: `Sala prove — ${r.nome||'—'}`,
                parziali: { pagato: Number(r.pagato)||0, bibite: Number(r.bibite)||0 },
                _ts: r.data || '' }));
            tuttiRighe = [...realMovs, ...retteAll, ...salaAll]
              .sort((a, b) => b._ts.localeCompare(a._ts));
          } else {
            tuttiRighe = [
              ...virtuali.map(m => ({ ...m, _ts: m.at || '' })),
              ...salaVirtuali,
              ...movMese.map(m => ({ ...m, _tipo: 'r', _ts: m.created_at || m.data || '' })),
            ].sort((a, b) => b._ts.localeCompare(a._ts));
          }
          const totale = tuttiRighe.length;
          const visibili = (q || showAll) ? tuttiRighe : tuttiRighe.slice(0, 3);
          return (
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,gap:8,flexWrap:"wrap"}}>
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,
                color:P.secondary,textTransform:"uppercase"}}>
                CRONOLOGIA MOVIMENTI
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <input
                  type="text" placeholder="Cerca movimento…"
                  value={searchMov}
                  onChange={e=>setSearchMov(e.target.value)}
                  style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${searchMov?P.gold:P.border}`,
                    background:P.bg,color:P.text,fontSize:14,
                    fontFamily:"'Figtree',sans-serif",outline:"none",width:170,
                    boxShadow: searchMov ? `0 0 0 2px ${P.goldGlow}` : 'none'}}
                />
                {searchMov && (
                  <button onClick={()=>setSearchMov('')}
                    style={{background:"none",border:"none",cursor:"pointer",color:P.secondary,fontSize:15,padding:"2px 4px",lineHeight:1}}
                    title="Cancella ricerca">✕</button>
                )}
              </div>
            </div>

            {visibili.map((m, i) => {
              if (m._tipo === 'v') {
                const isSconto = m.importo < 0;
                const col = isSconto ? P.red : P.green;
                return (
                <div key={`v-${i}`} style={{display:"flex",alignItems:"center",gap:10,
                  padding:isMobile?"7px 10px":"8px 12px",borderRadius:8,marginBottom:4,
                  background:`${col}08`,border:`1px solid ${col}25`}}>
                  <div style={{flexShrink:0,width:isMobile?62:90}}>
                    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?12:13,color:P.secondary}}>
                      {m.at ? fmtDataRiga(m.at.slice(0,10)) : '—'}
                    </div>
                    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?11:12,color:P.muted,marginTop:1}}>
                      {m.at ? fmtOra(m.at) : ''}
                    </div>
                  </div>
                  <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?15:16,fontWeight:600,
                    color:col,flexShrink:0,width:isMobile?66:80}}>
                    {isSconto ? `-${Math.abs(m.importo)}€` : `+${m.importo}€`}
                  </span>
                  <span style={{fontSize:isMobile?14:15,flex:1,color:isSconto?P.red:P.secondary}}>{m.descrizione}</span>
                </div>
                );
              }
              if (m._tipo === 's') {
                const { pagato, bibite } = m.parziali;
                const dettaglio = bibite > 0
                  ? `+€${pagato} (sala) +€${bibite} (bibite) = +€${m.importo}`
                  : `+€${pagato} (sala)`;
                return (
                <div key={`s-${i}`} style={{display:"flex",alignItems:"center",gap:10,
                  padding:isMobile?"7px 10px":"8px 12px",borderRadius:8,marginBottom:4,
                  background:`${P.purple}08`,border:`1px solid ${P.purple}25`}}>
                  <div style={{flexShrink:0,width:isMobile?62:90}}>
                    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?12:13,color:P.secondary}}>
                      {fmtDataRiga(m._ts)}
                    </div>
                  </div>
                  <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?15:16,fontWeight:600,
                    color:P.purple,flexShrink:0,width:isMobile?66:80}}>
                    +{m.importo}€
                  </span>
                  <span style={{fontSize:isMobile?14:15,flex:1,color:P.secondary}}>
                    {m.descrizione} · <span style={{color:P.purple}}>{dettaglio}</span>
                  </span>
                </div>
                );
              }
              const isEditImp = editingCell?.id===m.id && editingCell?.field==='importo';
              const isEditDesc = editingCell?.id===m.id && editingCell?.field==='descrizione';
              const impColor = Number(m.importo)>=0?P.green:P.red;
              return (
              <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,
                padding:isMobile?"7px 10px":"8px 12px",borderRadius:8,marginBottom:4,
                background:P.bg,border:`1px solid ${P.borderSubtle}`}}>
                <div style={{flexShrink:0,width:isMobile?62:90}}>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?12:13,color:P.secondary}}>{fmtDataRiga(m.data)}</div>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?11:12,color:P.muted,marginTop:1}}>{fmtOra(m.created_at)}</div>
                </div>
                {isEditImp ? (
                  <input autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)}
                    onBlur={()=>commitEdit(m.id,'importo')}
                    onKeyDown={e=>{if(e.key==='Enter')commitEdit(m.id,'importo');if(e.key==='Escape')setEditingCell(null);}}
                    style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?15:16,fontWeight:600,
                      color:impColor,width:isMobile?66:80,background:'transparent',border:`1px solid ${impColor}`,
                      borderRadius:4,padding:'2px 4px',outline:'none',flexShrink:0}}/>
                ) : (
                  <span onClick={()=>startEdit(m,'importo')}
                    title="Clicca per modificare"
                    style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?15:16,fontWeight:600,
                      color:impColor,flexShrink:0,width:isMobile?66:80,cursor:'text',
                      borderBottom:`1px dashed ${impColor}44`}}>
                    {Number(m.importo)>=0?'+':''}{m.importo}€
                  </span>
                )}
                {isEditDesc ? (
                  <input autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)}
                    onBlur={()=>commitEdit(m.id,'descrizione')}
                    onKeyDown={e=>{if(e.key==='Enter')commitEdit(m.id,'descrizione');if(e.key==='Escape')setEditingCell(null);}}
                    style={{fontSize:15,flex:1,background:'transparent',border:`1px solid ${P.border}`,
                      borderRadius:4,padding:'2px 6px',color:P.text,outline:'none'}}/>
                ) : (
                  <span onClick={()=>startEdit(m,'descrizione')}
                    title="Clicca per modificare"
                    style={{fontSize:isMobile?14:15,flex:1,cursor:'text',
                      fontStyle:m.descrizione?'normal':'italic',
                      color:m.descrizione?P.text:P.muted,
                      borderBottom:`1px dashed ${P.border}`}}>
                    {m.descrizione||'—'}
                  </span>
                )}
                <button onClick={()=>deleteMovimentoCassa(m.id)}
                  title="Elimina"
                  style={{background:"none",border:"none",cursor:"pointer",
                    color:P.secondary,fontSize:16,padding:"2px 6px",borderRadius:4,lineHeight:1}}
                  onMouseEnter={e=>e.currentTarget.style.color=P.red}
                  onMouseLeave={e=>e.currentTarget.style.color=P.muted}>
                  ✕
                </button>
              </div>
              );
            })}

            {!q && totale > 3 && (
              <button onClick={()=>setShowAll(v=>!v)}
                style={{background:"none",border:`1px solid ${P.border}`,color:P.secondary,
                  padding:"6px 16px",borderRadius:8,cursor:"pointer",fontSize:14,
                  fontFamily:"'IBM Plex Mono',monospace",marginTop:6,width:"100%"}}>
                {showAll ? '▲ Mostra meno' : `▼ Tutti i ${totale} movimenti`}
              </button>
            )}
            {q && totale === 0 && (
              <div style={{textAlign:"center",padding:"14px 0",color:P.muted,fontSize:14,
                fontFamily:"'IBM Plex Mono',monospace"}}>
                Nessun risultato per "{searchMov}"
              </div>
            )}
          </div>
          );
        })()}
      </div>
    );
  };

  const Allievi = () => {

    const filtered = allievi.filter(a =>
      a.nome.toLowerCase().includes(search.toLowerCase()) &&
      (filtroM==="TUTTI"||getAllievoCorsi(a).some(c=>c.maestro===filtroM))
    );

    // Totali da checkbox allievi (mese corrente)
    const totRettePagate = allievi.filter(a=>getRettaPagata(a.id)).reduce((s,a)=>s+rettaNetta(a, meseAllievi),0);
    const totIscrPagate  = allievi.filter(a=>getIscrPagata(a.id) && a.iscrizione).reduce((s,a)=>s+(a.iscrizione||0),0);
    const nRette = allievi.filter(a=>getRettaPagata(a.id)).length;
    const nIscr  = allievi.filter(a=>getIscrPagata(a.id) && a.iscrizione).length;

    const NavMeseAllievi = () => (
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <button onClick={()=>setMeseAllievi(meseAdd(meseAllievi,-1))}
          style={{background:"transparent",border:`1px solid ${P.gold}`,
            color:P.gold,borderRadius:8,width:32,height:32,
            cursor:"pointer",fontSize:17,display:"flex",
            alignItems:"center",justifyContent:"center"}}>‹</button>
        <span style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?15:16,fontWeight:700,
          minWidth:120,textAlign:"center",color:P.text}}>
          {mesiInfo(meseAllievi).label}
        </span>
        <button onClick={()=>setMeseAllievi(meseAdd(meseAllievi,1))}
          style={{background:"transparent",border:`1px solid ${P.gold}`,
            color:P.gold,borderRadius:8,width:32,height:32,
            cursor:"pointer",fontSize:17,display:"flex",
            alignItems:"center",justifyContent:"center"}}>›</button>
      </div>
    );
    // Totali da stati pagamenti
    const totAttesa  = pagamenti.filter(p=>p.stato==="attesa").reduce((s,p)=>s+p.retta+(p.iscrizione||0),0);
    const totRitardo = pagamenti.filter(p=>p.stato==="ritardo").reduce((s,p)=>s+p.retta+(p.iscrizione||0),0);

    return (
      <div style={{animation:"fi .35s ease"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:26,flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.secondary,marginBottom:7,fontWeight:400,textTransform:"uppercase"}}>GESTIONE</div>
            <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5}}>Pagamenti</h1>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <NavMeseAllievi/>
            <button onClick={()=>setModal("allievo")} style={{background:P.gold,color:"#fff",border:"none",
              padding:"10px 20px",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:15,fontFamily:"'Figtree',sans-serif"}}>
              + Aggiungi
            </button>
          </div>
        </div>

        {/* Pannello riepilogo pagamenti */}
        <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:14,marginBottom:22}}>
          {[
            {l:"Rette Incassate",  v:`€${totRettePagate}`, sub:`${nRette} allievi`,        c:P.green},
            {l:"Iscrizioni",       v:`€${totIscrPagate}`,  sub:`${nIscr} allievi`,          c:P.yellow},
            {l:"In Attesa",        v:`€${totAttesa}`,       sub:"da confermare",             c:P.blue},
            {l:"In Ritardo",       v:`€${totRitardo}`,      sub:"da sollecitare",            c:P.red},
          ].map(s=>(
            <div key={s.l} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:12,padding:"16px 20px",
              borderLeft:`3px solid ${s.c}`}}>
              <div style={{fontSize:12,color:P.secondary,marginBottom:4,letterSpacing:1}}>{s.l.toUpperCase()}</div>
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:24,fontWeight:500,color:s.c}}>{s.v}</div>
              <div style={{fontSize:13,color:P.secondary,marginTop:3}}>{s.sub}</div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
          <input placeholder="Cerca allievo..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,
              padding:"9px 15px",borderRadius:8,width:250,fontSize:15,fontFamily:"'Syne',sans-serif"}}/>
          <span style={{marginLeft:"auto",fontSize:14,color:P.secondary}}>{filtered.length} allievi</span>
        </div>
        <div style={{display:"flex",gap:7,marginBottom:16,flexWrap:"wrap"}}>
          {["TUTTI",...mkeysVisibili()].map(m=>{
            const c=maestriColors[m]||P.gold; const act=filtroM===m;
            return <button key={m} onClick={()=>setFiltroM(m)} style={{
              padding:"4px 12px",borderRadius:20,border:`1px solid ${act?c:P.border}`,
              background:act?`${c}18`:"transparent",color:act?c:P.muted,
              cursor:"pointer",fontSize:13,fontWeight:act?600:400,fontFamily:"'Syne',sans-serif"}}>
              {m}
            </button>;
          })}
        </div>

      </div>
    );
  };

  // ── PRESENZE ────────────────────────────────────────────────────────────────
  const Presenze = () => {
    const [addMaestroForm, setAddMaestroForm] = useState({maestro:'', retta:''});
    const [cambioMaestro, setCambioMaestro] = useState(null); // {id, ci, da, a} in attesa di conferma
    const [presenzeView, setPresenzeView] = useState('settimana');
    const [presenzeDay, setPresenzeDay] = useState(new Date());
    const [telInput, setTelInput] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [dataNascitaInput, setDataNascitaInput] = useState('');
    useEffect(() => {
      const a = allieviAll.find(x => x.id === presAllievo);
      setTelInput(a?.telefono || '');
      setEmailInput(a?.email || '');
      setDataNascitaInput(a?.dataNascita || '');
    }, [presAllievo]);
    // Il riquadro della griglia arriva fino al fondo dello schermo: l'elenco scorre al suo
    // interno e la riga dei giorni resta sempre visibile (sticky) mentre si scorre.
    const grigliaRef = useRef(null);
    const [grigliaTop, setGrigliaTop] = useState(0);
    useEffect(() => {
      const misura = () => {
        if (grigliaRef.current) setGrigliaTop(Math.round(grigliaRef.current.getBoundingClientRect().top + window.scrollY));
      };
      misura();
      window.addEventListener('resize', misura);
      return () => window.removeEventListener('resize', misura);
    });
    const grigliaMaxH = `max(320px, calc(100dvh - ${grigliaTop}px - ${isMobile ? '64px - env(safe-area-inset-bottom)' : '24px'}))`;
    const thFisso = { position:"sticky", top:0, background:P.card, zIndex:2, boxShadow:`inset 0 -1px 0 ${P.border}` };
    // Su schermo grande, scelto un maestro la griglia dei maestri si richiude
    const [scegliMaestro, setScegliMaestro] = useState(false);
    // Griglia ingrandita (pulsante Aa), ricordata su questo dispositivo
    const [grande, setGrande] = useState(() => { try { return localStorage.getItem('na_presenze_grande') === '1'; } catch { return false; } });
    const toggleGrande = () => setGrande(v => {
      try { localStorage.setItem('na_presenze_grande', v ? '0' : '1'); } catch {}
      return !v;
    });
    const zs = (n) => Math.round(n * (grande ? 1.3 : 1) * (isMobile ? 0.85 : 1));
    // Vista giorno: data per esteso col giorno della settimana ("Venerdì 23 ottobre 2026")
    const giornoEOggi = presenzeDay.toDateString() === new Date().toDateString();
    const _dataLunga = presenzeDay.toLocaleDateString('it-IT', {weekday:'long', day:'numeric', month:'long', year:'numeric'});
    const dataGiornoLunga = _dataLunga.charAt(0).toUpperCase() + _dataLunga.slice(1);
    const pulsanteAa = (
      <button onClick={toggleGrande} title={grande ? "Griglia normale" : "Ingrandisci griglia"}
        style={{padding:isMobile?"10px 14px":"5px 12px",borderRadius:isMobile?10:20,cursor:"pointer",
          fontFamily:"'Syne',sans-serif",fontWeight:700,whiteSpace:"nowrap",lineHeight:1,
          border:`1px solid ${grande?P.gold:P.border}`,background:grande?`${P.gold}18`:"transparent",
          color:grande?P.gold:P.secondary}}>
        <span style={{fontSize:14}}>A</span><span style={{fontSize:17}}>A</span>
      </button>
    );
    const getWeekDays = (d) => {
      const start = new Date(d);
      const day = start.getDay();
      start.setDate(start.getDate() - (day===0?6:day-1));
      return Array.from({length:7}, (_,i) => { const x=new Date(start); x.setDate(start.getDate()+i); return x; });
    };
    const { primo, days, label } = mesiInfo(meseAllievi);
    const _oggiD = new Date();
    const _oggiTag = NOMI_MESI_IT[_oggiD.getMonth()] + '-' + _oggiD.getFullYear();
    const isOggi = (d) => meseAllievi === _oggiTag && d === _oggiD.getDate();
    const mesePz = presenze[meseAllievi] || {};
    const lista = allievi
      .filter(a=>filtroMP==="TUTTI"||getAllievoCorsi(a).some(c=>c.maestro===filtroMP))
      .sort((a,b)=>(a.nome||'').localeCompare(b.nome||'','it'));
    const daysArr = Array.from({length:days},(_,i)=>i+1);

    const goMese = (delta) => {
      const nm = meseAdd(meseAllievi, delta);
      setMeseAllievi(nm);
      const [n,a] = nm.split('-');
      setPresenzeDay(new Date(parseInt(a),MESI_IDX[n],1));
    };
    const NavMese = ({compact=false}) => (
      <div style={{display:"flex",alignItems:"center",gap:compact?8:12}}>
        <button onClick={()=>goMese(-1)}
          style={{background:"transparent",border:`1px solid ${P.gold}`,
            color:P.gold,borderRadius:8,width:32,height:32,
            cursor:"pointer",fontSize:17,display:"flex",
            alignItems:"center",justifyContent:"center"}}>‹</button>
        <span style={{fontFamily:"'Syne',sans-serif",fontSize:compact?14:isMobile?20:26,
          fontWeight:700,minWidth:compact?100:140,textAlign:"center"}}>{label}</span>
        <button onClick={()=>goMese(1)}
          style={{background:"transparent",border:`1px solid ${P.gold}`,
            color:P.gold,borderRadius:8,width:32,height:32,
            cursor:"pointer",fontSize:17,display:"flex",
            alignItems:"center",justifyContent:"center"}}>›</button>
      </div>
    );

    if(presAllievo) {
      const a = allieviAll.find(x=>x.id===presAllievo);
      if(!a) return null;
      const c=mc(a.maestro,maestriColors);
      // Su mobile lo spazio è poco: i nomi lunghi rimpiccioliscono invece di essere tagliati
      const nLen = (a.nome||'').length;
      const fontNome = isMobile ? (nLen > 26 ? 15 : nLen > 19 ? 17 : 19) : 22;
      const pz=mesePz[a.id]||{};
      const totP=Object.values(pz).filter(v=>v==="45p"||v==="1p"||v==="2p"||v==="3p"||v==="p").length;
      const totA=Object.values(pz).filter(v=>v==="a").length;
      const cells=[]; for(let i=0;i<primo;i++) cells.push(null);
      for(let d=1;d<=days;d++) cells.push(d);
      while(cells.length%7!==0) cells.push(null);
      const weeks=[]; for(let i=0;i<cells.length;i+=7) weeks.push(cells.slice(i,i+7));
      const corsiAllievo = getAllievoCorsi(a);
      const isMultiMaestro = corsiAllievo.length > 1;
      const lezMese = (a.lezioniMaestro || {})[meseAllievi] || {};
      const calcLezioni = (retta) => {
        if(retta<=0) return 0;
        if(retta>=120) return 4;
        if(retta>=90) return 3;
        if(retta>=60) return 2;
        return 1;
      };
      const nLezioni = calcLezioni(getRettaImportoMese(a.id, meseAllievi));
      const oggi = new Date();
      const oggiStr = `${String(oggi.getDate()).padStart(2,'0')}/${String(oggi.getMonth()+1).padStart(2,'0')}/${String(oggi.getFullYear()).slice(-2)}`;
      const waMsg = encodeURIComponent(`Salve, oggi ${oggiStr} ha prenotato le ${nLezioni} lezioni del mese di ${label} per il corso di:`);
      const cycleMaestro = (e, d) => {
        e.stopPropagation();
        const cur = lezMese[String(d)];
        const idx = corsiAllievo.findIndex(cc => cc.maestro === cur);
        const next = corsiAllievo[(idx + 1) % corsiAllievo.length].maestro;
        updateLezioneMAestro(a.id, d, meseAllievi, next);
      };
      return (
        <div style={{animation:"fi .35s ease"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:8}}>
            <button onClick={()=>{setPresAllievo(null);setTab(presAllievoFrom);}} style={{background:"transparent",border:`1px solid ${P.border}`,
              color:P.muted,padding:"7px 14px",borderRadius:8,cursor:"pointer",fontSize:14}}>
              ← Torna indietro
            </button>
            <NavMese compact/>
            <button onClick={()=>setConfirmDelete(a)} style={{background:`${P.red}15`,border:`1px solid ${P.red}40`,
              color:P.red,padding:"7px 16px",borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:600}}>
              Archivia allievo
            </button>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24,flexWrap:"wrap"}}>
            <Avatar nome={a.nome} color={c} size={48}/>
            {/* flex:1 + minWidth:0 — senza questo i nomi lunghi vengono tagliati su mobile */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <EditableField value={a.nome||''} onChange={v=>updateAllievo(a.id,"nome",v)} color={c}
                  inputStyle={{fontFamily:"'Syne',sans-serif",fontSize:fontNome,fontWeight:700,
                    width:"100%",maxWidth:"100%",boxSizing:"border-box",minWidth:0}}/>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:2,marginBottom:8}}>
                {getAllievoCorsi(a).map((corso,ci)=>{
                  const cc=mc(corso.maestro,maestriColors);
                  return <span key={ci} style={{fontSize:13,color:cc,fontWeight:600}}>{corso.maestro}</span>;
                })}
              </div>
              {/* Metodo pagamento — per mese (usa meseAllievi, condiviso con Dashboard) */}
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {(() => {
                  const isBonif = getBonificoMese(a.id, meseAllievi);
                  const label = isBonif?"🏦 Bonifico":"💵 Contanti";
                  const col = isBonif?P.blue:P.green;
                  return (
                    <button onClick={()=>toggleBonifico(a.id, meseAllievi)}
                      title={isBonif?"Clicca per Contanti":"Clicca per Bonifico"}
                      style={{padding:"4px 12px",borderRadius:20,border:`1px solid ${col}`,
                        background:`${col}18`,
                        color:col,cursor:"pointer",fontSize:12,
                        fontFamily:"'IBM Plex Mono',monospace",letterSpacing:0.5,fontWeight:600}}>
                      {label}
                    </button>
                  );
                })()}
              </div>
            </div>
            <div style={{marginLeft:"auto",display:"flex",gap:14}}>
              <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:10,padding:"12px 18px",textAlign:"center"}}>
                <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:24,fontWeight:500,color:P.green}}>{totP}</div>
                <div style={{fontSize:12,color:P.muted,marginTop:2}}>PRESENZE</div>
              </div>
              <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:10,padding:"12px 18px",textAlign:"center"}}>
                <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:24,fontWeight:500,color:P.red}}>{totA}</div>
                <div style={{fontSize:12,color:P.muted,marginTop:2}}>ASSENZE</div>
              </div>
            </div>
          </div>

          {/* Sezione Corso Multi Maestro */}
          <div style={{background:P.card2,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"16px 20px",marginBottom:16}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,
              color:P.muted,marginBottom:10,textTransform:"uppercase"}}>Corso Multi Maestro</div>

            {/* Select — solo corsi multi */}
            <select
              value={a.corsoId || ''}
              onChange={e => updateAllievo(a.id, 'corsoId', e.target.value || null)}
              style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,
                padding:"8px 12px",color:P.text,fontSize:15,cursor:"pointer",marginBottom:10}}>
              <option value="">— Nessun corso assegnato —</option>
              {corsi.filter(c => c.tipo === 'multi').map(c => (
                <option key={c.id} value={String(c.id)}>{c.nome}</option>
              ))}
            </select>

            {(() => {
              const cSel = getCorsoAllievo(a);
              if (!cSel || cSel.tipo !== 'multi') return null;
              const prezzoOra = (cSel.maestri||[]).reduce((s,m)=>s+m.quotaOraria,0);
              const oreScelte = getOreMDI(a.id, meseAllievi);
              const costoCalcolato = getCostoMusicaInsieme(a, meseAllievi);
              const pagato = getMDIPagato(a.id, meseAllievi);

              // genera i pulsanti: da 1h fino a durataLezioneOre × lezioniMensili (max ore possibili)
              const maxOre = (cSel.durataLezioneOre||1) * (cSel.lezioniMensili||1);
              const opzioniOre = Array.from({length: maxOre}, (_, i) => i + 1);

              return (
                <>
                  {/* Info corso */}
                  <div style={{background:`${P.blue}10`,border:`1px solid ${P.blue}25`,borderRadius:8,
                    padding:"10px 14px",marginBottom:12}}>
                    <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:6}}>
                      <span style={{fontSize:13,color:P.muted}}>{cSel.durataLezioneOre}h/lezione · {cSel.lezioniMensili} lezioni/mese</span>
                      <span style={{fontSize:13,color:P.blue,fontWeight:600}}>€{prezzoOra}/h</span>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {(cSel.maestri||[]).map((m,mi)=>(
                        <span key={mi} style={{fontSize:12,padding:"2px 8px",borderRadius:20,
                          background:`${P.blue}15`,color:P.blue,border:`1px solid ${P.blue}30`}}>
                          {m.nome} · €{m.quotaOraria}/h
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Pulsanti selezione ore */}
                  <div style={{marginBottom:12}}>
                    <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:P.muted,
                      letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>
                      Ore frequentate · {label}
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {opzioniOre.map(n => {
                        const attivo = oreScelte === n;
                        return (
                          <button key={n} onClick={() => setOreMDI(a.id, meseAllievi, attivo ? 0 : n)}
                            style={{padding:"8px 14px",borderRadius:8,cursor:"pointer",
                              fontFamily:"'IBM Plex Mono',monospace",fontSize:14,fontWeight:600,
                              border:`1px solid ${attivo ? P.blue : P.border}`,
                              background: attivo ? `${P.blue}20` : 'transparent',
                              color: attivo ? P.blue : P.muted}}>
                            {n}h
                            <div style={{fontSize:11,fontWeight:400,marginTop:2,color:attivo?P.blue:P.muted}}>
                              €{n * prezzoOra}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Pulsante Segna Pagamento */}
                  <button onClick={() => toggleMDI(a.id, meseAllievi)}
                    style={{width:"100%",padding:"12px 16px",borderRadius:10,cursor:"pointer",
                      border:`1px solid ${pagato ? P.green : P.border}`,
                      background: pagato ? `${P.green}15` : `${P.red}08`,
                      color: pagato ? P.green : P.red,
                      fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,fontSize:14,textAlign:"left",
                      transition:"all .2s"}}>
                    <div style={{fontSize:18,marginBottom:4}}>{pagato ? "✓" : "✗"}</div>
                    <div>Segna Pagamento {cSel.nome}</div>
                    <div style={{fontSize:12,fontWeight:400,color:P.muted,marginTop:2}}>
                      {oreScelte}h frequentate · €{costoCalcolato} {pagato ? '— incassato' : '— da incassare'}
                    </div>
                  </button>

                  {/* Sconto MDI */}
                  <div style={{marginTop:10,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <Chk checked={(a.scontoMDI||0)>0} color={P.gold}
                      onChange={()=>updateAllievo(a.id,"scontoMDI",(a.scontoMDI||0)>0 ? 0 : 10)}/>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{fontSize:14,color:P.text,fontWeight:500}}>Sconto MDI</span>
                      {(a.scontoMDI||0)>0 && (
                        <div style={{display:"flex",alignItems:"center",gap:6,
                          background:P.card3,border:`1px solid ${P.borderStrong}`,borderRadius:8,
                          padding:"4px 10px"}}>
                          <span style={{color:P.muted,fontSize:14,fontFamily:"'IBM Plex Mono',monospace"}}>-€</span>
                          <input
                            type="number" min="0"
                            defaultValue={a.scontoMDI||0}
                            placeholder="0"
                            onBlur={e=>updateAllievo(a.id,"scontoMDI",Math.max(0,Number(e.target.value)))}
                            onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
                            style={{background:"transparent",border:"none",color:P.text,
                              width:60,fontSize:15,fontFamily:"'IBM Plex Mono',monospace",
                              fontWeight:500,outline:"none"}}
                          />
                        </div>
                      )}
                    </div>
                    {(a.scontoMDI||0)>0 && (
                      <span style={{fontSize:12,color:P.muted}}>uscita · non influisce sullo stipendio</span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Sezione Maestri */}
          <div style={{background:P.card2,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"16px 20px",marginBottom:16}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,
              color:P.muted,marginBottom:12,textTransform:"uppercase"}}>Maestri</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
              {getAllievoCorsi(a).map((corso,ci)=>{
                const cc=mc(corso.maestro,maestriColors);
                return (
                  <div key={ci} style={{display:"flex",alignItems:"center",gap:6,
                    background:`${cc}15`,border:`1px solid ${cc}35`,borderRadius:20,padding:"5px 12px"}}>
                    {/* Tocco sul nome → si sceglie un altro maestro (con conferma sotto) */}
                    <select value={corso.maestro} title="Cambia maestro"
                      onChange={e=>setCambioMaestro({id:a.id, ci, da:corso.maestro, a:e.target.value})}
                      style={{background:"transparent",border:"none",color:cc,fontSize:isMobile?16:14,
                        fontWeight:600,cursor:"pointer",outline:"none",padding:0,maxWidth:220,
                        WebkitAppearance:"none",appearance:"none",fontFamily:"inherit"}}>
                      <option value={corso.maestro}>{corso.maestro} ▾</option>
                      {mkeysAssegnabili.filter(m=>!getAllievoCorsi(a).some(c=>c.maestro===m)).map(m=>(
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span style={{color:P.muted,fontSize:12,fontFamily:"'IBM Plex Mono',monospace"}}>€{corso.retta}</span>
                    {getAllievoCorsi(a).length>1 && (
                      <button onClick={()=>{
                        const nc=getAllievoCorsi(a).filter((_,i)=>i!==ci);
                        updateAllievoCorsi(a.id,nc);
                      }} style={{background:"none",border:"none",color:P.muted,cursor:"pointer",
                        fontSize:14,padding:"0 0 0 2px",lineHeight:1}}>✕</button>
                    )}
                  </div>
                );
              })}
            </div>
            {cambioMaestro?.id===a.id && (
              <div style={{background:P.card3,border:`1px solid ${P.goldBorder}`,borderRadius:10,
                padding:"12px 14px",marginBottom:12}}>
                <div style={{fontSize:15,color:P.text,fontWeight:600,marginBottom:6}}>
                  Cambiare maestro: {cambioMaestro.da} → {cambioMaestro.a}?
                </div>
                <div style={{fontSize:14,color:P.secondary,lineHeight:1.45,marginBottom:10}}>
                  Il cambio vale anche per i mesi passati: lezioni e stipendi già registrati passano
                  a {cambioMaestro.a}. Va bene per correggere un errore. Se invece l'allievo cambia
                  maestro solo da adesso, metti in pausa questa scheda e creane una nuova.
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{
                    const nc=getAllievoCorsi(a).map((c,i)=>i===cambioMaestro.ci?{...c,maestro:cambioMaestro.a}:c);
                    updateAllievoCorsi(a.id,nc);
                    setCambioMaestro(null);
                    notify(`Maestro cambiato: ${cambioMaestro.a}`);
                  }} style={{background:P.gold,color:"#fff",border:"none",borderRadius:6,
                    padding:"7px 14px",cursor:"pointer",fontSize:14,fontWeight:600}}>Cambia</button>
                  <button onClick={()=>setCambioMaestro(null)}
                    style={{background:"transparent",border:`1px solid ${P.border}`,color:P.muted,
                      borderRadius:6,padding:"7px 14px",cursor:"pointer",fontSize:14}}>Annulla</button>
                </div>
              </div>
            )}
            {/* Aggiungi maestro */}
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <select value={addMaestroForm.maestro}
                onChange={e=>setAddMaestroForm(f=>({...f,maestro:e.target.value}))}
                style={{background:P.card,border:`1px solid ${P.border}`,
                  color:addMaestroForm.maestro?P.text:P.muted,
                  borderRadius:6,padding:"5px 8px",fontSize:13,cursor:"pointer",outline:"none"}}>
                <option value="">+ Aggiungi maestro...</option>
                {mkeysAssegnabili.filter(m=>!getAllievoCorsi(a).some(c=>c.maestro===m)).map(m=>(
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {addMaestroForm.maestro && (<>
                <div style={{display:"flex",alignItems:"center",gap:4,
                  background:P.card3,border:`1px solid ${P.borderSubtle}`,borderRadius:6,padding:"3px 8px"}}>
                  <span style={{color:P.muted,fontSize:13}}>€</span>
                  <input type="number" min="0" placeholder="0" value={addMaestroForm.retta}
                    onChange={e=>setAddMaestroForm(f=>({...f,retta:e.target.value}))}
                    style={{background:"transparent",border:"none",color:P.text,width:60,
                      fontSize:14,fontFamily:"'IBM Plex Mono',monospace",outline:"none"}}/>
                </div>
                <button onClick={()=>{
                  const nc=[...getAllievoCorsi(a),{maestro:addMaestroForm.maestro,
                    retta:Number(addMaestroForm.retta)||0,quota_m:null}];
                  updateAllievoCorsi(a.id,nc);
                  setAddMaestroForm({maestro:'',retta:''});
                }} style={{background:P.gold,color:"#fff",border:"none",borderRadius:6,
                  padding:"5px 12px",cursor:"pointer",fontSize:13,fontWeight:600}}>
                  Aggiungi
                </button>
              </>)}
            </div>
          </div>



          {/* Solfeggio — visibile per tutti gli allievi */}
          <div style={{background:P.card2,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"14px 20px",marginBottom:16}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,
              color:P.muted,marginBottom:10,textTransform:"uppercase"}}>SOLFEGGIO</div>
            <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
              <Chk checked={!!a.solfeggio} color={P.gold}
                onChange={()=>updateAllievo(a.id,"solfeggio",!a.solfeggio)}/>
              <div>
                <div style={{fontSize:15,color:P.text,fontWeight:500}}>Iscritto al solfeggio</div>
                {a.solfeggio && <div style={{fontSize:12,color:P.muted,marginTop:1}}>Partecipa al corso di solfeggio</div>}
              </div>
            </label>
          </div>

          {/* Pagamento mese */}
          <div style={{background:P.card2,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"16px 20px",marginBottom:16}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,
              color:P.muted,marginBottom:12,textTransform:"uppercase"}}>
              Pagamento · {label}
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {/* Retta */}
              {(() => {
                const pagato = getRettaPagata(a.id, meseAllievi);
                const rettaImp = getRettaImportoMese(a.id, meseAllievi);
                const scontoMese = getScontoMese(a.id, meseAllievi);
                return (
                  // Sul telefono Retta + Importo + Sconto occupano una riga intera e l'iscrizione va sotto:
                  // con minWidth 160 il gruppo restava stretto e i box finivano sopra quello dell'iscrizione
                  <div style={{display:"flex",gap:6,flex:1,minWidth:isMobile?"100%":160}}>
                    <button onClick={()=>toggleRettaMese(a.id,'retta', meseAllievi)}
                      style={{flex:1,padding:"12px 14px",borderRadius:10,cursor:"pointer",
                        border:`1px solid ${pagato?P.green:P.border}`,
                        background:pagato?`${P.green}15`:`${P.red}08`,
                        color:pagato?P.green:P.red,
                        fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,fontSize:14,textAlign:"left"}}>
                      <div style={{fontSize:18,marginBottom:4}}>{pagato?"✓":"✗"}</div>
                      <div>Retta</div>
                    </button>
                    <div style={{display:"flex",flexDirection:"column",justifyContent:"center",
                      background:P.bg,border:`1px solid ${P.border}`,borderRadius:10,
                      padding:"10px 12px",minWidth:72}}>
                      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:P.muted,
                        letterSpacing:1,textTransform:"uppercase",marginBottom:4}}>Importo</div>
                      <div style={{display:"flex",alignItems:"center",gap:3}}>
                        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:P.secondary}}>€</span>
                        <input
                          type="number" min="0" step="1"
                          value={draftImporto[a.id+'-'+meseAllievi] !== undefined ? draftImporto[a.id+'-'+meseAllievi] : rettaImp}
                          onChange={e=>setDraftImporto(prev=>({...prev,[a.id+'-'+meseAllievi]:e.target.value}))}
                          onBlur={e=>{
                            const v=Number(e.target.value);
                            if(!isNaN(v) && v !== rettaImp) updateRettaImporto(a.id, v, meseAllievi);
                            setDraftImporto(prev=>{const n={...prev};delete n[a.id+'-'+meseAllievi];return n;});
                          }}
                          onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
                          style={{width:56,background:"transparent",border:"none",
                            fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?16:15,fontWeight:600,
                            color:P.text,outline:"none",padding:0}}
                        />
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",justifyContent:"center",
                      background:P.bg,border:`1px solid ${scontoMese>0?"rgba(196,107,107,0.4)":P.border}`,borderRadius:10,
                      padding:"10px 12px",minWidth:72}}>
                      <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,
                        color:scontoMese>0?P.red:P.muted,
                        letterSpacing:1,textTransform:"uppercase",marginBottom:4}}>Sconto</div>
                      <div style={{display:"flex",alignItems:"center",gap:3}}>
                        <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:P.secondary}}>€</span>
                        <input
                          type="number" min="0" step="1"
                          value={draftSconto[a.id+'-'+meseAllievi] !== undefined ? draftSconto[a.id+'-'+meseAllievi] : scontoMese}
                          onChange={e=>setDraftSconto(prev=>({...prev,[a.id+'-'+meseAllievi]:e.target.value}))}
                          onBlur={e=>{
                            const v=Number(e.target.value);
                            if(!isNaN(v) && v !== scontoMese) updateScontoMese(a.id, v, meseAllievi);
                            setDraftSconto(prev=>{const n={...prev};delete n[a.id+'-'+meseAllievi];return n;});
                          }}
                          onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
                          style={{width:56,background:"transparent",border:"none",
                            fontFamily:"'IBM Plex Mono',monospace",fontSize:isMobile?16:15,fontWeight:600,
                            color:scontoMese>0?P.red:P.text,outline:"none",padding:0}}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Iscrizione */}
              {a.iscrizione>0 && (
                <button onClick={()=>toggleRettaMese(a.id,'iscr', meseAllievi)}
                  style={{flex:1,minWidth:120,padding:"12px 16px",borderRadius:10,cursor:"pointer",
                    border:`1px solid ${getIscrPagata(a.id, meseAllievi)?P.green:P.border}`,
                    background:getIscrPagata(a.id, meseAllievi)?`${P.green}15`:`${P.red}08`,
                    color:getIscrPagata(a.id, meseAllievi)?P.green:P.red,
                    fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,fontSize:14,textAlign:"left"}}>
                  <div style={{fontSize:18,marginBottom:4}}>{getIscrPagata(a.id, meseAllievi)?"✓":"✗"}</div>
                  <div>Iscrizione</div>
                  <div style={{fontSize:12,fontWeight:400,color:P.muted,marginTop:2}}>€{a.iscrizione}</div>
                </button>
              )}
            </div>
          </div>

          {/* Telefono + WhatsApp */}
          <div style={{background:P.card2,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"16px 20px",marginBottom:16}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,
              color:P.muted,marginBottom:10,textTransform:"uppercase"}}>Contatto</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                <input
                  value={telInput} onChange={e=>setTelInput(e.target.value)}
                  onBlur={()=>{ if(telInput!==(a.telefono||'')) updateAllievo(a.id,"telefono",telInput); }}
                  onKeyDown={e=>{if(e.key==="Enter")e.target.blur();}}
                  placeholder="Numero telefono / WhatsApp"
                  style={{background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,
                    padding:"8px 12px",color:P.text,fontSize:15,flex:1,minWidth:160,
                    fontFamily:"'Syne',sans-serif"}}
                />
                <a href={telInput ? `https://wa.me/${telInput.replace(/\D/g,'')}?text=${waMsg}` : '#'}
                  target="_blank" rel="noreferrer"
                  onClick={e=>!telInput&&e.preventDefault()}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:8,
                    background:telInput?"#25D366":`${P.muted}22`,color:telInput?"#fff":P.muted,
                    textDecoration:"none",fontSize:14,fontWeight:600,cursor:telInput?"pointer":"not-allowed",
                    whiteSpace:"nowrap"}}>
                  <span style={{fontSize:17}}>📱</span> WhatsApp ({nLezioni} lez.)
                </a>
              </div>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <input
                  value={emailInput} onChange={e=>setEmailInput(e.target.value)}
                  onBlur={()=>{ if(emailInput!==(a.email||'')) updateAllievo(a.id,"email",emailInput); }}
                  onKeyDown={e=>{if(e.key==="Enter")e.target.blur();}}
                  placeholder="Email"
                  style={{background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,
                    padding:"8px 12px",color:P.text,fontSize:15,flex:1,minWidth:160,
                    fontFamily:"'Syne',sans-serif"}}
                />
                <div style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:160}}>
                  <label style={{fontSize:12,color:P.muted,letterSpacing:1,textTransform:"uppercase",whiteSpace:"nowrap"}}>Nato il</label>
                  <input
                    type="date" value={dataNascitaInput||''} onChange={e=>setDataNascitaInput(e.target.value)}
                    onBlur={()=>{ if(dataNascitaInput!==(a.dataNascita||'')) updateAllievo(a.id,"dataNascita",dataNascitaInput||null); }}
                    style={{background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,
                      padding:"8px 10px",color:dataNascitaInput?P.text:P.muted,fontSize:15,flex:1,
                      fontFamily:"'Syne',sans-serif"}}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Note allievo */}
          <div style={{background:P.card2,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"16px 20px",marginBottom:16}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,
              color:P.muted,marginBottom:10,textTransform:"uppercase"}}>Note</div>
            <NoteInput
              value={a.note||''}
              onChange={v=>updateAllievo(a.id,"note",v)}
              color={c}
            />
          </div>

          {/* Calendario presenze */}
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,padding:24,marginBottom:14}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:8}}>
              {GDS.map(g=><div key={g} style={{textAlign:"center",fontSize:12,
                color:g==="Dom"||g==="Sab"?P.red:P.muted,fontWeight:600,letterSpacing:1}}>{g.toUpperCase()}</div>)}
            </div>
            {weeks.map((wk,wi)=>(
              <div key={wi} style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:4}}>
                {wk.map((d,di)=>{
                  // Chiave diversa dai giorni (key={d}): con key={di} le caselle vuote si scambiavano coi giorni 1-6 e cambiando mese restavano caselle in più
                  if(!d) return <div key={`vuoto-${di}`}/>;
                  const v=pz[d]; const ci=cellInfo(v);
                  const dow=(primo+d-1)%7; const we=dow===0||dow===6;
                  const oggi=isOggi(d);
                  const dayM = lezMese[String(d)];
                  const dayMC = dayM ? mc(dayM, maestriColors) : null;
                  return (
                    <div key={d} {...gestiCasella(a,d,meseAllievi)}
                      style={{borderRadius:8,padding:"6px 4px",textAlign:"center",cursor:"pointer",
                        background:v?ci.bg:oggi?`${P.gold}12`:we?"rgba(28,32,56,.8)":"transparent",
                        border:`1px solid ${oggi?P.gold:v?ci.border:P.border}`,transition:"transform .1s"}}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
                      <div style={{width:24,height:24,borderRadius:"50%",margin:"0 auto 2px",
                        background:oggi&&!v?P.gold:"transparent",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:16,fontWeight:oggi?700:600,
                        color:oggi&&!v?"#080B14":v?ci.color:we?P.red:P.muted,
                        fontFamily:"'Syne',sans-serif"}}>{d}</div>
                      <div style={{fontSize:13,fontWeight:700,color:ci.color,minHeight:14}}>{v?ci.label:""}</div>
                      {isMultiMaestro && v==="p" && (
                        <div onClick={e=>cycleMaestro(e,d)}
                          title={dayM ? `Maestro: ${dayM} — clicca per cambiare` : "Clicca per assegnare maestro"}
                          style={{marginTop:3,fontSize:11,fontWeight:700,letterSpacing:0.3,
                            padding:"1px 5px",borderRadius:10,cursor:"pointer",
                            background:dayMC?`${dayMC}25`:`${P.muted}20`,
                            color:dayMC||P.muted,border:`1px solid ${dayMC?`${dayMC}50`:`${P.muted}30`}`,
                            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:"100%"}}>
                          {dayM ? dayM.split(' ')[0] : "?"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <p style={{fontSize:13,color:P.muted,marginBottom:12}}>Tocca: vuoto→P→A→R→▷→vuoto</p>
        </div>
      );
    }

    // Calcola la quota da pagare ai maestri NON-titolari per un allievo in un mese:
    // — corsi singoli: 60% della retta per ogni corso con maestro non-titolare
    // — corso MDI (multi): ore effettive × quotaOraria dei maestri non-titolari
    const calcQuotaMaestri = (a, mese) => {
      const quotaRegolare = getAllievoCorsi(a).reduce((s, cc) => {
        if (!cc.maestro || isTitolare(cc.maestro)) return s;
        return s + (cc.retta || 0) * 0.60;
      }, 0);
      const corsoMDI = getCorsoAllievo(a);
      const quotaMDI = (corsoMDI && corsoMDI.tipo === 'multi' && getMDIPagato(a.id, mese))
        ? (corsoMDI.maestri || [])
            .filter(m => !isTitolare(m.nome))
            .reduce((s, m) => s + (m.quotaOraria || 0) * getOreMDI(a.id, mese), 0)
        : 0;
      return quotaRegolare + quotaMDI;
    };

    const exportPresenzeExcel = () => {
      const header = ['Nome', 'Retta lorda (€)', 'Importo pagato (€)', 'Netto atteso (€)', 'Sconto (€)', ...daysArr.map(d => String(d))];
      const rows = lista.map(a => {
        const pz = mesePz[a.id] || {};
        const mdiCosto = getCostoMusicaInsieme(a, meseAllievi); // costo atteso (ore × €/h)
        const mdiPagato = getMDIPagato(a.id, meseAllievi) ? mdiCosto : 0; // solo se flaggato pagato
        const lorda = getRettaImportoMese(a.id, meseAllievi) + mdiCosto;
        const netta = rettaNetta(a, meseAllievi) + mdiPagato;
        // Quota totale maestri: stessa formula di stipendioMaestro — importoMese × ratio × perc
        const tuttiCorsiA = getAllievoCorsi(a);
        const rettaTotA = tuttiCorsiA.reduce((s, cc) => s + (cc.retta || 0), 0);
        const importoMeseA = getRettaImportoMese(a.id, meseAllievi);
        // Il titolare non detrae stipendio — la sua quota è entrata al 100% per l'accademia
        const quotaMaestro = Math.round(tuttiCorsiA.reduce((s, cc) => {
          if (!cc.maestro) return s;
          const perc = isTitolare(cc.maestro) ? 0 : 0.60;
          const ratio = rettaTotA > 0 ? (cc.retta || 0) / rettaTotA : 1;
          return s + importoMeseA * ratio * perc;
        }, 0) * 100) / 100;
        // MDI: costo non-titolare che l'accademia paga (anche se specNoRetta = true)
        const corsoA = getCorsoAllievo(a);
        const oreA = getOreMDI(a.id, meseAllievi);
        const mdiCostoNonTitolare = (corsoA && corsoA.tipo === 'multi')
          ? Math.round((corsoA.maestri || []).reduce((s, m) =>
              !isTitolare(m.nome) ? s + (m.quotaOraria || 0) * oreA : s, 0) * 100) / 100
          : 0;
        // netto = rettaNetta - quota_maestri_regolare + mdi_incasso - mdi_costo_non_titolare
        const nettoAtteso = Math.round((rettaNetta(a, meseAllievi) - quotaMaestro + mdiCosto - mdiCostoNonTitolare) * 100) / 100;
        return [
          a.nome,
          lorda,
          quotaMaestro,
          nettoAtteso,
          getScontoMese(a.id, meseAllievi),
          ...daysArr.map(d => { const v = pz[d]; return v ? cellInfo(v).label : ''; }),
        ];
      });
      const totaleLordo = lista.reduce((s, a) => {
        return s + getRettaImportoMese(a.id, meseAllievi) + getCostoMusicaInsieme(a, meseAllievi);
      }, 0);
      const totaleNetto = lista.reduce((s, a) => {
        const mdiPagato = getMDIPagato(a.id, meseAllievi) ? getCostoMusicaInsieme(a, meseAllievi) : 0;
        return s + rettaNetta(a, meseAllievi) + mdiPagato;
      }, 0);
      const totaleNettoAcc = lista.reduce((s, a) => {
        const tuttiCorsiA = getAllievoCorsi(a);
        const rettaTotA = tuttiCorsiA.reduce((sr, cc) => sr + (cc.retta || 0), 0);
        const importoMeseA = getRettaImportoMese(a.id, meseAllievi);
        const qm = tuttiCorsiA.reduce((qs, cc) => {
          if (!cc.maestro) return qs;
          const perc = isTitolare(cc.maestro) ? 0 : 0.60;
          const ratio = rettaTotA > 0 ? (cc.retta || 0) / rettaTotA : 1;
          return qs + importoMeseA * ratio * perc;
        }, 0);
        const corsoA = getCorsoAllievo(a);
        const oreA = getOreMDI(a.id, meseAllievi);
        const mdiCostoA = getCostoMusicaInsieme(a, meseAllievi);
        const mdiCostoNonTitolareA = (corsoA && corsoA.tipo === 'multi')
          ? (corsoA.maestri || []).reduce((qs, m) =>
              !isTitolare(m.nome) ? qs + (m.quotaOraria || 0) * oreA : qs, 0)
          : 0;
        return s + rettaNetta(a, meseAllievi) - qm + mdiCostoA - mdiCostoNonTitolareA;
      }, 0);
      const totaleSconti = lista.reduce((s, a) => s + getScontoMese(a.id, meseAllievi), 0);
      const totaleRow = ['TOTALE RETTE', totaleLordo, totaleNetto, Math.round(totaleNettoAcc * 100) / 100, totaleSconti, ...daysArr.map(() => '')];
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows, totaleRow]);
      // Evidenzia in giallo le righe degli allievi MDI
      lista.forEach((a, i) => {
        const corsoMDI = getCorsoAllievo(a);
        if (!corsoMDI || corsoMDI.tipo !== 'multi') return;
        const rowIdx = i + 1; // +1 per l'header
        const nCols = header.length;
        for (let c = 0; c < nCols; c++) {
          const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c });
          if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };
          ws[cellRef].s = { fill: { fgColor: { rgb: 'FFFDE7' } } };
        }
      });
      // larghezza colonne: Nome larga, 4 colonne importo, giorni stretti
      ws['!cols'] = [
        {wch: 22},
        {wch: 16},
        {wch: 18},
        {wch: 20},
        {wch: 12},
        ...daysArr.map(() => ({wch: 4})),
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, label);
      XLSX.writeFile(wb, `Presenze_${meseAllievi}.xlsx`);
    };

    return (
      <div style={{animation:"fi .35s ease"}}>
        <div style={{marginBottom:22,display:"flex",alignItems:isMobile?"flex-start":"center",
          justifyContent:"space-between",flexWrap:"wrap",gap:12,flexDirection:isMobile?"column":"row"}}>
          <div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.muted,marginBottom:7,fontWeight:400,textTransform:"uppercase"}}>REGISTRO</div>
            <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5,margin:0}}>Presenze</h1>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <button onClick={exportPresenzeExcel}
              style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:13,padding:"7px 14px",
                borderRadius:8,border:`1px solid ${P.gold}`,background:`${P.gold}15`,
                color:P.gold,cursor:"pointer",letterSpacing:0.5}}>
              ↓ Excel
            </button>
            <NavMese/>
          </div>
        </div>
        {/* Su telefono: tendina maestri + pulsante Aa su una riga sola */}
        {isMobile && (
          <div style={{display:"flex",gap:8,marginBottom:16}}>
            <select value={filtroMP} onChange={e=>setFiltroMP(e.target.value)}
              style={{flex:1,minWidth:0,padding:"10px 12px",borderRadius:10,fontSize:17,
                fontFamily:"'Syne',sans-serif",background:P.card2,
                color:filtroMP==="TUTTI"?P.text:(maestriColors[filtroMP]||P.gold),
                border:`1px solid ${filtroMP==="TUTTI"?P.border:(maestriColors[filtroMP]||P.gold)}`}}>
              {["TUTTI",...mkeysVisibili()].map(m=><option key={m} value={m}>{m==="TUTTI"?"Tutti i maestri":m}</option>)}
            </select>
            {pulsanteAa}
          </div>
        )}
        {!isMobile && filtroMP!=="TUTTI" && !scegliMaestro && (()=>{
          const c = maestriColors[filtroMP]||P.gold;
          const btn = {padding:"8px 14px",borderRadius:10,border:`1px solid ${P.border}`,background:"transparent",
            color:P.secondary,cursor:"pointer",fontSize:14,fontFamily:"'Syne',sans-serif",whiteSpace:"nowrap"};
          return (
            <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{padding:"8px 16px",borderRadius:10,border:`1px solid ${c}`,background:`${c}18`,
                color:c,fontSize:16,fontWeight:700,fontFamily:"'Syne',sans-serif"}}>{filtroMP}</div>
              <button onClick={()=>setScegliMaestro(true)} style={btn}>Cambia maestro</button>
              <button onClick={()=>setFiltroMP("TUTTI")} style={btn}>✕ Tutti i maestri</button>
            </div>
          );
        })()}
        {!isMobile && (filtroMP==="TUTTI" || scegliMaestro) && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7,marginBottom:16}}>
          {["TUTTI",...mkeysVisibili()].map(m=>{
            const c = maestriColors[m]||P.gold; const act = filtroMP===m;
            return (
              <button key={m} onClick={()=>{setFiltroMP(m);setScegliMaestro(false);}} style={{
                padding:"8px 10px",borderRadius:10,border:`1px solid ${act?c:P.border}`,
                background:act?`${c}18`:P.card2,color:act?c:P.secondary,
                cursor:"pointer",fontSize:13,fontWeight:act?700:400,
                fontFamily:"'Syne',sans-serif",textAlign:"center",
                transition:"all .15s",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {m}
              </button>
            );
          })}
        </div>
        )}
        {/* Toggle viste */}
        <div style={{display:"flex",gap:6,rowGap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
          {[['mese','Mese'],['settimana','Settimana'],['giorno','Giorno']].map(([v,l])=>(
            <button key={v} onClick={()=>{setPresenzeView(v);if(v!=='mese'){const oggi=new Date();setPresenzeDay(oggi);setMeseAllievi(NOMI_MESI_IT[oggi.getMonth()]+'-'+oggi.getFullYear());}}}
              style={{padding:"5px 14px",borderRadius:20,border:`1px solid ${presenzeView===v?P.gold:P.border}`,
                background:presenzeView===v?`${P.gold}18`:"transparent",
                color:presenzeView===v?P.gold:P.muted,cursor:"pointer",fontSize:14,
                fontFamily:"'Syne',sans-serif",fontWeight:presenzeView===v?600:400}}>
              {l}
            </button>
          ))}
          {(presenzeView==='giorno'||presenzeView==='settimana') && (
            <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:isMobile?0:12}}>
              <button onClick={()=>{const d=new Date(presenzeDay);d.setDate(d.getDate()-(presenzeView==='giorno'?1:7));setPresenzeDay(d);setMeseAllievi(NOMI_MESI_IT[d.getMonth()]+'-'+d.getFullYear());}}
                style={{background:"transparent",border:`1px solid ${P.gold}`,color:P.gold,borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
              <span style={{fontSize:17,fontWeight:600,color:P.text,fontFamily:"'Syne',sans-serif",minWidth:isMobile?140:190,textAlign:"center"}}>
                {presenzeView==='giorno'
                  ? `${GDS[presenzeDay.getDay()]} ${presenzeDay.getDate()} ${NOMI_MESI_IT[presenzeDay.getMonth()]}${isMobile?'':' '+presenzeDay.getFullYear()}`
                  : (()=>{const days=getWeekDays(presenzeDay);return `${days[0].getDate()} – ${days[6].getDate()} ${NOMI_MESI_IT[days[6].getMonth()]}`;})()}
              </span>
              <button onClick={()=>{const d=new Date(presenzeDay);d.setDate(d.getDate()+(presenzeView==='giorno'?1:7));setPresenzeDay(d);setMeseAllievi(NOMI_MESI_IT[d.getMonth()]+'-'+d.getFullYear());}}
                style={{background:"transparent",border:`1px solid ${P.gold}`,color:P.gold,borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
            </div>
          )}
          {!isMobile && <div style={{marginLeft:"auto"}}>{pulsanteAa}</div>}
        </div>

        {presenzeView==='mese' && (
        <div ref={grigliaRef} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"auto",maxHeight:grigliaMaxH}}>
          <table style={{minWidth:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${P.border}`}}>
                <th style={{...thFisso,padding:"12px 16px",textAlign:"left",fontSize:zs(11),letterSpacing:2,color:P.muted,
                  left:0,zIndex:3,minWidth:zs(180)}}>ALLIEVO</th>
                <th style={{...thFisso,padding:"12px 8px",fontSize:zs(11),color:P.muted,textAlign:"center",minWidth:zs(40)}}>TOT</th>
                {daysArr.map(d=>{
                  const dow=(primo+d-1)%7; const we=dow===0||dow===6;
                  const oggi=isOggi(d);
                  return <th key={d} style={{...thFisso,padding:"6px 2px",textAlign:"center",minWidth:zs(28),
                    fontSize:zs(11),color:oggi?P.gold:we?P.red:P.muted,fontWeight:600}}>
                    <div style={{width:oggi?zs(24):undefined,height:oggi?zs(24):undefined,borderRadius:oggi?"50%":undefined,
                      background:oggi?P.gold:undefined,color:oggi?"#080B14":undefined,
                      display:oggi?"flex":undefined,alignItems:oggi?"center":undefined,
                      justifyContent:oggi?"center":undefined,margin:oggi?"0 auto":undefined,
                      fontWeight:oggi?700:600}}>{d}</div>
                    <div style={{fontWeight:400,marginTop:oggi?2:0}}>{GDS[dow].substring(0,2)}</div>
                  </th>;
                })}
              </tr>
            </thead>
            <tbody>
              {lista.map((a,i)=>{
                const c=mc(a.maestro,maestriColors);
                const pz=mesePz[a.id]||{};
                const totOre=Object.values(pz).reduce((s,v)=>s+oreFromStato(v),0);
                const fmtOre = totOre===0?"0":Number.isInteger(totOre)?String(totOre):String(totOre).replace(".",",");
                const oreColor = totOre===0?P.red:totOre<=1?"#f97316":totOre<=2?P.yellow:totOre<=3?"#a3e635":P.green;
                return (
                  <tr key={a.id} style={{borderBottom:i<lista.length-1?`1px solid ${P.border}`:"none"}}
                    onMouseEnter={e=>e.currentTarget.style.background=`${c}07`}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
	                    <td style={{padding:`${zs(8)}px 16px`,position:"sticky",left:0,background:P.card,zIndex:1,cursor:"pointer"}}
	                      onClick={()=>setPresAllievo(a.id)}>
	                      <div style={{display:"flex",alignItems:"center",gap:7}}>
	                        <span style={{width:zs(26),textAlign:"right",fontFamily:"'IBM Plex Mono',monospace",
	                          fontSize:zs(12),color:P.muted,flexShrink:0}}>{i+1}</span>
	                        <div style={{display:"flex",alignItems:"center"}}>
	                          {getAllievoCorsi(a).map((corso,ci)=>{
	                            const dc=mc(corso.maestro,maestriColors);
                            return <div key={ci} style={{width:zs(8),height:zs(8),borderRadius:"50%",background:dc,flexShrink:0,
                              marginLeft:ci>0?-2:0,border:ci>0?`1px solid ${P.card2}`:"none"}}/>;
                          })}
                        </div>
                        <span style={{fontSize:zs(15),fontWeight:500,whiteSpace:"nowrap"}}>{a.nome}</span>
                      </div>
                    </td>
                    <td style={{padding:"8px 8px",textAlign:"center"}}>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:zs(15),fontWeight:700,
                        color:oreColor}}>{fmtOre}h</span>
                    </td>
                    {daysArr.map(d=>{
                      const v=pz[d]; const ci=cellInfo(v); const oggi=isOggi(d);
                      return <td key={d} style={{padding:"3px 2px",textAlign:"center",
                        background:oggi?`${P.gold}10`:undefined}}>
                        <div {...gestiCasella(a,d,meseAllievi)}
                          style={{width:zs(26),height:zs(26),borderRadius:4,margin:"0 auto",background:ci.bg,
                            color:ci.color,display:"flex",alignItems:"center",justifyContent:"center",
                            fontSize:zs(11),fontWeight:700,cursor:"pointer",
                            border:`1px solid ${oggi?P.gold:v?ci.border:"transparent"}`}}
                          onMouseEnter={e=>e.currentTarget.style.transform="scale(1.3)"}
                          onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
                          {ci.label}
                        </div>
                      </td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        {presenzeView==='giorno' && (
          // overflow "clip" (non "hidden") arrotonda gli angoli senza bloccare l'intestazione sticky
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"clip"}}>
            <div style={{position:"sticky",top:isStandalone?"env(safe-area-inset-top)":0,zIndex:2,background:P.card,
              display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",padding:"14px 20px",borderBottom:`1px solid ${P.border}`}}>
              <span style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?19:24,fontWeight:700,
                color:giornoEOggi?P.gold:P.text}}>{dataGiornoLunga}</span>
              {giornoEOggi ? (
                <span style={{fontSize:12,fontWeight:700,letterSpacing:1,padding:"4px 10px",borderRadius:20,
                  background:`${P.gold}20`,color:P.gold,border:`1px solid ${P.gold}55`}}>OGGI</span>
              ) : (
                <button onClick={()=>{const oggi=new Date();setPresenzeDay(oggi);setMeseAllievi(NOMI_MESI_IT[oggi.getMonth()]+'-'+oggi.getFullYear());}}
                  style={{fontSize:13,padding:"6px 12px",borderRadius:20,cursor:"pointer",fontFamily:"'Syne',sans-serif",
                    background:"transparent",color:P.gold,border:`1px solid ${P.gold}`}}>Vai a oggi</button>
              )}
            </div>
            {lista.map((a,i)=>{
              const c=mc(a.maestro,maestriColors);
              const dayKey = presenzeDay.getDate();
              const meseKey = NOMI_MESI_IT[presenzeDay.getMonth()]+'-'+presenzeDay.getFullYear();
              const v=(presenze[meseKey]||{})[a.id]?.[dayKey];
              const ci=cellInfo(v);
              return (
	                <div key={a.id} {...gestiCasella(a,dayKey,meseKey)}
	                  style={{display:"flex",alignItems:"center",gap:12,padding:`${zs(12)}px 16px`,
	                    borderBottom:i<lista.length-1?`1px solid ${P.border}`:"none",
	                    background:v?ci.bg:"transparent",cursor:"pointer"}}
	                  onMouseEnter={e=>e.currentTarget.style.background=v?ci.bg:`${c}08`}
	                  onMouseLeave={e=>e.currentTarget.style.background=v?ci.bg:"transparent"}>
	                  <span style={{width:zs(26),textAlign:"right",fontFamily:"'IBM Plex Mono',monospace",
	                    fontSize:zs(12),color:P.muted,flexShrink:0}}>{i+1}</span>
	                  <Avatar nome={a.nome} color={c} size={zs(34)}/>
	                  <span style={{flex:1,fontSize:zs(16),fontWeight:500}}>{a.nome}</span>
                  <span style={{fontSize:zs(13),color:P.muted}}>{getAllievoCorsi(a).map(cc=>cc.maestro).join(', ')}</span>
                  <div style={{width:zs(36),height:zs(36),borderRadius:6,background:v?ci.bg:"transparent",
                    border:`1px solid ${v?ci.border:P.border}`,display:"flex",alignItems:"center",
                    justifyContent:"center",fontSize:zs(15),fontWeight:700,color:v?ci.color:P.muted}}>
                    {v?ci.label:'·'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {presenzeView==='settimana' && (
          <div ref={grigliaRef} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"auto",maxHeight:grigliaMaxH}}>
            <table style={{minWidth:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${P.border}`}}>
                  <th style={{...thFisso,padding:"10px 12px",textAlign:"left",fontSize:zs(11),letterSpacing:2,color:P.muted,left:0,zIndex:3,minWidth:zs(170)}}>ALLIEVO</th>
                  {getWeekDays(presenzeDay).map((d,di)=>{
                    const isToday=d.toDateString()===new Date().toDateString();
                    return <th key={di} style={{...thFisso,padding:"8px 6px",textAlign:"center",fontSize:zs(14),color:isToday?P.gold:P.muted,minWidth:zs(48),letterSpacing:1}}>
                      <div>{['Lun','Mar','Mer','Gio','Ven','Sab','Dom'][di]}</div>
                      <div style={{width:isToday?zs(26):undefined,height:isToday?zs(26):undefined,borderRadius:isToday?"50%":undefined,background:isToday?P.gold:undefined,color:isToday?"#080B14":undefined,display:"flex",alignItems:"center",justifyContent:"center",margin:"2px auto 0",fontWeight:isToday?700:600}}>{d.getDate()}</div>
                    </th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {lista.map((a,i)=>{
                  const c=mc(a.maestro,maestriColors);
                  return (
                    <tr key={a.id} style={{borderBottom:i<lista.length-1?`1px solid ${P.border}`:"none"}}
                      onMouseEnter={e=>e.currentTarget.style.background=`${c}07`}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
	                      <td style={{padding:`${zs(10)}px 12px`,position:"sticky",left:0,background:P.card,zIndex:1,cursor:"pointer"}}
	                        onClick={()=>setPresAllievo(a.id)}>
	                        <div style={{display:"flex",alignItems:"center",gap:6}}>
	                          <span style={{width:zs(24),textAlign:"right",fontFamily:"'IBM Plex Mono',monospace",
	                            fontSize:zs(12),color:P.muted,flexShrink:0}}>{i+1}</span>
	                          <Avatar nome={a.nome} color={c} size={zs(30)}/>
	                          <span style={{fontSize:zs(15),fontWeight:500,whiteSpace:"nowrap"}}>{a.nome}</span>
                        </div>
                      </td>
                      {getWeekDays(presenzeDay).map((d,di)=>{
                        const meseKey=NOMI_MESI_IT[d.getMonth()]+'-'+d.getFullYear();
                        const v=(presenze[meseKey]||{})[a.id]?.[d.getDate()];
                        const ci=cellInfo(v);
                        const isToday=d.toDateString()===new Date().toDateString();
                        return (
                          <td key={di} {...gestiCasella(a,d.getDate(),meseKey)}
                            style={{padding:"4px 6px",textAlign:"center",cursor:"pointer",
                              background:isToday?`${P.gold}10`:undefined}}>
                            <div style={{width:zs(36),height:zs(36),borderRadius:6,margin:"0 auto",
                              background:v?ci.bg:"transparent",
                              border:`1px solid ${v?ci.border:isToday?`${P.gold}60`:P.border}`,
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:zs(14),fontWeight:700,color:v?ci.color:P.muted}}>
                              {v?ci.label:'·'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── PAGAMENTI — componente esterno (src/components/Pagamenti.jsx) ────────────
  // Usato come: <Pagamenti pagamenti={pagamenti} maestriColors={maestriColors}
  //               isMobile={isMobile} goToAllievo={goToAllievo} togglePagamento={togglePagamento}/>

  // ── STIPENDI ─────────────────────────────────────────────────────────────────
  // Calcola la quota maestro per un allievo (usa quota_m fissa se presente, altrimenti 60%)
  const quotaMaestro = (a) => {
    const pag = pagamenti.find(p=>p.id===a.id);
    return pag && pag.quota_m!=null ? pag.quota_m : Math.round(a.retta * PERC_MAESTRO);
  };

  // ── STIPENDI — componente esterno (src/components/Stipendi.jsx) ───────────────
  // Usato come: <Stipendi allievi={allievi} pagamenti={pagamenti} mkeys={mkeys}
  //   maestriColors={maestriColors} stipendiPagati={stipendiPagati}
  //   isMobile={isMobile} dbReady={dbReady} goToMaestro={goToMaestro}
  //   goToAllievo={goToAllievo} saveHistory={saveHistory}
  //   setStipendi={setStipendi} saveStipendioToDB={saveStipendioToDB}
  //   quotaMaestro={quotaMaestro}/>


  // ── NICOL SEGRETARIA ─────────────────────────────────────────────────────────


  // ── MUSICA D'INSIEME ──────────────────────────────────────────────────────────
  const MusicaInsieme = () => {
    const [meseMDI, setMeseMDI] = useState(getMeseCorrenteReale);

    const [nomeMese, annoStr] = meseMDI.split('-');
    const anno = parseInt(annoStr, 10);
    const mIdxCal = NOMI_MESI_IT.indexOf(nomeMese);
    const nGiorni = new Date(anno, mIdxCal + 1, 0).getDate();
    const primoGiorno = new Date(anno, mIdxCal, 1).getDay();
    const meseLabelCap = nomeMese.charAt(0).toUpperCase() + nomeMese.slice(1);

    // Allievi MDI = allievi con corso di tipo 'multi'
    const mdiList = allievi.filter(a => {
      // Prima: cerca tramite corsoId (link diretto alla definizione corso)
      if (a.corsoId) {
        const corso = corsi.find(cc => cc.id === a.corsoId);
        return corso?.tipo === 'multi';
      }
      // Fallback: se l'allievo ha più maestri nel JSONB corsi = multi-maestro
      return (a.corsi || []).length > 1;
    });

    // Presenze: { "mese-anno": { allId: { giorno: true } } }
    const presMese = (mdiPresenze[meseMDI] || {});

    const togglePresenzaMDI = async (allId, giorno) => {
      const wasPresente = !!(mdiPresenze[meseMDI]?.[allId]?.[giorno]);
      const nuovoPresente = !wasPresente;
      if (nuovoPresente) {
        const { error } = await supabase.from('mdi_presenze').upsert(
          { mese: meseMDI, allievo_id: allId, giorno },
          { onConflict: 'mese,allievo_id,giorno' }
        );
        if (error) { notify('Errore salvataggio presenza: ' + error.message, 'err'); return; }
      } else {
        const { error } = await supabase.from('mdi_presenze').delete()
          .eq('mese', meseMDI).eq('allievo_id', allId).eq('giorno', giorno);
        if (error) { notify('Errore rimozione presenza: ' + error.message, 'err'); return; }
      }
      setMdiPresenze(prev => {
        const curMese = prev[meseMDI] || {};
        const curAll = curMese[allId] || {};
        const next = { ...curAll };
        if (next[giorno]) delete next[giorno]; else next[giorno] = true;
        return { ...prev, [meseMDI]: { ...curMese, [allId]: next } };
      });
    };

    // Calendario: celle
    const cells = [];
    for (let i = 0; i < primoGiorno; i++) cells.push(null);
    for (let d = 1; d <= nGiorni; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    const oggi = new Date();
    const isOggi = (d) => anno === oggi.getFullYear() && mIdxCal === oggi.getMonth() && d === oggi.getDate();

    const COLOR_MDI = "#fb923c";

    return (
      <div style={{animation:"fi .35s ease"}}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",
          marginBottom:26,flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:11,letterSpacing:3,color:COLOR_MDI,marginBottom:7,fontWeight:600,textTransform:"uppercase"}}>CORSO</div>
            <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5}}>Musica d'Insieme</h1>
            <p style={{color:P.secondary,fontSize:15,marginTop:4}}>{mdiList.length} allievi iscritti</p>
          </div>
          {/* Nav mese */}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>setMeseMDI(meseAdd(meseMDI,-1))}
              style={{background:"transparent",border:`1px solid ${P.border}`,color:P.text,
                padding:"5px 12px",borderRadius:7,cursor:"pointer",fontSize:16}}>←</button>
            <span style={{fontSize:15,fontWeight:600,color:P.text,minWidth:110,textAlign:"center"}}>
              {meseLabelCap} {anno}
            </span>
            <button onClick={()=>setMeseMDI(meseAdd(meseMDI,1))}
              style={{background:"transparent",border:`1px solid ${P.border}`,color:P.text,
                padding:"5px 12px",borderRadius:7,cursor:"pointer",fontSize:16}}>→</button>
          </div>
        </div>

        {/* Calendario */}
        <div style={{background:P.card,border:`2px solid ${COLOR_MDI}44`,borderRadius:16,
          padding:isMobile?"16px 12px":"24px 28px",marginBottom:24,
          boxShadow:`0 4px 24px ${COLOR_MDI}12`}}>

          {/* Intestazione giorni */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:isMobile?4:6,marginBottom:8}}>
            {GDS.map(g=>(
              <div key={g} style={{textAlign:"center",fontSize:12,
                color:g==="Dom"||g==="Sab"?P.red:P.muted,fontWeight:700,letterSpacing:.5}}>
                {g.toUpperCase()}
              </div>
            ))}
          </div>

          {/* Settimane */}
          {weeks.map((wk,wi)=>(
            <div key={wi} style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:isMobile?4:6,marginBottom:isMobile?4:6}}>
              {wk.map((d,di)=>{
                // Chiave diversa dai giorni (key={d}): con key={di} le caselle vuote si scambiavano coi giorni 1-6 e cambiando mese restavano caselle in più
                if(!d) return <div key={`vuoto-${di}`}/>;
                const dow = (primoGiorno + d - 1) % 7;
                const isDom = dow === 0;
                const isSab = dow === 6;
                const isWe = isDom || isSab;
                const isHoje = isOggi(d);
                const presAllievi = mdiList.filter(a => !!(presMese[a.id]||{})[d]);
                const totP = presAllievi.length;

                return (
                  <div key={d} style={{
                    borderRadius:10,
                    padding:isMobile?"6px 2px":"8px 4px",
                    textAlign:"center",
                    background: isDom ? "rgba(28,32,56,.7)" : isSab ? "rgba(28,32,56,.4)" : `${P.bg}cc`,
                    border:`1px solid ${isHoje?COLOR_MDI:isWe?"#1D2238":"#1D2238"}`,
                    minHeight:isMobile?60:80,
                    display:"flex",flexDirection:"column",gap:4,
                  }}>
                    {/* Numero giorno */}
                    <div style={{fontSize:isMobile?14:16,fontWeight:isHoje?800:700,
                      color:isHoje?COLOR_MDI:isWe?P.red:P.text,
                      fontFamily:"'Syne',sans-serif",marginBottom:2}}>
                      {d}
                    </div>
                    {/* Chip allievi (solo non-weekend) */}
                    {!isWe && (
                      <div style={{display:"flex",flexDirection:"column",gap:3,alignItems:"stretch"}}>
                        {mdiList.map(a=>{
                          const presente = !!(presMese[a.id]||{})[d];
                          return (
                            <div key={a.id}
                              onClick={()=>togglePresenzaMDI(a.id, d)}
                              style={{
                                fontSize:11,fontWeight:600,padding:"2px 4px",borderRadius:4,
                                cursor:"pointer",userSelect:"none",textAlign:"center",
                                background: presente?"rgba(251,146,60,0.25)":P.card2,
                                color: presente?COLOR_MDI:P.muted,
                                border:`1px solid ${presente?COLOR_MDI+"55":P.border}`,
                                transition:"all .12s",
                                whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
                              }}>
                              {a.nome.split(" ")[0]}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Riepilogo presenze per allievo */}
        {mdiList.length > 0 && (
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${P.border}`,
              fontSize:14,fontWeight:600,color:P.secondary,letterSpacing:1,textTransform:"uppercase"}}>
              Riepilogo {meseLabelCap}
            </div>
            {mdiList.map((a,i)=>{
              const totP = Object.values(presMese[a.id]||{}).filter(Boolean).length;
              return (
                <div key={a.id} style={{display:"flex",alignItems:"center",gap:12,
                  padding:"10px 20px",borderBottom:i<mdiList.length-1?`1px solid ${P.border}`:"none"}}>
                  <div style={{flex:1,fontSize:15,fontWeight:500,color:P.text}}>{a.nome}</div>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:16,fontWeight:600,
                    color:totP>0?COLOR_MDI:P.muted}}>
                    {totP} {totP===1?"presenza":"presenze"}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {mdiList.length === 0 && (
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"32px",textAlign:"center",color:P.muted,fontSize:15}}>
            Nessun allievo iscritto a un corso multi-maestro.
          </div>
        )}
      </div>
    );
  };


  // ── DATABASE ALLIEVI ─────────────────────────────────────────────────────────
  // ── DATABASE ALLIEVI — componente esterno (src/components/DatabaseAllievi.jsx) ─
  // Usato come: <DatabaseAllievi allievi={allievi} setAllievi={setAllievi}
  //   maestriColors={maestriColors} isMobile={isMobile}
  //   getRettaPagata={getRettaPagata} addAllievo={addAllievo}
  //   setConfirmDelete={setConfirmDelete} setPresAllievoFrom={setPresAllievoFrom}
  //   setPresAllievo={setPresAllievo} goTab={goTab} notify={notify}/>

  // ── DATABASE MAESTRI ─────────────────────────────────────────────────────────
  const DatabaseMaestri = () => {
    const [dbSearch, setDbSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [dbForm, setDbForm] = useState({nome:'',cognome:'',telefono:'',email:'',corsi:'',note:'',color:'#E9622A'});
    const [saving, setSaving] = useState(false);
    const [mostraArchiviati, setMostraArchiviati] = useState(false);
    const [conferma, setConferma] = useState(null); // {tipo:'archivia'|'elimina', m}

    const f = (k) => (e) => setDbForm(p => ({...p, [k]: e.target.value}));

    const openAdd = () => {
      setEditTarget(null);
      setDbForm({nome:'',cognome:'',telefono:'',email:'',corsi:'',note:'',color:'#E9622A'});
      setShowModal(true);
    };
    const openEdit = (m) => {
      setEditTarget(m);
      setDbForm({
        nome: m.nome||'', cognome: m.cognome||'',
        telefono: m.telefono||'', email: m.email||'',
        corsi: (m.corsi||[]).join(', '),
        note: m.note||'',
        color: m.color||'#E9622A',
      });
      setShowModal(true);
    };

    // Nome completo come lo usano allievi, calendario e stipendi ("NICCOLÒ BOZZAOTRE")
    const nomeCompletoM = (m) => `${m.nome||''} ${m.cognome||''}`.trim().toUpperCase();
    // Se in modifica cambia il nome: dove verrà aggiornato anche (per l'avviso nella finestra)
    const rinominaInfo = (() => {
      if (!editTarget) return null;
      const da = nomeCompletoM(editTarget), a = nomeCompletoM(dbForm);
      if (!a || da === a) return null;
      const allieviN = allieviAll.filter(x => (x.maestro||'').trim().toUpperCase() === da ||
        getAllievoCorsi(x).some(c => (c.maestro||'').trim().toUpperCase() === da)).length;
      const lezioniN = [...calRecurring, ...calSingle].filter(e => (e.maestro||'').trim().toUpperCase() === da).length;
      return { da, a, allieviN, lezioniN };
    })();

    const save = async () => {
      setSaving(true);
      const payload = {
        nome: dbForm.nome.trim(),
        cognome: dbForm.cognome.trim(),
        telefono: dbForm.telefono.trim(),
        email: dbForm.email.trim(),
        corsi: dbForm.corsi.split(',').map(s=>s.trim()).filter(Boolean),
        note: dbForm.note.trim(),
        color: dbForm.color,
      };
      try {
        if (editTarget) {
          // Nome cambiato: il DB lo rinomina in un colpo solo anche su allievi, calendario,
          // corsi, stipendi e account. Prima cambiava solo qui e il maestro "perdeva" gli allievi.
          if (rinominaInfo) {
            const { error: eR } = await supabase.rpc('rinomina_maestro',
              { p_id: editTarget.id, p_nome: payload.nome, p_cognome: payload.cognome });
            if (eR) throw eR;
          }
          const { error } = await supabase.from('maestri').update(payload).eq('id', editTarget.id);
          if (error) throw error;
          setMaestriDB(p => p.map(m => m.id === editTarget.id ? {...m, ...payload} : m));
          if (rinominaInfo) { setShowModal(false); await loadFromDB(userRole); }
        } else {
          const { data, error } = await supabase.from('maestri').insert(payload).select().single();
          if (error) throw error;
          setMaestriDB(p => [...p, data]);
        }
        setShowModal(false);
        notify('Salvato ✓', 'ok');
      } catch(err) {
        notify('Errore salvataggio: ' + err.message, 'err');
      } finally {
        setSaving(false);
      }
    };

    // Archiviare nasconde il maestro da filtri, tendine ed elenchi (vedi mkeysVisibili):
    // nessun dato viene toccato e si ripristina con un tocco
    const setArchiviato = async (m, valore) => {
      const patch = { archiviato: valore, archiviato_il: valore ? oggiISO() : null };
      const { error } = await supabase.from('maestri').update(patch).eq('id', m.id);
      if (error) {
        notify(error.code === '42703' || error.code === 'PGRST204'
          ? "Manca la colonna 'archiviato' sul database: esegui lo script SQL su Supabase."
          : 'Errore: ' + error.message, 'err');
        return;
      }
      setMaestriDB(p => p.map(x => x.id === m.id ? {...x, ...patch} : x));
      notify(`${m.nome} ${m.cognome} ${valore ? 'archiviato' : 'ripristinato'}`, 'ok');
    };

    // Eliminabile solo un maestro senza storico: cancellarlo perderebbe colore e quota,
    // che servono agli stipendi dei mesi passati
    const nomeCompleto = (m) => `${m.nome||''} ${m.cognome||''}`.trim().toUpperCase();
    const haStoricoM = (m) => {
      const n = nomeCompleto(m);
      const stesso = (x) => (x||'').trim().toUpperCase() === n;
      return allieviAll.some(a => stesso(a.maestro) || getAllievoCorsi(a).some(c => stesso(c.maestro)))
        || [...calRecurring, ...calSingle].some(e => stesso(e.maestro))
        || corsi.some(c => stesso(c.maestro) || (c.maestri||[]).some(x => stesso(x.nome)));
    };
    const deleteMaestro = async (m) => {
      const { error } = await supabase.from('maestri').delete().eq('id', m.id);
      if (error) { notify('Errore: ' + error.message, 'err'); return; }
      setMaestriDB(p => p.filter(x => x.id !== m.id));
      notify('Maestro eliminato', 'ok');
    };

    const nArchiviati = maestriDB.filter(m => m.archiviato).length;
    const filtrati = maestriDB
      .filter(m => mostraArchiviati ? m.archiviato : !m.archiviato)
      .filter(m => `${m.nome} ${m.cognome} ${(m.corsi||[]).join(' ')}`.toLowerCase().includes(dbSearch.toLowerCase()));
    const fmtDataM = (iso) => { const [y,mm,d] = String(iso||'').split('-'); return d ? `${d}/${mm}/${y}` : ''; };

    return (
      <div style={{animation:'fi .35s ease'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:26,flexWrap:'wrap',gap:12}}>
          <div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.muted,marginBottom:7,textTransform:'uppercase'}}>ANAGRAFICA</div>
            <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5}}>Database Maestri</h1>
          </div>
          <button onClick={openAdd} style={{background:P.gold,color:'#fff',border:'none',padding:'10px 20px',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:15}}>+ Aggiungi</button>
        </div>

        <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',marginBottom:16}}>
          <input placeholder="Cerca per nome o corso..." value={dbSearch} onChange={e=>setDbSearch(e.target.value)}
            style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,padding:'9px 15px',borderRadius:8,width:'100%',maxWidth:320,fontSize:15,fontFamily:"'Syne',sans-serif"}}/>
          {(nArchiviati > 0 || mostraArchiviati) && (
            <button onClick={()=>setMostraArchiviati(v=>!v)}
              style={{padding:'9px 14px',borderRadius:8,cursor:'pointer',fontSize:14,fontFamily:"'Syne',sans-serif",
                border:`1px solid ${mostraArchiviati?P.gold:P.border}`,background:mostraArchiviati?`${P.gold}18`:'transparent',
                color:mostraArchiviati?P.gold:P.secondary}}>
              {mostraArchiviati ? '← Torna agli attivi' : `Mostra archiviati (${nArchiviati})`}
            </button>
          )}
        </div>
        {mostraArchiviati && (
          <div style={{fontSize:14,color:P.secondary,marginBottom:12,lineHeight:1.5}}>
            I maestri archiviati non compaiono in filtri, tendine ed elenchi, tranne nei mesi in cui hanno
            comunque allievi. Lezioni, stipendi e storico restano intatti.
          </div>
        )}

        {filtrati.length === 0 ? (
          <div style={{textAlign:'center',padding:'48px 0',color:P.muted,fontSize:16}}>
            {mostraArchiviati ? 'Nessun maestro archiviato.' : 'Nessun maestro nel database. Clicca "+ Aggiungi" per iniziare.'}
          </div>
        ) : (
          <div style={{display:'grid',gap:12}}>
            {filtrati.map(m => {
              const nAllievi = allievi.filter(a => getAllievoCorsi(a).some(c => c.maestro === m.nome.toUpperCase() || c.maestro === `${m.nome} ${m.cognome}`.toUpperCase())).length;
              const stipendio = stipendioMaestro(`${m.nome} ${m.cognome}`.trim().toUpperCase(), meseAllievi);
              const c = m.color || P.gold;
              return (
                <div key={m.id} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:12,padding:'18px 20px',borderLeft:`3px solid ${c}`}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:"'Syne',sans-serif",fontSize:17,fontWeight:700,color:m.archiviato?P.muted:c}}>{m.nome} {m.cognome}</div>
                      {m.archiviato ? (
                        <div style={{fontSize:13,color:P.muted,marginTop:3}}>Archiviato{m.archiviato_il ? ` il ${fmtDataM(m.archiviato_il)}` : ''}</div>
                      ) : nAllievi === 0 && (
                        <div style={{display:'inline-block',fontSize:12,marginTop:5,padding:'2px 8px',borderRadius:20,
                          background:`${P.gold}14`,color:P.gold,border:`1px solid ${P.gold}40`}}>Nessun allievo questo mese</div>
                      )}
                      <div style={{fontSize:14,color:P.muted,marginTop:3}}>
                        {m.telefono && <span style={{marginRight:12}}>📞 {m.telefono}</span>}
                        {m.email && <span>✉ {m.email}</span>}
                      </div>
                      {(m.corsi||[]).length > 0 && (
                        <div style={{marginTop:8,display:'flex',gap:6,flexWrap:'wrap'}}>
                          {m.corsi.map(corso => (
                            <span key={corso} style={{fontSize:12,padding:'2px 8px',borderRadius:20,background:`${c}18`,color:c,border:`1px solid ${c}30`}}>{corso}</span>
                          ))}
                        </div>
                      )}
                      {m.note && <div style={{fontSize:14,color:P.muted,marginTop:8,fontStyle:'italic'}}>{m.note}</div>}
                    </div>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6}}>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:22,fontWeight:600,color:P.green}}>€{stipendio}</div>
                        <div style={{fontSize:11,color:P.muted,letterSpacing:1,textTransform:'uppercase'}}>Stipendio {mesiInfo(meseAllievi).label}</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:16,fontWeight:600,color:P.blue}}>{nAllievi} allievi</div>
                      </div>
                      <button
                        onClick={async () => {
                          const nuovoVal = !m.is_solfeggio;
                          setMaestriDB(p => p.map(x => x.id === m.id ? {...x, is_solfeggio: nuovoVal} : x));
                          await supabase.from('maestri').update({is_solfeggio: nuovoVal}).eq('id', m.id);
                        }}
                        style={{
                          padding:'5px 12px', borderRadius:20, border:'none', cursor:'pointer',
                          fontSize:13, fontWeight:600, fontFamily:"'Syne',sans-serif",
                          background: m.is_solfeggio ? 'rgba(167,139,250,0.2)' : P.card2,
                          color: m.is_solfeggio ? '#a78bfa' : P.muted,
                          outline: `1px solid ${m.is_solfeggio ? '#a78bfa44' : P.border}`,
                        }}>
                        {m.is_solfeggio ? '♪ Ins. Solfeggio' : 'Solfeggio: No'}
                      </button>
                      <div style={{display:'flex',gap:6,marginTop:4,flexWrap:'wrap',justifyContent:'flex-end'}}>
                        <button onClick={()=>openEdit(m)} style={{padding:'6px 12px',borderRadius:6,border:`1px solid ${P.border}`,background:'transparent',color:P.secondary,cursor:'pointer',fontSize:13}}>✏ Modifica</button>
                        {m.archiviato ? (<>
                          <button onClick={()=>setArchiviato(m,false)} style={{padding:'6px 12px',borderRadius:6,border:`1px solid ${P.green}40`,background:`${P.green}12`,color:P.green,cursor:'pointer',fontSize:13,whiteSpace:'nowrap'}}>↩ Ripristina</button>
                          {!haStoricoM(m) && (
                            <button onClick={()=>setConferma({tipo:'elimina',m})} style={{padding:'6px 12px',borderRadius:6,border:`1px solid ${P.red}30`,background:`${P.red}12`,color:P.red,cursor:'pointer',fontSize:13}}>Elimina</button>
                          )}
                        </>) : (
                          <button onClick={()=>setConferma({tipo:'archivia',m})} style={{padding:'6px 12px',borderRadius:6,border:`1px solid ${P.red}30`,background:`${P.red}12`,color:P.red,cursor:'pointer',fontSize:13}}>Archivia</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {conferma && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.78)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}
            onClick={e=>{if(e.target===e.currentTarget)setConferma(null);}}>
            <div style={{background:P.card2,border:'1px solid rgba(180,60,60,0.35)',borderRadius:18,padding:28,width:440,maxWidth:'94vw'}}>
              <div style={{fontFamily:"'Syne',sans-serif",fontSize:20,fontWeight:700,marginBottom:8}}>
                {conferma.tipo==='archivia' ? 'Archivia' : 'Elimina'} {conferma.m.nome} {conferma.m.cognome}
              </div>
              <p style={{fontSize:15,color:P.secondary,marginBottom:20,lineHeight:1.6}}>
                {conferma.tipo==='archivia'
                  ? <>Non comparirà più in filtri, tendine ed elenchi, tranne nei mesi in cui ha comunque allievi.
                     Lezioni, stipendi e storico restano intatti. Lo ritrovi con “Mostra archiviati”.</>
                  : <>Non ha allievi, lezioni né corsi registrati, quindi i conti non cambiano.
                     Il maestro viene cancellato dal database e non si può recuperare.</>}
              </p>
              <div style={{display:'flex',gap:10}}>
                <button onClick={()=>setConferma(null)}
                  style={{flex:1,padding:11,background:'transparent',border:`1px solid ${P.border}`,color:P.secondary,
                    borderRadius:8,cursor:'pointer',fontSize:15,fontFamily:"'Syne',sans-serif"}}>Annulla</button>
                <button onClick={()=>{ const {tipo,m}=conferma; setConferma(null); tipo==='archivia' ? setArchiviato(m,true) : deleteMaestro(m); }}
                  style={{flex:1,padding:11,background:`${P.red}20`,border:`1px solid ${P.red}60`,color:P.red,
                    borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:15,fontFamily:"'Syne',sans-serif"}}>
                  {conferma.tipo==='archivia' ? 'Archivia' : 'Elimina definitivamente'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}
            onClick={e=>{if(e.target===e.currentTarget)setShowModal(false);}}>
            <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:16,padding:28,width:420,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto'}}>
              <div style={{fontSize:17,fontWeight:600,marginBottom:18,color:P.text}}>{editTarget?'Modifica maestro':'Nuovo maestro'}</div>
              {[
                {k:'nome',l:'Nome',ph:'Mario'},
                {k:'cognome',l:'Cognome',ph:'Rossi'},
                {k:'telefono',l:'Telefono',ph:'+39 333 ...'},
                {k:'email',l:'Email',ph:'mario@email.com'},
                {k:'corsi',l:'Corsi (separati da virgola)',ph:'Chitarra, Pianoforte'},
                {k:'note',l:'Note',ph:'...'},
              ].map(({k,l,ph=''})=>(
                <div key={k} style={{marginBottom:12}}>
                  <label style={{fontSize:12,color:P.muted,display:'block',marginBottom:4,letterSpacing:1,textTransform:'uppercase'}}>{l}</label>
                  <input type="text" value={dbForm[k]||''} onChange={f(k)} placeholder={ph}
                    style={{width:'100%',background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,padding:'8px 10px',color:P.text,fontSize:15}}/>
                  {k==='cognome' && rinominaInfo && (
                    <div style={{marginTop:8,padding:'8px 10px',borderRadius:8,background:P.goldGlow,
                      border:`1px solid ${P.goldBorder}`,fontSize:14,color:P.text,lineHeight:1.45}}>
                      Il nome diventa <b>{rinominaInfo.a}</b> anche su {rinominaInfo.allieviN} allievi,
                      {' '}{rinominaInfo.lezioniN} lezioni del calendario, corsi e stipendi.
                    </div>
                  )}
                </div>
              ))}
              <div style={{marginBottom:16}}>
                <label style={{fontSize:12,color:P.muted,display:'block',marginBottom:4,letterSpacing:1,textTransform:'uppercase'}}>Colore</label>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {['#E9622A','#fb923c','#facc15','#86efac','#34d399','#22d3ee','#60a5fa','#6366f1','#a78bfa','#e879f9','#f472b6','#f87171','#c084fc','#818cf8','#38bdf8','#2dd4bf','#4ade80','#a3e635','#fbbf24','#f97316','#ef4444','#94a3b8','#64748b','#cbd5e1'].map(col=>(
                    <button key={col} onClick={()=>setDbForm(p=>({...p,color:col}))} style={{width:24,height:24,borderRadius:'50%',background:col,border:dbForm.color===col?`3px solid ${P.text}`:'3px solid transparent',cursor:'pointer',padding:0}}/>
                  ))}
                </div>
              </div>
              <div style={{display:'flex',gap:8,marginTop:18}}>
                <button onClick={()=>setShowModal(false)} style={{flex:1,padding:'9px',borderRadius:8,border:`1px solid ${P.border}`,background:'transparent',color:P.muted,cursor:'pointer',fontSize:15}}>Annulla</button>
                <button onClick={save} disabled={saving} style={{flex:1,padding:'9px',borderRadius:8,border:'none',background:P.gold,color:'#fff',cursor:'pointer',fontWeight:600,fontSize:15}}>{saving?'Salvataggio...':'Salva'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── MAESTRI ──────────────────────────────────────────────────────────────────
  const Maestri = () => {
    // Calcola lordo, stipendio e netto per ogni maestro
    const calcoloMaestro = (m) => {
      const suoi = allievi.filter(a => getAllievoCorsi(a).some(c=>c.maestro===m));
      // tutti chi ha pagato, inclusi speciali (importo già 0 se specNoRetta)
      const paganti = suoi.filter(a => getRettaPagata(a.id));
      const lordo = paganti.reduce((s,a) => {
        const importo = getImportoAllievoMese(a, meseAllievi);
        if (importo === 0) return s;
        // proporziona l'importo custom al peso dei corsi di questo maestro
        const corsiA = getAllievoCorsi(a);
        const corsiMaestro = corsiA.filter(c=>c.maestro===m);
        const totCorsi = corsiA.reduce((t,c)=>t+c.retta, 0);
        if (a.specPrezzoCustom != null && totCorsi > 0) {
          const quota = corsiMaestro.reduce((t,c)=>t+c.retta, 0);
          return s + Math.round(importo * quota / totCorsi);
        }
        return s + corsiMaestro.reduce((t,c)=>t+c.retta, 0);
      }, 0);
      const stipendio = paganti.reduce((s,a) => {
        if (getImportoAllievoMese(a, meseAllievi) === 0) return s;
        return s + getAllievoCorsi(a).filter(c=>c.maestro===m).reduce((ss,c)=>{
          const qm = c.quota_m!=null ? c.quota_m : Math.round(c.retta*PERC_MAESTRO);
          return ss+qm;
        },0);
      }, 0);
      const netto = lordo - stipendio;
      return {lordo, stipendio, netto, totAllievi: suoi.length};
    };

    const totLordo      = mkeys.reduce((s,m)=>s+calcoloMaestro(m).lordo,0);
    const totStipDovuto = mkeys.filter(m=>!(stipendiPagati[meseAllievi]?.[m])).reduce((s,m)=>s+calcoloMaestro(m).stipendio,0);
    const totStipPagato = mkeys.filter(m=>!!(stipendiPagati[meseAllievi]?.[m])).reduce((s,m)=>s+calcoloMaestro(m).stipendio,0);

    return (
    <div style={{animation:"fi .35s ease"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:26}}>
        <div>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.muted,marginBottom:7,fontWeight:400,textTransform:"uppercase"}}>ORGANICO</div>
          <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5}}>Maestri</h1>
        </div>
        <button onClick={()=>setModal("maestro")} style={{background:P.gold,color:"#fff",border:"none",
          padding:"10px 20px",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:15,fontFamily:"'Figtree',sans-serif"}}>
          + Aggiungi Maestro
        </button>
      </div>

      {/* Riquadri sommario stipendi */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:14,marginBottom:28}}>
        {[
          {l:"Lordo Incassato",    v:`€${totLordo}`,                        c:P.gold},
          {l:"Stipendi da pagare", v:`€${totStipDovuto}`,                   c:P.red},
          {l:"Stipendi pagati",    v:`€${totStipPagato}`,                   c:P.green},
          {l:"Totale Stipendi",    v:`€${totStipDovuto+totStipPagato}`,     c:P.purple},
        ].map(s=>(
          <div key={s.l} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:12,padding:"16px 20px"}}>
            <div style={{fontSize:12,color:P.muted,marginBottom:6,letterSpacing:.5}}>{s.l}</div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:22,fontWeight:500,color:s.c}}>{s.v}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
        {mkeysVisibili().map(m=>{
          const c=mc(m,maestriColors);
          const lista=allievi.filter(a=>a.maestro===m);
          const {lordo, stipendio, netto} = calcoloMaestro(m);
          const pagato = !!(stipendiPagati[meseAllievi]?.[m]);
          return (
            <div key={m} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,
              padding:22,borderTop:`3px solid ${c}`}}>

              {/* Header maestro */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",gap:11}}>
                  <Avatar nome={m} color={c} size={42}/>
                  <div>
                    <div style={{fontWeight:600,fontSize:16,color:c}}>{m}</div>
                    <div style={{fontSize:12,color:P.muted,marginTop:2}}>{lista.length} allievi</div>
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:7}}>
                  {/* Pulsante Pagato / Da pagare */}
                  <button
                    onClick={()=>{saveHistory({tipo:'stipendio',m,mese:meseAllievi,prima:!!stipendiPagati[meseAllievi]?.[m]});const nv=!(stipendiPagati[meseAllievi]?.[m]);setStipendi(p=>({...p,[meseAllievi]:{...(p[meseAllievi]||{}),[m]:nv}}));if(dbReady)saveStipendioToDB(m,nv,meseAllievi);}}
                    style={{
                      padding:"7px 14px", borderRadius:20, border:"none",
                      cursor:"pointer", fontSize:13, fontWeight:600,
                      fontFamily:"'Syne',sans-serif", transition:"all .2s",
                      background: pagato ? "rgba(74,222,128,.2)" : "rgba(248,113,113,.15)",
                      color: pagato ? P.green : P.red,
                      outline: `1px solid ${pagato ? P.green+"44" : P.red+"44"}`,
                    }}>
                    {pagato ? "✓ Pagato" : "Da pagare"}
                  </button>
                  {/* Toggle insegnante solfeggio */}
                  {(()=>{
                    const rec = maestriDB.find(r=>{
                      const full = `${r.nome||''} ${r.cognome||''}`.trim().toUpperCase();
                      const short = (r.nome||'').trim().toUpperCase();
                      return full===m || short===m;
                    });
                    const isSolf = rec?.is_solfeggio||false;
                    return (
                      <button
                        onClick={async()=>{
                          if(!rec) return;
                          const nv = !rec.is_solfeggio;
                          setMaestriDB(prev=>prev.map(r=>r.id===rec.id?{...r,is_solfeggio:nv}:r));
                          if(dbReady) await supabase.from('maestri').update({is_solfeggio:nv}).eq('id',rec.id);
                        }}
                        style={{
                          padding:"4px 10px", borderRadius:12, border:"none",
                          cursor:"pointer", fontSize:12, fontWeight:600,
                          background: isSolf?"rgba(139,92,246,0.2)":"rgba(80,80,120,0.1)",
                          color: isSolf?"#8b5cf6":P.muted,
                          outline: `1px solid ${isSolf?"rgba(139,92,246,0.4)":"transparent"}`,
                        }}>
                        ♪ {isSolf?"Ins. Solfeggio":"Solfeggio: No"}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* Riepilogo economico */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
                <div style={{background:P.bg,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:11,color:P.muted,letterSpacing:1,marginBottom:3}}>LORDO</div>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:18,fontWeight:500,color:P.gold}}>€{lordo}</div>
                </div>
                <div style={{background:P.bg,borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:11,color:P.muted,letterSpacing:1,marginBottom:3}}>STIPENDIO</div>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:18,fontWeight:500,color:P.red}}>€{stipendio}</div>
                </div>
                <div style={{background:P.card2,borderRadius:8,padding:"10px 12px",
                  border:`1px solid ${P.borderSubtle}`}}>
                  <div style={{fontSize:11,color:P.muted,letterSpacing:1,marginBottom:3}}>NETTO</div>
                  <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:18,fontWeight:500,
                    color: netto>0?P.green:P.muted}}>€{netto}</div>
                </div>
              </div>

              {/* Chips allievi */}
              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                {lista.map(a=>(
                  <span key={a.id} style={{fontSize:12,padding:"2px 8px",borderRadius:10,
                    background: getRettaPagata(a.id)?`${c}20`:`${c}08`,
                    color: getRettaPagata(a.id)?c:`${c}88`,
                    border:`1px solid ${getRettaPagata(a.id)?c+"44":c+"18"}`}}>
                    {a.nome.split(" ")[0]}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    );
  };

  // ── SOLFEGGIO SAVE HELPERS ───────────────────────────────────────────────────
  const saveSolfeggioOre = async (mese, ore) => {
    const n = Number(ore);
    const stipendio = Math.round(n * NICOL_TARIFFA * 100) / 100;
    setSolfeggioMesi(prev => prev.map(m => m.mese===mese ? {...m, ore:n, stipendio} : m));
    if (dbReady) await supabase.from('solfeggio_ore').upsert({mese, ore:n});
  };
  const toggleSolfeggioMese = async (mese) => {
    setSolfeggioMesi(prev => prev.map(m => m.mese===mese ? {...m, pagato:!m.pagato} : m));
    const row = solfeggioMesi.find(m=>m.mese===mese);
    if (row && dbReady) await supabase.from('solfeggio_ore').upsert({mese, pagato:!row.pagato});
  };

  const saveSolfeggioLezione = async (dateStr, ore) => {
    const n = Number(ore);
    setSolfeggioLezioni(prev => ({ ...prev, [dateStr]: { ...(prev[dateStr]||{}), ore: n } }));
    if (dbReady) await supabase.from('solfeggio_lezioni').upsert({ data: dateStr, ore_lezione: n }, { onConflict: 'data' });
  };

  const toggleSolfeggioConfermato = async (dateStr) => {
    const cur = solfeggioLezioni[dateStr]?.confermato || false;
    const oreCorrente = solfeggioLezioni[dateStr]?.ore ?? 2.5;
    const nuovoVal = !cur;
    setSolfeggioLezioni(prev => ({ ...prev, [dateStr]: { ...(prev[dateStr]||{}), ore: oreCorrente, confermato: nuovoVal } }));
    if (dbReady) {
      // Prima prova ad aggiornare la riga esistente
      const { data: existing } = await supabase.from('solfeggio_lezioni').select('id').eq('data', dateStr).maybeSingle();
      let error;
      if (existing) {
        ({ error } = await supabase.from('solfeggio_lezioni').update({ confermato: nuovoVal }).eq('data', dateStr));
      } else {
        ({ error } = await supabase.from('solfeggio_lezioni').insert({ data: dateStr, ore_lezione: oreCorrente, confermato: nuovoVal, presenze: [] }));
      }
      if (error) console.error('toggleSolfeggioConfermato error:', error);
    }
  };

  const toggleSolfeggioPresenza = async (dateStr, allId) => {
    const cur = (solfeggioPresenze[dateStr] || {})[allId];
    // Ciclo: null → 'P' → 'A' → null
    const next = cur === 'P' ? 'A' : cur === 'A' ? null : 'P';
    setSolfeggioPresenze(prev => {
      const dayMap = { ...(prev[dateStr]||{}) };
      if (next === null) delete dayMap[allId]; else dayMap[allId] = next;
      return { ...prev, [dateStr]: dayMap };
    });
    if (dbReady) {
      // Una riga per data/allievo (vincolo UNIQUE già presente): si aggiorna, non si reinserisce
      const { error } = next !== null
        ? await supabase.from('solfeggio_presenze')
            .upsert({ data: dateStr, allievo_id: allId, stato: next }, { onConflict: 'data,allievo_id' })
        : await supabase.from('solfeggio_presenze').delete()
            .eq('data', dateStr).eq('allievo_id', allId);
      if (error) notify('Presenza solfeggio non salvata: ' + error.message, 'err');
    }
  };

  // Sposta una lezione da oldDs a newDs (stessa settimana, giorno diverso).
  // Prima si scrive il giorno nuovo e solo dopo si toglie il vecchio: prima la riga vecchia
  // veniva cancellata e la nuova non si salvava (colonna "ore" inesistente), così la lezione
  // tornava al lunedì e perdeva ore, conferma e presenze.
  const cambiaGiornoLezione = async (oldDs, newDs) => {
    if (oldDs === newDs) return;
    const oldPres = solfeggioPresenze[oldDs];
    const lez = { ore: solfeggioLezioni[oldDs]?.ore ?? 2.5, confermato: solfeggioLezioni[oldDs]?.confermato || false };
    setSolfeggioLezioni(prev => {
      const next = { ...prev };
      delete next[oldDs];
      next[newDs] = lez;
      return next;
    });
    setSolfeggioPresenze(prev => {
      const next = { ...prev };
      delete next[oldDs];
      if (oldPres) next[newDs] = oldPres;
      return next;
    });
    if (!dbReady) return;
    const errore = (e) => notify('Lezione non spostata: ' + e.message, 'err');

    // Lezione nel giorno nuovo (la tabella non ha un vincolo sulla data: si aggiorna se c'è già)
    const { data: esiste, error: e0 } = await supabase.from('solfeggio_lezioni').select('id').eq('data', newDs).maybeSingle();
    if (e0) return errore(e0);
    const riga = { data: newDs, ore_lezione: lez.ore, confermato: lez.confermato };
    const { error: e1 } = esiste
      ? await supabase.from('solfeggio_lezioni').update(riga).eq('id', esiste.id)
      : await supabase.from('solfeggio_lezioni').insert({ ...riga, presenze: [] });
    if (e1) return errore(e1);

    const rows = Object.entries(oldPres || {}).map(([allId, stato]) => ({ data: newDs, allievo_id: Number(allId), stato }));
    if (rows.length > 0) {
      const { error: e2 } = await supabase.from('solfeggio_presenze').upsert(rows, { onConflict: 'data,allievo_id' });
      if (e2) return errore(e2);
    }

    // Solo ora si toglie il giorno vecchio
    const { error: e3 } = await supabase.from('solfeggio_lezioni').delete().eq('data', oldDs);
    const { error: e4 } = await supabase.from('solfeggio_presenze').delete().eq('data', oldDs);
    if (e3 || e4) errore(e3 || e4);
  };

  // ── SOLFEGGIO ────────────────────────────────────────────────────────────────
  const Solfeggio = () => {
    const TARIFFA = NICOL_TARIFFA;
    const [meseSolf, setMeseSolf] = useState(getMeseCorrenteReale);

    // Calcola info mese (anno, mIdx, n.giorni)
    const getMeseInfo = (mese) => {
      const [nomeMese, annoStr] = mese.split('-');
      const anno = parseInt(annoStr, 10);
      const mIdx = NOMI_MESI_IT.indexOf(nomeMese);
      const days = new Date(anno, mIdx + 1, 0).getDate();
      return { anno, mIdx, days };
    };
    const { anno, mIdx, days } = getMeseInfo(meseSolf);
    const meseLabelCap = meseSolf.split('-')[0].charAt(0).toUpperCase() + meseSolf.split('-')[0].slice(1);

    const GIORNI_IT = ['Domenica','Lunedì','Martedì','Mercoledì','Giovedì','Venerdì','Sabato'];

    // Data YYYY-MM-DD da numero giorno
    const toDateStr = (d) => `${anno}-${String(mIdx+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

    // Lunedì del mese → una "settimana" per ciascuno
    const settimane = [];
    for (let d = 1; d <= days; d++) {
      if (new Date(anno, mIdx, d).getDay() === 1) {
        // Giorni lun-dom entro il mese
        const giorni = [];
        for (let i = 0; i < 7; i++) {
          const dd = d + i;
          if (dd >= 1 && dd <= days) giorni.push(dd);
        }
        // Cerca se c'è già una lezione registrata in questa settimana
        const lezDs = giorni.map(toDateStr).find(ds => solfeggioLezioni[ds] !== undefined);
        const dataLezione = lezDs || toDateStr(d); // default lunedì
        settimane.push({ monDay: d, giorni, dataLezione });
      }
    }

    // Allievi iscritti al solfeggio
    const solfAllievi = allievi.filter(a => a.solfeggio);

    // Calcola costo totale del mese (solo giorni confermati)
    const oreTotMese = settimane.reduce((s, { dataLezione }) => {
      const conf = solfeggioLezioni[dataLezione]?.confermato || false;
      return s + (conf ? Number(solfeggioLezioni[dataLezione]?.ore ?? 0) : 0);
    }, 0);
    const costoArrotondato = Math.round(oreTotMese * TARIFFA * 100) / 100;

    // Oggi
    const oggi = new Date();
    const isOggiSolf = (d) => anno === oggi.getFullYear() && mIdx === oggi.getMonth() && d === oggi.getDate();

    // Nav mese
    const NavMeseSolf = () => (
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <button onClick={()=>setMeseSolf(meseAdd(meseSolf,-1))}
          style={{background:"transparent",border:`1px solid ${P.border}`,color:P.text,
            padding:"5px 12px",borderRadius:7,cursor:"pointer",fontSize:16}}>←</button>
        <span style={{fontSize:15,fontWeight:600,color:P.text,minWidth:100,textAlign:"center"}}>
          {meseLabelCap} {anno}
        </span>
        <button onClick={()=>setMeseSolf(meseAdd(meseSolf,1))}
          style={{background:"transparent",border:`1px solid ${P.border}`,color:P.text,
            padding:"5px 12px",borderRadius:7,cursor:"pointer",fontSize:16}}>→</button>
      </div>
    );

    return (
      <div style={{animation:"fi .35s ease"}}>

        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:26,flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.secondary,marginBottom:7,fontWeight:400,textTransform:"uppercase"}}>CORSO</div>
            <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5}}>Solfeggio</h1>
          </div>
          <NavMeseSolf/>
        </div>

        {/* Schede riepilogo */}
        <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(3,1fr)",gap:14,marginBottom:24}}>
          {[
            {l:`Costo ${meseLabelCap}`, v:`€${costoArrotondato}`, sub:`${oreTotMese}h × €12,50`, c:"#8b5cf6"},
            {l:"Allievi iscritti",      v:`${solfAllievi.length}`, sub:"con solfeggio attivo",    c:P.blue},
            {l:"Settimane nel mese",    v:`${settimane.length}`,    sub:"lezioni previste",        c:P.gold},
          ].map(s=>(
            <div key={s.l} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:12,padding:"16px 20px"}}>
              <div style={{fontSize:12,color:P.secondary,marginBottom:6,letterSpacing:.5}}>{s.l}</div>
              <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:22,fontWeight:500,color:s.c}}>{s.v}</div>
              <div style={{fontSize:12,color:P.secondary,marginTop:4}}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Calendario settimane */}
        {settimane.length === 0 ? (
          <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:12,
            padding:"32px",textAlign:"center",color:P.secondary,fontSize:15}}>
            Nessun lunedì in questo mese.
          </div>
        ) : (
          <div style={{display:"grid",gap:16}}>
            {settimane.map(({ monDay, giorni, dataLezione }) => {
              const ds = dataLezione;
              const oreLezione = Number(solfeggioLezioni[ds]?.ore ?? 2.5);
              const confermato = solfeggioLezioni[ds]?.confermato || false;
              const costoGiorno = Math.round(oreLezione * TARIFFA * 100) / 100;
              const dayNum = parseInt(ds.split('-')[2], 10);
              const isHoje = isOggiSolf(dayNum);
              return (
                <div key={monDay} style={{background:P.card,border:`1px solid ${isHoje?P.gold:P.border}`,
                  borderRadius:14,overflow:"hidden",
                  boxShadow:isHoje?`0 0 0 1px ${P.gold}55`:"none"}}>

                  {/* Header giorno */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    flexWrap:"wrap",gap:8,
                    padding:"13px 18px",borderBottom:`1px solid ${P.border}`,
                    background:isHoje?"rgba(233,98,42,0.06)":"transparent"}}>

                    {/* Sinistra: dropdown giorno */}
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      {isHoje && <div style={{width:8,height:8,borderRadius:"50%",background:P.gold,flexShrink:0}}/>}
                      <select
                        value={ds}
                        onChange={e => cambiaGiornoLezione(ds, e.target.value)}
                        style={{background:P.card2,border:`1px solid ${P.border}`,
                          color:isHoje?P.gold:P.text,borderRadius:8,padding:"5px 10px",
                          fontSize:15,fontWeight:600,fontFamily:"'Syne',sans-serif",
                          cursor:"pointer",outline:"none"}}>
                        {giorni.map(g => {
                          const gDs = toDateStr(g);
                          const dow = new Date(anno, mIdx, g).getDay();
                          return (
                            <option key={gDs} value={gDs}>
                              {GIORNI_IT[dow]} {g}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Destra: ore + conferma + costo */}
                    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      <span style={{fontSize:12,color:P.secondary}}>Ore lezione:</span>
                      <input
                        type="number" min="0" step="0.5"
                        defaultValue={oreLezione}
                        key={ds+"-"+oreLezione}
                        onBlur={e=>saveSolfeggioLezione(ds, e.target.value)}
                        onKeyDown={e=>e.key==='Enter'&&e.target.blur()}
                        style={{background:P.card2,border:`1px solid ${P.border}`,
                          color:P.text,borderRadius:6,padding:"4px 8px",width:65,
                          fontFamily:"'IBM Plex Mono',monospace",fontSize:15,outline:"none",textAlign:"center"}}
                      />
                      <span
                        onClick={()=>toggleSolfeggioConfermato(ds)}
                        style={{
                          fontSize:13,fontWeight:600,padding:"3px 12px",borderRadius:20,
                          cursor:"pointer",userSelect:"none",
                          background: confermato ? "rgba(91,184,122,0.18)" : "rgba(248,113,113,0.12)",
                          color: confermato ? P.green : P.red,
                          border: `1px solid ${confermato ? P.green+"44" : P.red+"33"}`,
                        }}>
                        {confermato ? "✓ Conteggiato" : "Non conteggiato"}
                      </span>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:14,
                        color:confermato&&costoGiorno>0?"#8b5cf6":P.muted,fontWeight:500}}>
                        {confermato&&costoGiorno>0?`€${costoGiorno}`:"—"}
                      </span>
                    </div>
                  </div>

                  {/* Griglia presenze allievi */}
                  {solfAllievi.length === 0 ? (
                    <div style={{padding:"14px 18px",fontSize:14,color:P.secondary,fontStyle:"italic"}}>
                      Nessun allievo iscritto al solfeggio. Abilita il solfeggio dalla scheda allievo.
                    </div>
                  ) : (
                    <div style={{padding:"10px 14px",display:"flex",flexWrap:"wrap",gap:8}}>
                      {solfAllievi.map(a=>{
                        const stato = (solfeggioPresenze[ds]||{})[a.id];
                        const isP = stato === 'P';
                        const isA = stato === 'A';
                        const bgCol = isP ? "rgba(91,184,122,0.18)" : isA ? "rgba(196,107,107,0.18)" : P.card2;
                        const bdCol = isP ? P.green+"55" : isA ? P.red+"55" : P.border;
                        const txtCol = isP ? P.green : isA ? P.red : P.muted;
                        return (
                          <button key={a.id}
                            onClick={()=>oreLezione>0 ? toggleSolfeggioPresenza(ds,a.id) : null}
                            title={oreLezione<=0?"Inserisci prima le ore di lezione":""}
                            style={{
                              display:"flex",alignItems:"center",gap:6,
                              background:bgCol,border:`1px solid ${bdCol}`,borderRadius:20,
                              padding:"5px 12px",cursor:oreLezione>0?"pointer":"default",
                              transition:"all .15s"
                            }}>
                            <div style={{width:6,height:6,borderRadius:"50%",
                              background:isP?P.green:isA?P.red:P.muted,flexShrink:0}}/>
                            <span style={{fontSize:14,fontWeight:500,color:txtCol}}>
                              {a.nome.split(" ")[0]}
                            </span>
                            <span style={{fontSize:12,color:txtCol,opacity:.8}}>
                              {isP?"P":isA?"A":"?"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Legenda */}
        <div style={{marginTop:20,display:"flex",alignItems:"center",gap:16,fontSize:13,color:P.secondary}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:P.green}}/>
            <span>P = Presente</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:P.red}}/>
            <span>A = Assente</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:P.muted}}/>
            <span>? = Non registrato</span>
          </div>
        </div>
      </div>
    );
  };

  // ── SALA PROVE ───────────────────────────────────────────────────────────────
  // ── MODAL ────────────────────────────────────────────────────────────────────
  const Modal = ({id,title,sub,onSave,children}) => modal!==id ? null : (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.78)",zIndex:50,
      display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setModal(null)}>
      <div style={{background:P.card2,border:`1px solid ${P.border}`,borderRadius:18,padding:32,
        width:440,maxWidth:"94vw",animation:"fi .3s ease",boxShadow:"0 24px 64px rgba(0,0,0,.55)"}} onClick={e=>e.stopPropagation()}>
        <div style={{fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:700,letterSpacing:-0.3,marginBottom:3}}>{title}</div>
        <div style={{fontSize:14,color:P.muted,marginBottom:22}}>{sub}</div>
        {children}
        <div style={{display:"flex",gap:10,marginTop:22}}>
          <button onClick={()=>setModal(null)} style={{flex:1,padding:"11px",background:"transparent",
            border:`1px solid ${P.border}`,color:P.muted,borderRadius:8,cursor:"pointer",fontSize:15}}>Annulla</button>
          <button onClick={onSave} style={{flex:1,padding:"11px",background:P.gold,border:"none",
            color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:15}}>Aggiungi</button>
        </div>
      </div>
    </div>
  );

  // ── GESTIONE UTENTI (solo admin) ─────────────────────────────────────────────
  const GestioneUtenti = () => {
    const [utenti, setUtenti]     = useState([]);
    const [loadingU, setLoadingU] = useState(true);
    const [editId, setEditId]     = useState(null);
    const [editForm, setEditForm] = useState({});

    useEffect(() => {
      supabase.from('utenti').select('*').order('id').then(({ data }) => {
        setUtenti(data || []);
        setLoadingU(false);
      });
    }, []);

    // L'utente esiste già (creato da Supabase → Authentication): si aggiornano solo ruolo e maestro.
    // Prima mandava anche una colonna "uuid" che non esiste e il salvataggio falliva in silenzio.
    const saveUtente = async (u) => {
      const { data, error } = await supabase.from('utenti')
        .update({ ruolo: u.ruolo, maestro_nome: u.maestro_nome || null })
        .eq('id', u.id).select('id');
      if (error || !data?.length) {
        notify('Utente non salvato' + (error ? ': ' + error.message : ''), 'err');
        return;
      }
      setUtenti(prev => prev.map(x => x.id === u.id ? u : x));
      setEditId(null);
      notify('Utente salvato', 'ok');
    };

    const RUOLI = ['admin', 'segreteria', 'maestro'];
    const roleColor = (r) => r==='admin'?P.gold:r==='segreteria'?P.blue:P.purple;

    return (
      <div style={{ animation:'fi .35s ease' }}>
        <div style={{ marginBottom:26 }}>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, letterSpacing:3, color:P.muted, marginBottom:7, textTransform:'uppercase' }}>ADMIN</div>
          <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:isMobile?22:32, fontWeight:800, letterSpacing:-0.5 }}>Gestione Utenti</h1>
          <p style={{ color:P.secondary, fontSize:15, marginTop:4 }}>
            Crea gli utenti in Supabase → Authentication → Users, poi associa qui ruolo e maestro.
          </p>
        </div>

        {loadingU ? (
          <div style={{ color:P.muted, fontSize:15 }}>Caricamento...</div>
        ) : utenti.length === 0 ? (
          <div style={{ background:P.card, border:`1px solid ${P.border}`, borderRadius:12, padding:32, textAlign:'center', color:P.muted, fontSize:15 }}>
            Nessun utente nella tabella. Aggiungi prima l'utente da Supabase → Authentication → Users, poi inserisci il suo UUID qui sotto.
          </div>
        ) : (
          <div style={{ background:P.card, border:`1px solid ${P.border}`, borderRadius:14, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${P.border}` }}>
                  {['Email','Ruolo','Maestro collegato',''].map(h => (
                    <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:11, letterSpacing:2, color:P.muted, fontWeight:600 }}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {utenti.map((u, i) => editId === u.id ? (
                  <tr key={u.id} style={{ borderBottom:i<utenti.length-1?`1px solid ${P.border}`:'none', background:P.card2 }}>
                    <td style={{ padding:'10px 16px', fontSize:15, color:P.muted }}>{u.email}</td>
                    <td style={{ padding:'10px 16px' }}>
                      <select value={editForm.ruolo} onChange={e=>setEditForm(f=>({...f,ruolo:e.target.value}))}
                        style={{ background:P.bg, border:`1px solid ${P.border}`, color:P.text, borderRadius:6, padding:'5px 8px', fontSize:15 }}>
                        {RUOLI.map(r=><option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <input value={editForm.maestro_nome||''} onChange={e=>setEditForm(f=>({...f,maestro_nome:e.target.value}))}
                        placeholder="es. ANTONELLO BUONOCORE"
                        style={{ background:P.bg, border:`1px solid ${P.border}`, color:P.text, borderRadius:6, padding:'5px 8px', fontSize:15, width:'100%' }}/>
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={()=>saveUtente({...u,...editForm})}
                          style={{ background:P.gold, border:'none', color:'#fff', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:14, fontWeight:600 }}>Salva</button>
                        <button onClick={()=>setEditId(null)}
                          style={{ background:'transparent', border:`1px solid ${P.border}`, color:P.muted, borderRadius:6, padding:'5px 10px', cursor:'pointer', fontSize:14 }}>✕</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={u.id} style={{ borderBottom:i<utenti.length-1?`1px solid ${P.border}`:'none' }}>
                    <td style={{ padding:'12px 16px', fontSize:15 }}>{u.email}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ fontSize:13, padding:'2px 10px', borderRadius:20, fontWeight:600,
                        background:`${roleColor(u.ruolo)}22`, color:roleColor(u.ruolo),
                        border:`1px solid ${roleColor(u.ruolo)}44` }}>{u.ruolo || '—'}</span>
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:15, color:P.secondary }}>{u.maestro_nome || <span style={{color:P.muted}}>—</span>}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <button onClick={()=>{ setEditId(u.id); setEditForm({ruolo:u.ruolo||'admin',maestro_nome:u.maestro_nome||''}); }}
                        style={{ background:'transparent', border:`1px solid ${P.border}`, color:P.muted, borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:13 }}>✏ Modifica</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── RENDER ───────────────────────────────────────────────────────────────────
  // Login gate
  if (authLoading) return (
    <div style={{ minHeight:'100vh', background:P.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:P.muted, fontFamily:"'IBM Plex Mono',monospace", fontSize:15, letterSpacing:2 }}>CARICAMENTO...</div>
    </div>
  );
  if (!authUser) return <LoginScreen />;
  if (userRole === 'noaccess') return (
    <div style={{minHeight:'100vh',background:P.bg,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
      <div style={{fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:P.text}}>Accesso non autorizzato</div>
      <div style={{fontSize:15,color:P.muted}}>Il tuo account non è ancora configurato. Contatta l'amministratore.</div>
      <button onClick={handleLogout} style={{marginTop:8,padding:'9px 22px',background:'transparent',border:`1px solid ${P.border}`,color:P.muted,borderRadius:8,cursor:'pointer',fontSize:15}}>Esci</button>
    </div>
  );
  if (userRole === 'sospeso') return (
    <div style={{minHeight:'100vh',background:P.bg,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16,padding:24,textAlign:'center'}}>
      <div style={{fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:P.text}}>Accesso sospeso</div>
      <div style={{fontSize:15,color:P.muted}}>Il tuo profilo maestro è archiviato. Se pensi sia un errore, contatta l'accademia.</div>
      <button onClick={handleLogout} style={{marginTop:8,padding:'9px 22px',background:'transparent',border:`1px solid ${P.border}`,color:P.muted,borderRadius:8,cursor:'pointer',fontSize:15}}>Esci</button>
    </div>
  );
  if (erroreCaricamento) return (
    <div style={{minHeight:'100vh',background:P.bg,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16,padding:24,textAlign:'center'}}>
      <div style={{fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:P.text}}>Dati non caricati</div>
      <div style={{fontSize:15,color:P.muted,maxWidth:420}}>Non riesco a leggere i dati dal database. Controlla la connessione e riprova; se il problema resta, esci e accedi di nuovo.</div>
      <div style={{display:'flex',gap:10,marginTop:8}}>
        <button onClick={() => loadFromDB(userRole)} style={{padding:'9px 22px',background:P.gold,border:'none',color:'#fff',borderRadius:8,cursor:'pointer',fontSize:15,fontWeight:600}}>Riprova</button>
        <button onClick={handleLogout} style={{padding:'9px 22px',background:'transparent',border:`1px solid ${P.border}`,color:P.muted,borderRadius:8,cursor:'pointer',fontSize:15}}>Esci</button>
      </div>
    </div>
  );

  return (
    <>
      {loading && (
        <div style={{position:"fixed",inset:0,background:P.bg,zIndex:999,
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:32,fontWeight:800,
            letterSpacing:-1,color:P.text}}>Nuove<span style={{color:P.gold}}>Armonie</span></div>
          <div style={{color:P.muted,fontSize:15,fontFamily:"'IBM Plex Mono',monospace",letterSpacing:2}}>CARICAMENTO...</div>
          <div style={{width:36,height:36,border:`2px solid ${P.borderSubtle}`,
            borderTop:`2px solid ${P.gold}`,borderRadius:"50%",
            animation:"spin 1s linear infinite"}}/>
        </div>
      )}
      <style>{FONT+`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:${P.bg};color:${P.text};font-family:'Figtree',sans-serif;}
        ::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-thumb{background:${P.border};}
        button,select,input{font-family:'Figtree',sans-serif;outline:none;}
        input::placeholder{color:${P.muted};}
        @keyframes fi{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes ni{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
        tr{transition:background .12s;} tr:hover .row-actions{opacity:1 !important;}
        .casella-pz{-webkit-user-select:none;user-select:none;-webkit-touch-callout:none;touch-action:manipulation;}
      `}</style>

      {/* MENÙ STATO PRESENZA: accanto alla casella su schermo grande, dal basso su telefono */}
      {menuPresenza && (()=>{
        const {id, nome, day, mese, rect} = menuPresenza;
        const cur = presenze[mese]?.[id]?.[day];
        const [nm, yy] = mese.split('-');
        const data = new Date(parseInt(yy,10), NOMI_MESI_IT.indexOf(nm), day);
        const W = 320, H = 380;
        let top = rect.bottom + 8;
        if(top + H > window.innerHeight) top = rect.top - H - 8;
        if(top < 8) top = Math.max(8, window.innerHeight - H - 8);
        const pos = isMobile
          ? {left:0, right:0, bottom:0, borderRadius:"18px 18px 0 0", paddingBottom:"calc(16px + env(safe-area-inset-bottom))"}
          : {width:W, borderRadius:14, top,
             left:Math.min(Math.max(8, rect.width > W ? rect.right - W : rect.left + rect.width/2 - W/2), window.innerWidth - W - 8)};
        // Un tocco di troppo (es. un terzo tocco veloce) non deve chiudere il menù appena
        // aperto o sceglierne uno stato: per un attimo i tocchi vengono ignorati
        const appenaAperto = () => Date.now() - menuPresenza.t < 400;
        const scegli = (stato) => { if(appenaAperto()) return; setPresenza(id, day, mese, stato); setMenuPresenza(null); };
        return (
          <div onClick={()=>{ if(!appenaAperto()) setMenuPresenza(null); }}
            style={{position:"fixed",inset:0,zIndex:60,background:isMobile?"rgba(0,0,0,.55)":"transparent"}}>
            <div onClick={e=>e.stopPropagation()}
              style={{position:"fixed",...pos,background:P.card2,border:`1px solid ${P.borderStrong}`,
                boxShadow:"0 16px 48px rgba(0,0,0,.55)",padding:14,animation:"fi .15s ease"}}>
              <div style={{fontSize:16,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{nome}</div>
              <div style={{fontSize:14,color:P.secondary,marginBottom:12}}>{GDS[data.getDay()]} {day} {nm}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {STATI_PRESENZA.map(s=>{
                  const ci = cellInfo(s.v);
                  const attuale = cur===s.v || (s.v==="1p"&&cur==="p") || (s.v==="1r"&&cur==="r");
                  return (
                    <button key={s.v} onClick={()=>scegli(s.v)}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,padding:"10px 4px",
                        borderRadius:10,cursor:"pointer",fontFamily:"inherit",color:P.text,
                        background:attuale?ci.bg:"transparent",border:`1px solid ${attuale?ci.color:P.border}`}}>
                      <span style={{minWidth:32,height:28,padding:"0 4px",borderRadius:6,background:ci.bg,
                        border:`1px solid ${ci.border}`,color:ci.color,fontSize:15,fontWeight:700,
                        display:"flex",alignItems:"center",justifyContent:"center"}}>{ci.label}</span>
                      <span style={{fontSize:13,lineHeight:1.2,textAlign:"center",color:attuale?P.text:P.secondary}}>{s.desc}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:8,marginTop:10}}>
                {cur && (
                  <button onClick={()=>scegli(null)}
                    style={{flex:1,padding:12,borderRadius:10,cursor:"pointer",fontFamily:"inherit",
                      background:`${P.red}14`,border:`1px solid ${P.red}55`,color:P.red,fontSize:15,fontWeight:600}}>
                    Cancella
                  </button>
                )}
                <button onClick={()=>{ if(!appenaAperto()) setMenuPresenza(null); }}
                  style={{flex:1,padding:12,borderRadius:10,cursor:"pointer",fontFamily:"inherit",
                    background:"transparent",border:`1px solid ${P.border}`,color:P.secondary,fontSize:15}}>
                  Chiudi{!isMobile && <span style={{fontSize:12,opacity:.7}}> (Esc)</span>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* NUOVA VERSIONE */}
      {nuovaVersione && (
        <div style={{position:"fixed",top:"calc(12px + env(safe-area-inset-top))",left:"50%",transform:"translateX(-50%)",zIndex:101,
          width:"max-content",maxWidth:"calc(100vw - 24px)",
          background:P.card3,border:`1px solid ${P.goldBorder}`,color:P.text,
          padding:"10px 12px 10px 16px",borderRadius:12,fontSize:15,
          display:"flex",alignItems:"center",gap:12,boxShadow:"0 8px 32px rgba(0,0,0,.45)"}}>
          <span>È disponibile una versione nuova dell'app</span>
          <button onClick={() => window.location.reload()}
            style={{padding:"7px 14px",background:P.gold,border:"none",color:"#fff",
              borderRadius:8,cursor:"pointer",fontSize:15,fontWeight:600,flexShrink:0}}>Aggiorna</button>
        </div>
      )}

      {/* NOTIFICA */}
      {notif && (
        <div style={{position:"fixed",top:"calc(18px + env(safe-area-inset-top))",right:18,zIndex:100,
          background:P.card3,
          border:`1px solid ${P.borderStrong}`,
          color:P.text,
          padding:"12px 18px",borderRadius:12,fontSize:15,fontWeight:500,
          animation:"ni .3s ease",display:"flex",alignItems:"center",gap:14,
          boxShadow:"0 8px 32px rgba(0,0,0,.45)"}}>
          <span style={{color:notif.t==="ok"?P.green:notif.t==="warn"?P.gold:P.red,fontSize:17}}>
            {notif.t==="ok"?"✓":notif.t==="warn"?"⚑":"✕"}
          </span>
          {notif.msg}
          {notif.t==="warn" && deletedAllievo && (
            <button onClick={undoElimina} style={{
              background:P.goldGlow,border:`1px solid ${P.goldBorder}`,
              color:P.gold,padding:"4px 12px",borderRadius:6,cursor:"pointer",
              fontFamily:"'IBM Plex Mono',monospace",fontSize:13,fontWeight:400,letterSpacing:0.5}}>
              Annulla
            </button>
          )}
        </div>
      )}

      {/* MODALE CONFERMA ARCHIVIAZIONE */}
      {confirmDelete && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.78)",zIndex:50,
          display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setConfirmDelete(null)}>
          <div style={{background:P.card2,border:`1px solid rgba(180,60,60,0.35)`,borderRadius:18,
            padding:32,width:400,maxWidth:"94vw",animation:"fi .3s ease",boxShadow:"0 24px 64px rgba(0,0,0,.55)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:20,fontWeight:700,letterSpacing:-0.3,marginBottom:8}}>
              Archivia allievo
            </div>
            <p style={{fontSize:15,color:P.muted,marginBottom:24,lineHeight:1.6}}>
              Vuoi archiviare <strong style={{color:P.text}}>{confirmDelete.nome}</strong>?<br/>
              Esce dalle liste da questo mese in poi, ma presenze e pagamenti restano
              conservati e i mesi passati non cambiano.<br/>
              Lo ritrovi in DB Allievi con “Mostra archiviati”.
            </p>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setConfirmDelete(null)}
                style={{flex:1,padding:"11px",background:"transparent",
                  border:`1px solid ${P.border}`,color:P.muted,
                  borderRadius:8,cursor:"pointer",fontSize:15,fontFamily:"'Syne',sans-serif"}}>
                Annulla
              </button>
              <button onClick={()=>archiviaAllievo(confirmDelete.id)}
                style={{flex:1,padding:"11px",background:`${P.red}20`,
                  border:`1px solid ${P.red}60`,color:P.red,
                  borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:15,fontFamily:"'Syne',sans-serif"}}>
                Archivia
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{display:"flex",minHeight:"100vh"}}>
        {/* SIDEBAR — solo desktop */}
        <aside style={{width:215,background:P.sidebar,borderRight:`1px solid ${P.borderSubtle}`,
          display: isMobile ? "none" : "flex",flexDirection:"column",padding:"28px 0",
          position:"fixed",height:"100vh",zIndex:10,overflowY:"auto"}}>
          <div style={{padding:"0 20px 22px",borderBottom:`1px solid ${P.borderSubtle}`}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.muted,marginBottom:6,textTransform:"uppercase"}}>Gestionale</div>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:19,fontWeight:800,lineHeight:1.15,letterSpacing:-0.5}}>
              Nuove<br/><span style={{color:P.gold}}>Armonie</span>
            </div>
          </div>
          <nav style={{flex:1,paddingTop:8}}>
            {NAV_ITEMS.map(item=>{
              if(item.children){
                const anyActive = item.children.some(c=>c.id===tab);
                return (
                  <div key={item.id}>
                    <button onClick={()=>setCorsiOpen(o=>!o)}
                      style={{width:"100%",padding:"10px 20px",display:"flex",alignItems:"center",gap:10,
                        background:anyActive?P.goldGlow:"transparent",
                        border:"none",borderLeft:`2px solid ${anyActive?P.gold:"transparent"}`,
                        color:anyActive?P.gold:"#C8CADB",cursor:"pointer",fontSize:15,
                        fontWeight:anyActive?600:400,transition:"all .15s",textAlign:"left",fontFamily:"'Figtree',sans-serif"}}>
                      <span style={{fontSize:16,opacity:.7}}>{item.icon}</span>
                      <span style={{flex:1}}>{item.label}</span>
                      <span style={{fontSize:11,transition:"transform .2s",
                        display:"inline-block",transform:corsiOpen?"rotate(90deg)":"rotate(0deg)",opacity:.5}}>▶</span>
                    </button>
                    {corsiOpen && item.children.map(child=>(
                      <button key={child.id} onClick={()=>goTab(child.id)}
                        style={{width:"100%",padding:"8px 20px 8px 34px",display:"flex",alignItems:"center",gap:10,
                          background:tab===child.id?P.goldGlow:"transparent",
                          border:"none",borderLeft:`2px solid ${tab===child.id?P.gold:"transparent"}`,
                          color:tab===child.id?P.gold:"#C8CADB",cursor:"pointer",fontSize:14,
                          fontWeight:tab===child.id?600:400,transition:"all .15s",textAlign:"left",fontFamily:"'Figtree',sans-serif"}}>
                        <span style={{fontSize:14,opacity:.6}}>{child.icon}</span>{child.label}
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <button key={item.id} onClick={()=>goTab(item.id)}
                  style={{width:"100%",padding:"10px 20px",display:"flex",alignItems:"center",gap:10,
                    background:tab===item.id?P.goldGlow:"transparent",
                    border:"none",borderLeft:`2px solid ${tab===item.id?P.gold:"transparent"}`,
                    color:tab===item.id?P.gold:"#C8CADB",cursor:"pointer",fontSize:15,
                    fontWeight:tab===item.id?600:400,transition:"all .15s",textAlign:"left",fontFamily:"'Figtree',sans-serif"}}>
                  <span style={{fontSize:16,opacity:tab===item.id?1:.6}}>{item.icon}</span>{item.label}
                </button>
              );
            })}
          </nav>
          <div style={{padding:"16px 20px",borderTop:`1px solid ${P.borderSubtle}`}}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:P.muted,marginBottom:2,letterSpacing:2,textTransform:"uppercase"}}>Anno scolastico</div>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:700,marginBottom:14,color:P.text}}>2025 / 2026</div>
            {authUser && (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:12,color:P.muted,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{authUser.email}</div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontSize:12,padding:"2px 8px",borderRadius:20,fontWeight:600,
                    background:userRole==="admin"?`${P.gold}22`:userRole==="segreteria"?`${P.blue}22`:`${P.purple}22`,
                    color:userRole==="admin"?P.gold:userRole==="segreteria"?P.blue:P.purple,
                    border:`1px solid ${userRole==="admin"?P.gold:userRole==="segreteria"?P.blue:P.purple}44`}}>
                    {userRole}
                  </span>
                  <button onClick={handleLogout} title="Esci"
                    style={{background:"transparent",border:`1px solid ${P.border}`,color:P.muted,
                      borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:13}}>
                    Esci
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* MAIN */}
        <main style={{marginLeft: isMobile ? 0 : 215, flex:1, minWidth:0,
          padding: isMobile
            ? (userRole==="maestro"
                ? `calc(${isStandalone?62:54}px + env(safe-area-inset-top)) 14px calc(20px + env(safe-area-inset-bottom))`
                : `14px 14px calc(64px + env(safe-area-inset-bottom))`)
            : "38px 42px",
          minHeight:"100vh"}}>
          {tab==="dashboard" && <Dashboard allievi={allievi}/>}
          {tab==="calendario"  && <Calendario
            calDate={calDate} setCalDate={setCalDate}
            calView={calView} setCalView={setCalView}
            calRecurring={calRecurring} setCalRecurring={setCalRecurring}
            calSingle={calSingle} setCalSingle={setCalSingle}
            calFiltroMaestro={calFiltroMaestro} setCalFiltroMaestro={setCalFiltroMaestro}
            calHidden={calHidden} setCalHidden={setCalHidden}
            maestriColors={maestriColors} maestriDB={maestriDB}
            saveEventoToDB={saveEventoToDB} notify={notify}
            readonlyMaestro={userRole==="maestro" ? userMaestro : null}
            isMaestroRole={userRole==="maestro"}
          />}
          {tab==="allievi"   && <Allievi/>}
          {tab==="presenze"  && <PresenzeHost render={Presenze}/>}
          {tab==="segreteria" && <Segreteria
            nicolPresenze={nicolPresenze} setNicolPresenze={setNicolPresenze}
            nicolMesi={nicolMesi} setNicolMesi={setNicolMesi}
            isMobile={isMobile}
            toggleStipendioConCassa={toggleStipendioConCassa}
            segreteriaNome={segreteriaNome}
            onSaveNome={async (nome)=>{ setSegreteriaNome(nome); await supabase.from('config').upsert({chiave:'segreteria_nome',valore:nome},{onConflict:'chiave'}); }}
            onSavePresenza={async (mese, giorno, presente)=>{
              if (presente) await supabase.from('segreteria_presenze').upsert({mese, giorno},{onConflict:'mese,giorno'});
              else await supabase.from('segreteria_presenze').delete().eq('mese',mese).eq('giorno',giorno);
            }}
            onSavePagato={async (mese, pagato)=>{
              await supabase.from('segreteria_pagamenti').upsert({mese, pagato},{onConflict:'mese'});
            }}
          />}
          {tab==="mdi"       && <MusicaInsieme/>}
          {tab==="maestri"   && <Maestri/>}
          {tab==="solfeggio" && <Solfeggio allievi={allievi} notify={notify}/>}
          {tab==="sala"      && <SalaProve sala={sala} isMobile={isMobile} addSala={addSala} deleteSala={deleteSala} updateSalaCell={updateSalaCell} notify={notify}/>}
          {tab==="db-allievi" && <DatabaseAllievi allievi={allieviAll} ripristinaAllievo={ripristinaAllievo} haStorico={haStorico} eliminaAllievoDefinitivo={eliminaAllievoDefinitivo} setAllievi={setAllievi} maestriColors={maestriColors} corsi={corsi} isMobile={isMobile} getRettaPagata={getRettaPagata} nextId={nextId} setNextId={setNextId} setConfirmDelete={setConfirmDelete} setPresAllievoFrom={setPresAllievoFrom} setPresAllievo={setPresAllievo} setTab={setTab} notify={notify} updateAllievoRettaBase={updateAllievoRettaBase}/>}
          {tab==="db-maestri" && <DatabaseMaestri/>}
          {tab==="db-corsi"   && <DatabaseCorsi corsi={corsi} onSaveCorso={onSaveCorso} onDeleteCorso={onDeleteCorso} maestriDB={maestriDB} isMobile={isMobile} notify={notify}/>}
          {tab==="pagamenti"  && <Pagamenti pagamenti={pagamenti} maestriColors={maestriColors} isMobile={isMobile} goToAllievo={goToAllievo} togglePagamento={togglePagamento}/>}
          {tab==="stipendi"   && <Stipendi allievi={allievi} pagamenti={pagamenti} mkeys={mkeysVisibili()}maestriColors={maestriColors} stipendiPagati={stipendiPagati} isMobile={isMobile} dbReady={dbReady} goToMaestro={goToMaestro} goToAllievo={goToAllievo} saveHistory={saveHistory} setStipendi={setStipendi} saveStipendioToDB={saveStipendioToDB} quotaMaestro={quotaMaestro} stipendioMaestro={stipendioMaestro} meseAllievi={meseAllievi} corsi={corsi} getAllievoCorsi={getAllievoCorsi} isTitolare={isTitolare} setTooltip={setTooltip} getRettaImportoMese={getRettaImportoMese} getRettaPagata={getRettaPagata}/>}
          {tab==="utenti"     && <GestioneUtenti/>}
        </main>
      </div>

      {/* Telefono, app dalla schermata Home: la barra di stato è trasparente e la pagina
          ci scorreva sotto, sovrapponendo il testo all'ora. Una fascia fissa la copre. */}
      {isMobile && (
        <div style={{position:"fixed",top:0,left:0,right:0,height:"env(safe-area-inset-top)",
          background:P.bg,zIndex:19}}/>
      )}

      {/* BOTTOM NAV — solo mobile, non per maestro (ha solo 1 tab) */}
      {isMobile && userRole !== "maestro" && (
        <nav style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:20,
          background:P.card, borderTop:`1px solid ${P.border}`,
          display:"flex", overflowX:"auto",
          WebkitOverflowScrolling:"touch",
          scrollbarWidth:"none",
          // Sopra la lineetta di iPhone basta parte del margine di sicurezza (34px): la barra resta più bassa
          paddingBottom:"max(4px, calc(env(safe-area-inset-bottom) - 12px))",
        }}>
          <style>{`::-webkit-scrollbar{display:none}`}</style>
          {NAV_ITEMS.map(item=>{
            if(item.children){
              const anyActive = item.children.some(c=>c.id===tab);
              return (
                <div key={item.id} style={{position:"relative",display:"flex"}}>
                  {corsiOpen && (
                    <div style={{position:"absolute",bottom:"100%",left:"50%",transform:"translateX(-50%)",
                      background:P.card,border:`1px solid ${P.border}`,borderRadius:10,padding:6,
                      display:"flex",flexDirection:"column",gap:2,zIndex:100,minWidth:120,
                      boxShadow:"0 -4px 16px rgba(0,0,0,.4)"}}>
                      {item.children.map(child=>(
                        <button key={child.id} onClick={()=>goTab(child.id)}
                          style={{padding:"10px 14px",border:"none",
                            background:tab===child.id?P.goldGlow:"transparent",
                            color:tab===child.id?P.gold:"#C8CADB",cursor:"pointer",fontSize:14,
                            fontWeight:tab===child.id?600:400,borderRadius:6,textAlign:"left",
                            whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:8}}>
                          <span>{child.icon}</span>{child.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <button onClick={()=>setCorsiOpen(o=>!o)}
                    style={{flex:"0 0 auto",display:"flex",flexDirection:"column",
                      alignItems:"center",justifyContent:"center",
                      padding:"7px 11px 4px",border:"none",background:"transparent",
                      color:anyActive?P.gold:"#C8CADB",cursor:"pointer",
                      borderTop:`2px solid ${anyActive?P.gold:"transparent"}`,
                      transition:"all .15s",minWidth:60}}>
                    <span style={{fontSize:18,lineHeight:1}}>{item.icon}</span>
                    <span style={{fontSize:11,marginTop:3,letterSpacing:.5,
                      fontWeight:anyActive?600:400,whiteSpace:"nowrap"}}>{item.label}</span>
                  </button>
                </div>
              );
            }
            const active = tab===item.id;
            return (
              <button key={item.id} onClick={()=>goTab(item.id)}
                style={{flex:"0 0 auto",display:"flex",flexDirection:"column",
                  alignItems:"center",justifyContent:"center",
                  padding:"7px 11px 4px",border:"none",background:"transparent",
                  color:active?P.gold:"#C8CADB",cursor:"pointer",
                  borderTop:`2px solid ${active?P.gold:"transparent"}`,
                  transition:"all .15s",minWidth:60}}>
                <span style={{fontSize:18,lineHeight:1}}>{item.icon}</span>
                <span style={{fontSize:11,marginTop:3,letterSpacing:.5,
                  fontWeight:active?600:400,whiteSpace:"nowrap"}}>{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* LOGOUT BTN — solo maestro in PWA standalone */}
      {isMobile && userRole === "maestro" && isStandalone && (
        <button onClick={handleLogout}
          style={{position:"fixed",bottom:"calc(18px + env(safe-area-inset-bottom))",right:14,zIndex:30,
            background:P.card,border:`1px solid ${P.border}`,color:P.muted,
            borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:14,
            boxShadow:"0 2px 8px rgba(0,0,0,.3)"}}>
          Esci
        </button>
      )}

      {/* MODALI */}
      <Modal id="allievo" title="Nuovo Allievo" sub="Inserisci i dati dell'allievo" onSave={addAllievo}>
        {inp("nome","Nome completo","text","Es. Mario Rossi")}
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
          {inp("retta","Retta €","number","Es. 120")}
          {inp("iscrizione","Iscrizione €","number","Es. 20")}
        </div>
        {inp("note","Note","text","Note aggiuntive")}
        <div style={{marginBottom:14}}>
          <label style={{fontSize:12,color:P.muted,letterSpacing:1.5,display:"block",marginBottom:5}}>MAESTRO</label>
          <select value={form.maestro||""} onChange={s("maestro")}
            style={{width:"100%",background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,
              padding:"10px 13px",color:form.maestro?P.text:P.muted,fontSize:15,cursor:"pointer"}}>
            <option value="" disabled>Seleziona maestro...</option>
            {mkeysAssegnabili.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </Modal>

      <Modal id="maestro" title="Nuovo Maestro" sub="Aggiungi un maestro all'organico" onSave={addMaestro}>
        {inp("nome","Nome","text","Es. MARCO")}
        <div style={{marginBottom:14}}>
          <label style={{fontSize:12,color:P.muted,letterSpacing:1.5,display:"block",marginBottom:5}}>COLORE</label>
          <input type="color" value={form.color||"#E9622A"} onChange={s("color")}
            style={{width:"100%",height:40,borderRadius:8,border:`1px solid ${P.border}`,
              background:P.bg,cursor:"pointer",padding:4}}/>
        </div>
      </Modal>

      {/* ── TOOLTIP GLOBALE — sempre nel DOM, opacity transition evita lampeggio ── */}
      <div
        data-info-tip="1"
        onMouseEnter={tooltip?.onMouseEnter}
        onMouseLeave={tooltip?.onMouseLeave}
        style={{
          position:"fixed", zIndex:9999,
          left: tooltip ? tooltip.x : -9999,
          top:  tooltip ? tooltip.y : -9999,
          opacity: tooltip ? 1 : 0,
          pointerEvents: tooltip ? "auto" : "none",
          transition:"opacity 0.12s ease",
          background:"#0D1020", border:"1px solid #1D2238",
          borderRadius:10, padding:"12px 16px",
          boxShadow:"0 8px 32px rgba(0,0,0,0.55)",
          minWidth:200, maxWidth:280,
        }}
      >
        {tooltip?.lines?.map((l, i) => (
          <div key={i} style={{
            display:"flex", justifyContent:"space-between", alignItems:"center",
            gap:12, padding:"2px 0",
            borderTop: l.sep ? "1px solid #1D2238" : "none",
            marginTop: l.sep ? 6 : 0, paddingTop: l.sep ? 8 : 2,
          }}>
            <span style={{
              fontSize: l.highlight ? 14 : 13,
              color: l.highlight ? "#EBE8F5" : "#B4B7D0",
              fontWeight: l.highlight ? 700 : 400,
            }}>{l.label}</span>
            {l.value !== undefined && (
              <span style={{
                fontFamily:"'IBM Plex Mono',monospace",
                fontSize: l.highlight ? 15 : 13,
                color: l.highlight ? "#E9622A" : "#B4B7D0",
                fontWeight: l.highlight ? 700 : 400,
                whiteSpace:"nowrap",
              }}>{l.value}</span>
            )}
          </div>
        ))}
      </div>

    </>
  );
}
